(function () {
  'use strict';

  var drawer = null;
  var panel = null;
  var lastFocus = null;
  var lastCart = { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0 };
  var walletProducts = [];
  var isOpen = false;
  var submitting = false;
  var checkoutPreparing = false;
  /** Body scroll-lock — capture once on open, restore once on close. */
  var scrollLocked = false;
  var lockedScrollY = 0;
  var eventsBound = false;
  var closeTimer = null;
  var openRaf = null;
  var drawerGeneration = 0;
  /** Idempotent UI layout pipeline — generation cancels stale rAF / image waits */
  var uiInitGeneration = 0;
  var uiInitRaf1 = null;
  var uiInitRaf2 = null;
  var uiInitViewportBound = false;

  /**
   * Shared selection state for all modes.
   * walletSelections[1] = pending PDP / first wallet
   * walletSelections[2] / [3] = extras (null until chosen)
   */
  var mode = 'individual'; // individual | gift | executive | complete | confirm
  var confirmContext = null;
  var walletSelections = { 1: null, 2: null, 3: null };
  var activeSlot = 2;
  /**
   * Active Gift/Executive set identity — stamped on cart line properties so
   * bundle lines can be replaced without touching standalone same-variant lines.
   * Underscore keys are hidden by the cart UI renderer.
   */
  var PROP_BUNDLE_ID = '_ev_bundle_id';
  var PROP_BUNDLE_OFFER = '_ev_bundle_offer';
  var PROP_BUNDLE_SLOT = '_ev_bundle_slot';
  var activeBundleId = null;
  var builderPersistQueue = Promise.resolve();
  /** Deduped in-flight /cart.js — all callers share one promise. */
  var cartFetchInflight = null;
  /** Page-lifetime Liquid compare_at map cache (DOM JSON). */
  var liquidCompareAtMapCache = null;
  var liquidCompareAtMapReady = false;
  /**
   * Per-card Select/CHANGE pending — visual only until Shopify confirms.
   * Do not fake discounts/totals while this is set.
   */
  var selectPending = null; /* { variantId, slot, previousEntry } */
  /**
   * Individual pre-cart configurator.
   * status: idle | selecting | submitting_single | submitting_pair | completed
   */
  var individualUpsell = {
    status: 'idle',
    wallet1: null,
    wallet2: null,
    cartMutationCommitted: false
  };

  /* Wallet image gallery — overlays drawer without resetting selection state */
  var galleryOpen = false;
  var galleryIndex = 0;
  var galleryImages = [];
  var galleryTrigger = null;
  var galleryProduct = null;
  var galleryScrollLeft = 0;

  function isIndividualMode() {
    return mode === 'individual';
  }

  function isIndividualCompleted() {
    return individualUpsell.status === 'completed' &&
      (isIndividualMode() || mode === 'complete');
  }

  function isIndividualSelecting() {
    return isIndividualMode() && individualUpsell.status === 'selecting';
  }

  function isIndividualSubmitting() {
    return isIndividualMode() && (
      individualUpsell.status === 'submitting_single' ||
      individualUpsell.status === 'submitting_pair'
    );
  }

  function isIndividualActiveFlow() {
    return isIndividualMode() && (
      individualUpsell.status === 'selecting' ||
      individualUpsell.status === 'submitting_single' ||
      individualUpsell.status === 'submitting_pair'
    );
  }

  function resetIndividualUpsell() {
    individualUpsell = {
      status: 'idle',
      wallet1: null,
      wallet2: null,
      cartMutationCommitted: false
    };
  }

  /* ---------- Individual social FOLLOW10 reward (local only) ---------- */
  var SOCIAL_LS = {
    unlocked: 'ev_social_reward_unlocked',
    source: 'ev_social_reward_source',
    pending: 'ev_social_reward_pending',
    applied: 'ev_social_reward_applied'
  };
  var SOCIAL_CODE_DEFAULT = 'FOLLOW10';
  var SOCIAL_URLS = {
    instagram: 'https://instagram.com/get_elitevault',
    facebook: 'https://facebook.com/getelitevault'
  };
  var socialRewardFocusBound = false;
  var socialCopyTimer = null;

  function getSocialCode() {
    var code = drawer && drawer.getAttribute('data-ev-social-code');
    return (code && String(code).trim()) || SOCIAL_CODE_DEFAULT;
  }

  function readSocialFlag(key) {
    try {
      return window.localStorage.getItem(key) || '';
    } catch (err) {
      return '';
    }
  }

  function writeSocialFlag(key, value) {
    try {
      if (value == null || value === '') window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, String(value));
    } catch (err) { /* private mode */ }
  }

  /**
   * Individual / 1 wallet only. Hidden for Gift, Executive, empty cart.
   * Incomplete intentional builders are not Individual.
   */
  function isSocialRewardEligibleUi() {
    if (!drawer || drawer.getAttribute('data-ev-social-reward-ready') !== 'true') return false;
    if (mode === 'gift' || mode === 'executive' || isConfirmMode()) return false;
    if (!(isIndividualMode() || mode === 'complete')) return false;
    if (typeof hasIndividualSecondWallet === 'function' && hasIndividualSecondWallet()) return false;
    var cart = lastCart || { items: [], item_count: 0 };
    var cartCount = parseInt(cart.item_count, 10) || 0;
    var units = countEligibleWalletUnits(cart);
    var hasSeed = !!variantIdOf(walletSelections && walletSelections[1]);
    /* Empty cart / no wallet seed — never advertise FOLLOW10 */
    if (cartCount <= 0 && units <= 0 && !hasSeed) return false;
    if (units <= 0 && !hasSeed) return false;
    return true;
  }

  function setSocialSectionHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.hidden = true;
      el.setAttribute('hidden', '');
    } else {
      el.hidden = false;
      el.removeAttribute('hidden');
    }
  }

  function syncSocialRewardUI() {
    if (!drawer) return;
    var root = drawer.querySelector('[data-ev-social-reward]');
    if (!root) return;

    if (!isSocialRewardEligibleUi()) {
      setSocialSectionHidden(root, true);
      root.classList.remove('is-expanded');
      return;
    }

    setSocialSectionHidden(root, false);
    var locked = root.querySelector('[data-ev-social-reward-locked]');
    var unlocked = root.querySelector('[data-ev-social-reward-unlocked]');
    var applied = root.querySelector('[data-ev-social-reward-applied]');
    var toggle = root.querySelector('[data-ev-social-reward-toggle]');
    var isUnlocked = readSocialFlag(SOCIAL_LS.unlocked) === '1';
    var isApplied = readSocialFlag(SOCIAL_LS.applied) === '1';

    setSocialSectionHidden(locked, isUnlocked);
    setSocialSectionHidden(unlocked, !isUnlocked || isApplied);
    setSocialSectionHidden(applied, !isApplied);

    var narrow = window.matchMedia && window.matchMedia('(max-width: 480px)').matches;
    if (toggle) {
      if (narrow && !root.classList.contains('is-expanded')) {
        setSocialSectionHidden(toggle, false);
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        setSocialSectionHidden(toggle, true);
        toggle.setAttribute('aria-expanded', root.classList.contains('is-expanded') ? 'true' : 'false');
      }
    }
  }

  function unlockSocialRewardFromReturn() {
    if (readSocialFlag(SOCIAL_LS.pending) !== '1') return;
    writeSocialFlag(SOCIAL_LS.pending, '');
    writeSocialFlag(SOCIAL_LS.unlocked, '1');
    syncSocialRewardUI();
  }

  function bindSocialRewardFocusUnlock() {
    if (socialRewardFocusBound) return;
    socialRewardFocusBound = true;
    window.addEventListener('focus', unlockSocialRewardFromReturn);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') unlockSocialRewardFromReturn();
    });
  }

  function openSocialProfile(source, url) {
    var key = String(source || '').toLowerCase();
    var target = SOCIAL_URLS[key] || url || '';
    if (!target) return;
    /* Persist interaction BEFORE opening — unlock only after return/focus. */
    writeSocialFlag(SOCIAL_LS.pending, '1');
    writeSocialFlag(SOCIAL_LS.source, key || '');
    window.open(target, '_blank', 'noopener,noreferrer');
  }

  function socialDiscountConfirmedInCart(cart, code) {
    var upper = String(code || '').toUpperCase();
    var codes = cart && cart.discount_codes;
    if (Array.isArray(codes)) {
      for (var i = 0; i < codes.length; i++) {
        var entry = codes[i];
        var c = String((entry && (entry.code || entry)) || '').toUpperCase();
        if (c === upper && (!entry || entry.applicable !== false)) return true;
      }
    }
    var apps = (cart && cart.cart_level_discount_applications) || [];
    for (var j = 0; j < apps.length; j++) {
      var title = String((apps[j] && (apps[j].title || apps[j].code)) || '').toUpperCase();
      if (title.indexOf(upper) >= 0) return true;
    }
    return false;
  }

  function applySocialDiscountCode() {
    var code = getSocialCode();
    var applyBtn = drawer && drawer.querySelector('[data-ev-social-apply]');
    if (applyBtn) {
      applyBtn.disabled = true;
      applyBtn.textContent = 'Applying…';
    }
    var redirectPath = window.location.pathname + (window.location.search || '');
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirectPath || '/'), {
      credentials: 'same-origin',
      redirect: 'follow'
    })
      .then(function () {
        writeSocialFlag(SOCIAL_LS.applied, '1');
        writeSocialFlag(SOCIAL_LS.unlocked, '1');
        return fetchCart();
      })
      .then(function (cart) {
        syncThemeCartState(cart);
        var confirmed = socialDiscountConfirmedInCart(cart, code);
        var title = drawer && drawer.querySelector('[data-ev-social-reward-applied-title]');
        if (title) {
          title.textContent = confirmed ? '10% Applied ✓' : (code + ' ready for checkout ✓');
        }
        syncSocialRewardUI();
        refreshUI(cart, { cards: 'states', quiet: true, preserveScroll: true });
        return cart;
      })
      .catch(function (error) {
        console.error('[EV Cart Drawer]', error);
        showError(error.message || 'Could not apply reward code.');
        if (applyBtn) {
          applyBtn.disabled = false;
          applyBtn.textContent = 'Apply 10% Off';
        }
      })
      .finally(function () {
        if (applyBtn && readSocialFlag(SOCIAL_LS.applied) !== '1') {
          applyBtn.disabled = false;
          applyBtn.textContent = 'Apply 10% Off';
        }
      });
  }

  function copySocialDiscountCode() {
    var code = getSocialCode();
    var btn = drawer && drawer.querySelector('[data-ev-social-copy]');
    var done = function () {
      if (!btn) return;
      btn.textContent = 'Copied ✓';
      if (socialCopyTimer) window.clearTimeout(socialCopyTimer);
      socialCopyTimer = window.setTimeout(function () {
        btn.textContent = 'Copy';
      }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(code).then(done).catch(function () {
        done();
      });
    }
    done();
    return Promise.resolve();
  }

  function cartLineToSummary(item) {
    if (!item) return null;
    var unitPrice = parseInt(item.original_price, 10) || parseInt(item.price, 10) || 0;
    var compareAt = parseInt(item.compare_at_price, 10) || 0;
    var summary = {
      variantId: item.variant_id,
      productId: item.product_id,
      productTitle: item.product_title || item.title || '',
      variantTitle: item.variant_title || '',
      price: unitPrice,
      compareAt: compareAt > unitPrice ? compareAt : null,
      image: item.image || '',
      quantity: 1,
      originalLinePrice: parseInt(item.original_line_price, 10) || unitPrice
    };
    return enrichSelectionCompareAt(summary);
  }

  /** Prefer Liquid PDP/catalog compare-at when cart JSON omits it. */
  function enrichSelectionCompareAt(entry) {
    if (!entry || !entry.variantId) return entry;
    var price = parseInt(entry.price, 10) || 0;
    var compareAt = parseInt(entry.compareAt, 10) || 0;
    if (compareAt > price) {
      entry.compareAt = compareAt;
      return entry;
    }
    var resolved = resolveVariantCompareAtCents(entry.variantId, price);
    if (resolved > price) {
      entry.compareAt = resolved;
      return entry;
    }
    var meta = findVariantMeta(entry.variantId);
    if (meta && meta.variant) {
      var catalogPrice = parseInt(meta.variant.price, 10) || price;
      if (!entry.price && catalogPrice > 0) entry.price = catalogPrice;
    }
    entry.compareAt = null;
    return entry;
  }

  /** Expand eligible cart lines into ordered wallet positions (qty counted). */
  function getEligibleWalletSummaries(cart, limit) {
    var discountApi = getDiscountApi();
    var max = limit || 2;
    var list = [];
    (cart.items || []).forEach(function (item) {
      if (discountApi && !discountApi.isEligibleWalletItem(item)) return;
      if (!discountApi && !/wallet/i.test(item.product_title || item.title || '')) return;
      var qty = parseInt(item.quantity, 10) || 1;
      for (var i = 0; i < qty; i++) {
        list.push(cartLineToSummary(item));
        if (list.length >= max) return;
      }
    });
    return list;
  }

  function syncIndividualUpsellFromCart(cart) {
    if (!isIndividualMode() && mode !== 'complete') return;
    if (isIndividualMode() && !individualUpsell.cartMutationCommitted && individualUpsell.status !== 'completed') {
      return;
    }
    var priorW1 = individualUpsell.wallet1;
    var priorW2 = individualUpsell.wallet2;
    var wallets = getEligibleWalletSummaries(cart, 2);
    if (wallets[0]) {
      individualUpsell.wallet1 = enrichSelectionCompareAt(wallets[0]);
      if (priorW1 && variantIdOf(priorW1) === variantIdOf(individualUpsell.wallet1) && priorW1.compareAt && !individualUpsell.wallet1.compareAt) {
        individualUpsell.wallet1.compareAt = priorW1.compareAt;
      }
    }
    if (wallets.length >= 2) {
      individualUpsell.wallet2 = enrichSelectionCompareAt(wallets[1]);
      if (priorW2 && variantIdOf(priorW2) === variantIdOf(individualUpsell.wallet2) && priorW2.compareAt && !individualUpsell.wallet2.compareAt) {
        individualUpsell.wallet2.compareAt = priorW2.compareAt;
      }
      if (!isIndividualSubmitting()) {
        individualUpsell.status = 'completed';
        individualUpsell.cartMutationCommitted = true;
      }
    } else if (!isIndividualSubmitting() && individualUpsell.cartMutationCommitted) {
      /* Keep upgrade/selecting UI after immediate standalone add — only mark completed once committed via footer or pair. */
      if (individualUpsell.status !== 'selecting') {
        individualUpsell.status = 'completed';
        individualUpsell.wallet2 = null;
      }
    }
  }

  function isStandaloneCartLine(item) {
    var props = lineBundleProps(item);
    return !props[PROP_BUNDLE_ID];
  }

  function findStandaloneVariantLine(cart, variantId) {
    var id = parseInt(variantId, 10);
    if (!id) return null;
    var match = null;
    (cart.items || []).forEach(function (item) {
      if (parseInt(item.variant_id, 10) === id && isStandaloneCartLine(item)) {
        match = item;
      }
    });
    return match;
  }

  /**
   * Individual: stamp current PDP wallet as a NORMAL standalone cart line immediately.
   * No _ev_bundle_* properties. Idempotent — will not add a second unit on reopen.
   */
  function bootstrapIndividualCart(cart, buildContext) {
    var seed = individualUpsell.wallet1 || walletSelections[1] || null;
    if ((!seed || !variantIdOf(seed)) && buildContext) {
      seed = {
        variantId: buildContext.variantId,
        productId: buildContext.productId,
        productTitle: buildContext.productTitle,
        variantTitle: buildContext.variantTitle,
        price: buildContext.price,
        compareAt: buildContext.compareAt,
        image: buildContext.image,
        available: buildContext.available !== false
      };
    }
    seed = enrichSelectionCompareAt(seed);
    var vid = variantIdOf(seed);
    if (!vid) return Promise.resolve(cart || lastCart);

    individualUpsell.wallet1 = seed;
    walletSelections[1] = seed;

    var existing = findStandaloneVariantLine(cart, vid);
    if (existing) {
      individualUpsell.cartMutationCommitted = true;
      return Promise.resolve(syncThemeCartState(cart));
    }

    return addItems([{ id: vid, quantity: 1 }]).then(function () {
      return fetchCart();
    }).then(function (nextCart) {
      individualUpsell.cartMutationCommitted = true;
      return syncThemeCartState(nextCart);
    });
  }

  function removeVariantLines(variantId, cart) {
    var id = parseInt(variantId, 10);
    var updates = {};
    (cart.items || []).forEach(function (item) {
      if (parseInt(item.variant_id, 10) === id) {
        updates[item.key] = 0;
      }
    });
    return applyCartQuantityUpdates(updates, cart);
  }

  function createBuilderBundleId() {
    return 'evb_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  function builderOfferKey() {
    return mode === 'executive' ? 'executive' : 'gift';
  }

  function lineBundleProps(item) {
    return (item && item.properties && typeof item.properties === 'object') ? item.properties : {};
  }

  function builderLineProperties(slot) {
    return {
      _ev_bundle_id: activeBundleId,
      _ev_bundle_offer: builderOfferKey(),
      _ev_bundle_slot: String(slot)
    };
  }

  function applyCartQuantityUpdates(updates, cart) {
    var keys = Object.keys(updates || {});
    if (!keys.length) return Promise.resolve(cart || lastCart);
    return fetch(getConfig().cartUpdateUrl || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        if (!response.ok) {
          throw new Error((data && (data.description || data.message)) || 'Could not update cart');
        }
        /* /cart/update.js returns the full cart — skip redundant /cart.js */
        if (data && Array.isArray(data.items)) return data;
        return fetchCart();
      });
    });
  }

  /** /cart/change.js — update line qty/properties; response is full cart. */
  function changeCartLine(lineKey, payload) {
    if (!lineKey) return Promise.resolve(lastCart);
    var body = Object.assign({ id: lineKey }, payload || {});
    return fetch(getConfig().cartChangeUrl || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        if (!response.ok) {
          throw new Error((data && (data.description || data.message)) || 'Could not update cart line');
        }
        if (data && Array.isArray(data.items)) return data;
        return fetchCart();
      });
    });
  }

  function syncThemeCartState(cart) {
    lastCart = cart || lastCart;
    if (window.EV && typeof window.EV.syncCartState === 'function') {
      window.EV.syncCartState(lastCart);
    }
    return lastCart;
  }

  function findBundleSlotLine(cart, setId, slot) {
    var match = null;
    (cart.items || []).forEach(function (item) {
      var props = lineBundleProps(item);
      if (props[PROP_BUNDLE_ID] === setId && String(props[PROP_BUNDLE_SLOT]) === String(slot)) {
        match = item;
      }
    });
    return match;
  }

  function findResumeBundleId(cart, offer, preferredVariantId) {
    var preferredId = parseInt(preferredVariantId, 10) || 0;
    var preferredSet = null;
    var fallbackSet = null;
    (cart.items || []).forEach(function (item) {
      var props = lineBundleProps(item);
      if (props[PROP_BUNDLE_OFFER] !== offer || !props[PROP_BUNDLE_ID]) return;
      fallbackSet = props[PROP_BUNDLE_ID];
      if (
        preferredId &&
        String(props[PROP_BUNDLE_SLOT]) === '1' &&
        parseInt(item.variant_id, 10) === preferredId
      ) {
        preferredSet = props[PROP_BUNDLE_ID];
      }
    });
    return preferredSet || fallbackSet || null;
  }

  function selectionFromCartItem(item) {
    return enrichSelectionCompareAt({
      variantId: item.variant_id,
      productId: item.product_id || 0,
      productTitle: item.product_title || item.title || '',
      variantTitle: item.variant_title || '',
      price: item.price,
      compareAt: item.compare_at_price || null,
      image: item.image || ''
    });
  }

  function hydrateSelectionsFromBundle(cart, setId) {
    walletSelections = { 1: null, 2: null, 3: null };
    (cart.items || []).forEach(function (item) {
      var props = lineBundleProps(item);
      if (props[PROP_BUNDLE_ID] !== setId) return;
      var slot = parseInt(props[PROP_BUNDLE_SLOT], 10);
      if (slot !== 1 && slot !== 2 && slot !== 3) return;
      walletSelections[slot] = selectionFromCartItem(item);
    });
    if (!walletSelections[2]) activeSlot = 2;
    else if (mode === 'executive' && !walletSelections[3]) activeSlot = 3;
    else activeSlot = mode === 'executive' ? 3 : 2;
  }

  function removeBuilderSlotLines(cart, setId, slot) {
    var updates = {};
    (cart.items || []).forEach(function (item) {
      var props = lineBundleProps(item);
      if (props[PROP_BUNDLE_ID] === setId && String(props[PROP_BUNDLE_SLOT]) === String(slot)) {
        updates[item.key] = 0;
      }
    });
    return applyCartQuantityUpdates(updates, cart);
  }

  function removeBundleById(cart, setId) {
    if (!setId) return Promise.resolve(cart || lastCart);
    var updates = {};
    (cart.items || []).forEach(function (item) {
      var props = lineBundleProps(item);
      if (props[PROP_BUNDLE_ID] === setId) {
        updates[item.key] = 0;
      }
    });
    return applyCartQuantityUpdates(updates, cart);
  }

  function removeStandaloneVariantLines(variantIds, cart) {
    var wanted = {};
    (variantIds || []).forEach(function (id) {
      var n = parseInt(id, 10);
      if (n) wanted[n] = true;
    });
    var updates = {};
    (cart.items || []).forEach(function (item) {
      var vid = parseInt(item.variant_id, 10);
      if (wanted[vid] && isStandaloneCartLine(item)) {
        updates[item.key] = 0;
      }
    });
    return applyCartQuantityUpdates(updates, cart);
  }

  function countBundleSlots(cart, setId) {
    var count = 0;
    (cart.items || []).forEach(function (item) {
      var props = lineBundleProps(item);
      if (props[PROP_BUNDLE_ID] === setId && props[PROP_BUNDLE_SLOT]) count += 1;
    });
    return count;
  }

  function findLargestBundleIdForOffer(cart, offer) {
    var bestId = null;
    var bestCount = 0;
    var seen = {};
    (cart.items || []).forEach(function (item) {
      var props = lineBundleProps(item);
      if (props[PROP_BUNDLE_OFFER] !== offer || !props[PROP_BUNDLE_ID]) return;
      var id = props[PROP_BUNDLE_ID];
      if (seen[id]) return;
      seen[id] = true;
      var n = countBundleSlots(cart, id);
      if (n > bestCount) {
        bestCount = n;
        bestId = id;
      }
    });
    return bestId ? { id: bestId, count: bestCount } : null;
  }

  /**
   * Cart-driven drawer mode (authoritative for mutations / reopen).
   *
   * Completed / persisted cart state:
   *   1 eligible wallet → individual
   *   2 → gift
   *   3+ → executive
   *
   * Incomplete gift/exec (1 gift slot, or 1–2 executive slots) is returned ONLY when
   * allowIncompleteBuilder=true — i.e. customer explicitly opened Gift/Executive and is
   * still choosing wallets. Never keep stale Gift/Exec after a wallet is removed from a
   * completed set (see syncDrawerModeAfterCartMutation).
   */
  function resolveDrawerModeFromCart(cart, options) {
    options = options || {};
    var allowIncomplete = !!options.allowIncompleteBuilder;

    var exec = findLargestBundleIdForOffer(cart, 'executive');
    if (exec && exec.count >= 3) return { mode: 'executive', bundleId: exec.id, slotCount: exec.count };

    var gift = findLargestBundleIdForOffer(cart, 'gift');
    if (gift && gift.count >= 2) return { mode: 'gift', bundleId: gift.id, slotCount: gift.count };

    /* Executive with exactly 2 remaining wallets → Gift (downgrade path). */
    if (exec && exec.count === 2) {
      return { mode: 'gift', bundleId: exec.id, slotCount: exec.count, normalizeOffer: 'gift' };
    }

    if (allowIncomplete) {
      if (exec && exec.count >= 1) return { mode: 'executive', bundleId: exec.id, slotCount: exec.count };
      if (gift && gift.count >= 1) return { mode: 'gift', bundleId: gift.id, slotCount: gift.count };
    }

    var wallets = getEligibleWalletSummaries(cart, 3);
    if (wallets.length >= 3) return { mode: 'executive', bundleId: null, wallets: wallets, slotCount: wallets.length };
    if (wallets.length >= 2) return { mode: 'gift', bundleId: null, wallets: wallets, slotCount: wallets.length };
    return { mode: 'individual', bundleId: null, wallets: wallets, slotCount: wallets.length };
  }

  function countEligibleWalletUnits(cart) {
    return getEligibleWalletSummaries(cart, 99).length;
  }

  /** Clear _ev_bundle_* on a line so it becomes a normal Individual standalone. */
  function promoteBundleLineToStandalone(cart, line) {
    if (!line || !line.key) return Promise.resolve(cart || lastCart);
    return changeCartLine(line.key, {
      quantity: parseInt(line.quantity, 10) || 1,
      properties: {
        _ev_bundle_id: '',
        _ev_bundle_offer: '',
        _ev_bundle_slot: ''
      }
    }).then(function (next) {
      return syncThemeCartState(next);
    });
  }

  /** Retag remaining executive lines as gift after 3→2 downgrade. */
  function normalizeBundleOfferOnLines(cart, setId, offer) {
    if (!setId || !offer) return Promise.resolve(cart || lastCart);
    var chain = Promise.resolve(cart || lastCart);
    (cart.items || []).forEach(function (item) {
      var props = lineBundleProps(item);
      if (props[PROP_BUNDLE_ID] !== setId) return;
      if (props[PROP_BUNDLE_OFFER] === offer) return;
      var slot = props[PROP_BUNDLE_SLOT] || '';
      chain = chain.then(function (prev) {
        return changeCartLine(item.key, {
          quantity: parseInt(item.quantity, 10) || 1,
          properties: {
            _ev_bundle_id: setId,
            _ev_bundle_offer: offer,
            _ev_bundle_slot: String(slot)
          }
        });
      });
    });
    return chain.then(function (next) {
      return syncThemeCartState(next);
    });
  }

  /**
   * After add/remove/change of builder slots: derive mode from cart, not stale UI mode.
   * Gift complete → remove W2 → Individual.
   * Executive complete → remove W3 → Gift.
   * Executive → remove to 1 → Individual.
   */
  function syncDrawerModeAfterCartMutation(cart) {
    var nextCart = cart || lastCart || { items: [] };
    var resolved = resolveDrawerModeFromCart(nextCart, { allowIncompleteBuilder: false });
    var units = countEligibleWalletUnits(nextCart);

    if (units <= 0) {
      mode = 'individual';
      activeBundleId = null;
      walletSelections = { 1: null, 2: null, 3: null };
      resetIndividualUpsell();
      activeSlot = 2;
      return Promise.resolve(syncThemeCartState(nextCart));
    }

    if (resolved.mode === 'individual') {
      var remaining =
        (resolved.wallets && resolved.wallets[0]) ||
        walletSelections[1] ||
        walletSelections[2] ||
        walletSelections[3] ||
        null;

      /* Prefer the remaining active-bundle line; promote to standalone. */
      var setId = activeBundleId;
      var bundleLine = null;
      if (setId) {
        (nextCart.items || []).forEach(function (item) {
          var props = lineBundleProps(item);
          if (props[PROP_BUNDLE_ID] === setId) bundleLine = item;
        });
      }
      if (!bundleLine) {
        (nextCart.items || []).forEach(function (item) {
          var discountApi = getDiscountApi();
          var eligible = discountApi
            ? discountApi.isEligibleWalletItem(item)
            : /wallet/i.test(item.product_title || item.title || '');
          if (eligible && !bundleLine) bundleLine = item;
        });
      }

      var promote =
        bundleLine && lineBundleProps(bundleLine)[PROP_BUNDLE_ID]
          ? promoteBundleLineToStandalone(nextCart, bundleLine)
          : Promise.resolve(nextCart);

      return promote.then(function (finalCart) {
        var w1 = bundleLine
          ? selectionFromCartItem(bundleLine)
          : enrichSelectionCompareAt(remaining);
        mode = 'individual';
        activeBundleId = null;
        walletSelections = { 1: w1, 2: null, 3: null };
        individualUpsell = {
          status: 'selecting',
          wallet1: w1,
          wallet2: null,
          cartMutationCommitted: true
        };
        activeSlot = 2;
        return syncThemeCartState(finalCart);
      });
    }

    if (resolved.mode === 'gift') {
      var giftId = resolved.bundleId || activeBundleId;
      var normalize =
        resolved.normalizeOffer === 'gift' && giftId
          ? normalizeBundleOfferOnLines(nextCart, giftId, 'gift')
          : Promise.resolve(nextCart);

      return normalize.then(function (finalCart) {
        mode = 'gift';
        resetIndividualUpsell();
        if (giftId) {
          activeBundleId = giftId;
          hydrateSelectionsFromBundle(finalCart, giftId);
        } else if (resolved.wallets) {
          walletSelections = {
            1: resolved.wallets[0] || null,
            2: resolved.wallets[1] || null,
            3: null
          };
        }
        if (!walletSelections[2]) activeSlot = 2;
        else activeSlot = 2;
        return syncThemeCartState(finalCart);
      });
    }

    /* executive */
    mode = 'executive';
    resetIndividualUpsell();
    if (resolved.bundleId) {
      activeBundleId = resolved.bundleId;
      hydrateSelectionsFromBundle(nextCart, resolved.bundleId);
    } else if (resolved.wallets) {
      walletSelections = {
        1: resolved.wallets[0] || null,
        2: resolved.wallets[1] || null,
        3: resolved.wallets[2] || null
      };
    }
    if (!walletSelections[2]) activeSlot = 2;
    else if (!walletSelections[3]) activeSlot = 3;
    else activeSlot = 3;
    return Promise.resolve(syncThemeCartState(nextCart));
  }

  function mergeAddedItemsIntoCart(cart, addData) {
    var base = cart && typeof cart === 'object' ? cart : { items: [], item_count: 0, total_price: 0 };
    var added = [];
    if (addData && Array.isArray(addData.items)) {
      added = addData.items;
    } else if (addData && addData.id) {
      added = [addData];
    }
    if (!added.length) return base;
    var next = Object.assign({}, base);
    next.items = (base.items || []).concat(added);
    var addQty = 0;
    var addPrice = 0;
    added.forEach(function (item) {
      addQty += parseInt(item.quantity, 10) || 1;
      addPrice += parseInt(item.final_line_price != null ? item.final_line_price : item.line_price, 10) || 0;
    });
    next.item_count = (parseInt(base.item_count, 10) || 0) + addQty;
    if (typeof base.total_price === 'number') {
      next.total_price = (parseInt(base.total_price, 10) || 0) + addPrice;
    }
    return next;
  }

  function cartHasVariant(cart, variantId) {
    var id = parseInt(variantId, 10);
    if (!id) return false;
    return (cart.items || []).some(function (item) {
      return parseInt(item.variant_id, 10) === id;
    });
  }

  /** After Select paints Gift UI, pull authoritative discount/totals without blocking. */
  function reconcileBuilderCartInBackground(expectedBundleId) {
    var openGen = drawerGeneration;
    var bundleId = expectedBundleId || activeBundleId;
    fetchCart()
      .then(function (cart) {
        if (openGen !== drawerGeneration || !isOpen) return;
        if (!(mode === 'gift' || mode === 'executive')) return;
        var units = countEligibleWalletUnits(cart);
        var mustDowngrade =
          (mode === 'gift' && units < 2) ||
          (mode === 'executive' && units < 3);
        if (mustDowngrade) {
          return syncDrawerModeAfterCartMutation(cart).then(function (nextCart) {
            if (openGen !== drawerGeneration || !isOpen) return;
            refreshUI(nextCart, { cards: 'states', quiet: true, preserveScroll: true });
          });
        }
        if (bundleId) {
          hydrateSelectionsFromBundle(cart, bundleId);
        }
        syncThemeCartState(cart);
        refreshUI(cart, { cards: 'states', quiet: true, preserveScroll: true });
      })
      .catch(function (error) {
        console.error('[EV Cart Drawer]', error);
      });
  }

  /**
   * Individual (1 standalone) + selected Wallet 2 → real Gift Set cart + UI.
   * Fast path: promote W1 via /cart/change.js, add W2 via /cart/add.js,
   * paint as soon as add succeeds (no remove+re-add of both lines).
   */
  function transitionIndividualToGiftSet(w2Entry) {
    var w1 = enrichSelectionCompareAt(individualUpsell.wallet1 || walletSelections[1]);
    var w2 = enrichSelectionCompareAt(w2Entry);
    var id1 = variantIdOf(w1);
    var id2 = variantIdOf(w2);
    if (!id1 || !id2) {
      return Promise.reject(new Error('Could not upgrade to Gift Set.'));
    }

    var previousMode = mode;
    var previousBundleId = activeBundleId;
    var previousSelections = {
      1: walletSelections[1] || null,
      2: walletSelections[2] || null,
      3: walletSelections[3] || null
    };
    var previousSlot = activeSlot;

    return enqueueBuilderPersist(function () {
      var newBundleId = createBuilderBundleId();
      var giftProps = function (slot) {
        return {
          _ev_bundle_id: newBundleId,
          _ev_bundle_offer: 'gift',
          _ev_bundle_slot: String(slot)
        };
      };

      var start =
        lastCart && Array.isArray(lastCart.items)
          ? Promise.resolve(lastCart)
          : fetchCart();

      return start
        .then(function (cart) {
          var line1 = findStandaloneVariantLine(cart, id1);
          if (!line1) {
            /* Fallback: stamp both gift lines in one add (no sequential persist+fetch). */
            return removeStandaloneVariantLines([id1, id2], cart).then(function (nextCart) {
              return addItems([
                { id: id1, quantity: 1, properties: giftProps(1) },
                { id: id2, quantity: 1, properties: giftProps(2) }
              ]).then(function (addData) {
                var merged = mergeAddedItemsIntoCart(nextCart, addData);
                if (cartHasVariant(merged, id1) && cartHasVariant(merged, id2)) {
                  return { cart: merged, needsReconcile: true };
                }
                return fetchCart().then(function (full) {
                  return { cart: full, needsReconcile: false };
                });
              });
            });
          }

          return changeCartLine(line1.key, {
            quantity: parseInt(line1.quantity, 10) || 1,
            properties: giftProps(1)
          }).then(function (nextCart) {
            var existing2 = findBundleSlotLine(nextCart, newBundleId, 2);
            if (existing2 && parseInt(existing2.variant_id, 10) === id2) {
              return { cart: nextCart, needsReconcile: false };
            }

            var stand2 = findStandaloneVariantLine(nextCart, id2);
            if (stand2) {
              return changeCartLine(stand2.key, {
                quantity: parseInt(stand2.quantity, 10) || 1,
                properties: giftProps(2)
              }).then(function (promoted) {
                return { cart: promoted, needsReconcile: false };
              });
            }

            return addItems([{
              id: id2,
              quantity: 1,
              properties: giftProps(2)
            }]).then(function (addData) {
              var merged = mergeAddedItemsIntoCart(nextCart, addData);
              if (cartHasVariant(merged, id2)) {
                return { cart: merged, needsReconcile: true };
              }
              return fetchCart().then(function (full) {
                return { cart: full, needsReconcile: false };
              });
            });
          });
        })
        .then(function (result) {
          var cart = result && result.cart ? result.cart : result;
          if (!cartHasVariant(cart, id2)) {
            throw new Error('Could not upgrade to Gift Set.');
          }

          mode = 'gift';
          activeBundleId = newBundleId;
          walletSelections = { 1: w1, 2: w2, 3: null };
          activeSlot = 2;
          resetIndividualUpsell();
          hydrateSelectionsFromBundle(cart, newBundleId);
          if (!walletSelections[1]) walletSelections[1] = w1;
          if (!walletSelections[2]) walletSelections[2] = w2;
          syncThemeCartState(cart);

          if (result && result.needsReconcile) {
            reconcileBuilderCartInBackground(newBundleId);
          }
          return cart;
        })
        .catch(function (err) {
          mode = previousMode;
          activeBundleId = previousBundleId;
          walletSelections = {
            1: previousSelections[1],
            2: previousSelections[2],
            3: previousSelections[3]
          };
          activeSlot = previousSlot;
          throw err;
        });
    });
  }

  /**
   * Completed Gift Set + selected Wallet 3 → real Executive Set cart + UI.
   * Prefer in-place property promotion (change.js) over remove-all + re-add.
   */
  function transitionGiftToExecutiveSet(w3Entry) {
    var w1 = enrichSelectionCompareAt(walletSelections[1]);
    var w2 = enrichSelectionCompareAt(walletSelections[2]);
    var w3 = enrichSelectionCompareAt(w3Entry);
    var id3 = variantIdOf(w3);
    if (!variantIdOf(w1) || !variantIdOf(w2) || !id3) {
      return Promise.reject(new Error('Could not upgrade to Executive Set.'));
    }

    var previousBundleId = activeBundleId;
    var previousMode = mode;
    var newBundleId = createBuilderBundleId();

    return enqueueBuilderPersist(function () {
      var start =
        lastCart && Array.isArray(lastCart.items)
          ? Promise.resolve(lastCart)
          : fetchCart();

      return start
        .then(function (cart) {
          return removeStandaloneVariantLines([id3], cart);
        })
        .then(function (cart) {
          var line1 = findBundleSlotLine(cart, previousBundleId, 1);
          var line2 = findBundleSlotLine(cart, previousBundleId, 2);
          if (!line1 || !line2) {
            /* Fallback: remove prior set and stamp three executive lines */
            mode = 'executive';
            activeBundleId = newBundleId;
            walletSelections = { 1: w1, 2: w2, 3: w3 };
            return removeBundleById(cart, previousBundleId)
              .then(function (nextCart) {
                return persistBuilderSlotUnlocked(1, w1, nextCart);
              })
              .then(function (nextCart) {
                return persistBuilderSlotUnlocked(2, w2, nextCart);
              })
              .then(function (nextCart) {
                return persistBuilderSlotUnlocked(3, w3, nextCart);
              });
          }

          var execProps = function (slot) {
            return {
              _ev_bundle_id: newBundleId,
              _ev_bundle_offer: 'executive',
              _ev_bundle_slot: String(slot)
            };
          };

          return changeCartLine(line1.key, {
            quantity: parseInt(line1.quantity, 10) || 1,
            properties: execProps(1)
          })
            .then(function () {
              return changeCartLine(line2.key, {
                quantity: parseInt(line2.quantity, 10) || 1,
                properties: execProps(2)
              });
            })
            .then(function (nextCart) {
              var existing3 = findBundleSlotLine(nextCart, newBundleId, 3);
              if (existing3 && parseInt(existing3.variant_id, 10) === id3) {
                return nextCart;
              }
              return addItems([{
                id: id3,
                quantity: 1,
                properties: execProps(3)
              }]).then(function () {
                return fetchCart();
              });
            });
        })
        .then(function (cart) {
          mode = 'executive';
          activeBundleId = newBundleId;
          walletSelections = { 1: w1, 2: w2, 3: w3 };
          activeSlot = 3;
          resetIndividualUpsell();
          hydrateSelectionsFromBundle(cart, newBundleId);
          if (!walletSelections[1]) walletSelections[1] = w1;
          if (!walletSelections[2]) walletSelections[2] = w2;
          if (!walletSelections[3]) walletSelections[3] = w3;
          return syncThemeCartState(cart);
        })
        .catch(function (err) {
          mode = previousMode;
          activeBundleId = previousBundleId;
          walletSelections[3] = null;
          throw err;
        });
    });
  }

  function enqueueBuilderPersist(task) {
    builderPersistQueue = builderPersistQueue
      .catch(function () { /* keep queue alive */ })
      .then(task);
    return builderPersistQueue;
  }

  function ensureActiveBundleId() {
    if (!activeBundleId) activeBundleId = createBuilderBundleId();
    return activeBundleId;
  }

  /** Add or replace one Gift/Executive slot line without touching standalone lines. */
  function persistBuilderSlotUnlocked(slot, entry, cartHint) {
    var variantId = variantIdOf(entry);
    if (!variantId || !(mode === 'gift' || mode === 'executive')) {
      return Promise.resolve(cartHint || lastCart);
    }
    ensureActiveBundleId();
    var setId = activeBundleId;
    var offer = builderOfferKey();
    var start =
      cartHint && Array.isArray(cartHint.items)
        ? Promise.resolve(cartHint)
        : lastCart && Array.isArray(lastCart.items)
          ? Promise.resolve(lastCart)
          : fetchCart();

    return start.then(function (cart) {
      var existing = findBundleSlotLine(cart, setId, slot);
      if (existing && parseInt(existing.variant_id, 10) === variantId) {
        return cart;
      }
      var chain = Promise.resolve(cart);
      if (existing) {
        chain = removeBuilderSlotLines(cart, setId, slot);
      }
      return chain.then(function (nextCart) {
        var stillThere = findBundleSlotLine(nextCart, setId, slot);
        if (stillThere && parseInt(stillThere.variant_id, 10) === variantId) {
          return nextCart;
        }
        return addItems([{
          id: variantId,
          quantity: 1,
          properties: {
            _ev_bundle_id: setId,
            _ev_bundle_offer: offer,
            _ev_bundle_slot: String(slot)
          }
        }]).then(function (addData) {
          var merged = mergeAddedItemsIntoCart(nextCart, addData);
          if (cartHasVariant(merged, variantId)) {
            return merged;
          }
          return fetchCart();
        });
      });
    }).then(function (cart) {
      return syncThemeCartState(cart);
    });
  }

  function persistBuilderSlot(slot, entry) {
    return enqueueBuilderPersist(function () {
      return persistBuilderSlotUnlocked(slot, entry);
    });
  }

  function clearBuilderSlotFromCart(slot) {
    if (!activeBundleId || !(mode === 'gift' || mode === 'executive')) {
      return Promise.resolve(lastCart);
    }
    var setId = activeBundleId;
    return enqueueBuilderPersist(function () {
      var start =
        lastCart && Array.isArray(lastCart.items)
          ? Promise.resolve(lastCart)
          : fetchCart();
      return start
        .then(function (cart) {
          return removeBuilderSlotLines(cart, setId, slot);
        })
        .then(function (cart) {
          /* Authoritative downgrade: Gift→Individual / Executive→Gift|Individual */
          return syncDrawerModeAfterCartMutation(cart);
        });
    });
  }

  function ensureBuilderSelectionsInCart() {
    if (!(mode === 'gift' || mode === 'executive')) {
      return Promise.resolve(lastCart);
    }
    ensureActiveBundleId();
    var slots = [1, 2];
    if (mode === 'executive') slots.push(3);
    return enqueueBuilderPersist(function () {
      return fetchCart().then(function (cart) {
        var chain = Promise.resolve(cart);
        slots.forEach(function (slot) {
          var entry = walletSelections[slot];
          if (!entry) return;
          chain = chain.then(function (prev) {
            return persistBuilderSlotUnlocked(slot, entry, prev);
          });
        });
        return chain;
      });
    });
  }

  /**
   * Cart is source of truth for Gift/Executive builders.
   * Resume an existing set when possible; otherwise stamp Wallet 1 immediately.
   */
  function bootstrapBuilderCart(cart, buildContext) {
    var offer = builderOfferKey();
    var preferredVariantId = buildContext && buildContext.variantId
      ? buildContext.variantId
      : variantIdOf(walletSelections[1]);
    var resumeId = findResumeBundleId(cart, offer, preferredVariantId);

    if (resumeId) {
      activeBundleId = resumeId;
      hydrateSelectionsFromBundle(cart, resumeId);
      if (!walletSelections[1] && buildContext) {
        walletSelections[1] = enrichSelectionCompareAt({
          variantId: buildContext.variantId,
          productId: buildContext.productId || 0,
          productTitle: buildContext.productTitle || '',
          variantTitle: buildContext.variantTitle || '',
          price: buildContext.price || 0,
          compareAt: buildContext.compareAt || null,
          image: buildContext.image || ''
        });
      }
    } else {
      activeBundleId = createBuilderBundleId();
      if (!walletSelections[1] && buildContext) {
        resetSelections(buildContext);
      }
    }

    if (!walletSelections[1]) {
      return Promise.resolve(syncThemeCartState(cart));
    }

    return persistBuilderSlotUnlocked(1, walletSelections[1]).then(function (nextCart) {
      hydrateSelectionsFromBundle(nextCart, activeBundleId);
      if (!walletSelections[1] && buildContext) {
        walletSelections[1] = enrichSelectionCompareAt({
          variantId: buildContext.variantId,
          productId: buildContext.productId || 0,
          productTitle: buildContext.productTitle || '',
          variantTitle: buildContext.variantTitle || '',
          price: buildContext.price || 0,
          compareAt: buildContext.compareAt || null,
          image: buildContext.image || ''
        });
      }
      if (!walletSelections[2]) activeSlot = 2;
      else if (mode === 'executive' && !walletSelections[3]) activeSlot = 3;
      return nextCart;
    });
  }

  function getDiscountApi() {
    return window.EV && window.EV.CartDiscount ? window.EV.CartDiscount : null;
  }

  function getConfig() {
    return window.EV_CONFIG || window.FPV_CONFIG || {};
  }

  function getMoneyFormat() {
    return getConfig().moneyFormat || '${{amount}}';
  }

  function formatMoney(cents) {
    var amount = typeof cents === 'string' ? parseInt(cents, 10) : cents;
    if (isNaN(amount)) amount = 0;
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, getMoneyFormat());
    }
    return '$' + (amount / 100).toFixed(2);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function giftPct() {
    var n = parseInt(drawer && drawer.getAttribute('data-gift-pct'), 10);
    return isNaN(n) ? 25 : n;
  }

  function executivePct() {
    var n = parseInt(drawer && drawer.getAttribute('data-executive-pct'), 10);
    return isNaN(n) ? 30 : n;
  }

  function variantIdOf(entry) {
    if (!entry) return null;
    if (typeof entry === 'number' || typeof entry === 'string') return parseInt(entry, 10) || null;
    return parseInt(entry.variantId || entry.variant_id || entry.id, 10) || null;
  }

  function normalizeCatalog(raw) {
    if (!Array.isArray(raw)) return [];
    return raw.map(function (product) {
      var images = [];
      if (Array.isArray(product.images)) {
        product.images.forEach(function (img) {
          if (!img) return;
          var src = typeof img === 'string' ? img : (img.src || img.src_sm || null);
          if (!src) return;
          images.push({
            src: src,
            src_sm: (typeof img === 'object' && img.src_sm) || src,
            width: (typeof img === 'object' && img.width) || null,
            height: (typeof img === 'object' && img.height) || null,
            alt: (typeof img === 'object' && img.alt) || product.title || ''
          });
        });
      }
      if (!images.length && product.image) {
        images.push({
          src: product.image,
          src_sm: product.image,
          width: null,
          height: null,
          alt: product.title || ''
        });
      }
      return {
        id: product.id,
        title: product.title,
        url: product.url,
        handle: product.handle,
        image: product.image,
        images: images,
        variants: (product.variants || []).map(function (v) {
          return {
            variant_id: v.variant_id || v.id,
            title: v.title,
            price: v.price,
            compare_at_price: v.compare_at_price || null,
            available: v.available !== false,
            image: v.image
          };
        }).filter(function (v) {
          return v.available !== false && v.variant_id;
        })
      };
    }).filter(function (p) {
      return p.variants && p.variants.length;
    });
  }

  function parseJsonNode(selector) {
    var node = document.querySelector(selector);
    if (!node) return [];
    try {
      return JSON.parse(node.textContent || '[]') || [];
    } catch (e) {
      return [];
    }
  }

  function loadWalletProducts() {
    var primary = normalizeCatalog(parseJsonNode('[data-ev-wallet-products]'));
    if (primary.length) return primary;
    return normalizeCatalog(parseJsonNode('[data-ev-eligible-wallets]'));
  }

  /** Parse wallet catalog once per page lifetime. */
  function ensureWalletProducts() {
    if (walletProducts && walletProducts.length) return walletProducts;
    walletProducts = loadWalletProducts();
    return walletProducts;
  }

  /**
   * Shared /cart.js fetch. Concurrent callers await the same in-flight request.
   * options.force bypasses dedupe (rare).
   */
  function fetchCart(options) {
    options = options || {};
    if (options.force) cartFetchInflight = null;
    if (cartFetchInflight) return cartFetchInflight;
    cartFetchInflight = fetch(getConfig().cartJsUrl || '/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && typeof cart === 'object') lastCart = cart;
        return cart;
      })
      .finally(function () {
        cartFetchInflight = null;
      });
    return cartFetchInflight;
  }

  function getUsableCachedCart() {
    if (lastCart && typeof lastCart === 'object' && Array.isArray(lastCart.items)) {
      return lastCart;
    }
    return null;
  }

  function addItems(items) {
    if (window.EV && typeof window.EV.addItemsToCart === 'function') {
      return window.EV.addItemsToCart(items);
    }
    return fetch(getConfig().cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        if (!response.ok) {
          throw new Error((data && (data.description || data.message)) || 'Could not add to cart');
        }
        return data;
      });
    });
  }

  function shortProductTitle(title) {
    return String(title || '')
      .replace(/^EliteVault™?\s*/i, '')
      .replace(/^EliteVault\s*/i, '')
      .trim();
  }

  function showError(message) {
    var el = drawer.querySelector('[data-ev-cart-drawer-error]');
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.textContent = message;
    el.hidden = false;
  }

  function captureDrawerScroll() {
    var body = getPrimaryScrollEl();
    var viewport = drawer && drawer.querySelector('[data-ev-cart-drawer-viewport]');
    var slots = getSlotsViewport();
    return {
      bodyTop: body ? body.scrollTop || 0 : 0,
      productsLeft: viewport ? viewport.scrollLeft || 0 : 0,
      slotsLeft: slots ? slots.scrollLeft || 0 : 0
    };
  }

  function restoreDrawerScroll(snap) {
    if (!snap || !drawer) return;
    var apply = function () {
      var body = getPrimaryScrollEl();
      if (body) body.scrollTop = snap.bodyTop;
      var viewport = drawer.querySelector('[data-ev-cart-drawer-viewport]');
      if (viewport) viewport.scrollLeft = snap.productsLeft;
      var slots = getSlotsViewport();
      if (slots) slots.scrollLeft = snap.slotsLeft;
    };
    apply();
    window.requestAnimationFrame(apply);
  }

  function beginSelectPending(variantId, slot, previousEntry) {
    selectPending = {
      variantId: parseInt(variantId, 10) || 0,
      slot: slot,
      previousEntry: previousEntry || null
    };
    if (!drawer || !selectPending.variantId) return;
    var track = drawer.querySelector('[data-ev-cart-drawer-track]');
    if (!track) return;
    track.querySelectorAll('.ev-drawer-card[data-variant-id]').forEach(function (card) {
      var vid = parseInt(card.getAttribute('data-variant-id'), 10) || 0;
      var isTarget = vid === selectPending.variantId;
      /* Invalidate cached state so post-pending applyCardState always re-enables */
      card.removeAttribute('data-ev-card-state');
      card.classList.toggle('is-pending', isTarget);
      var btn = card.querySelector('[data-ev-drawer-pick]');
      if (!btn || card.classList.contains('is-sold-out')) return;
      btn.disabled = true;
      if (isTarget) {
        btn.setAttribute('aria-busy', 'true');
        if (btn.textContent !== 'Adding…') btn.textContent = 'Adding…';
        card.setAttribute('data-ev-card-state', 'pending||Adding…|1');
      }
    });
  }

  function endSelectPending() {
    selectPending = null;
    if (!drawer) return;
    var track = drawer.querySelector('[data-ev-cart-drawer-track]');
    if (!track) return;
    track.querySelectorAll('.ev-drawer-card[data-variant-id]').forEach(function (card) {
      card.classList.remove('is-pending');
      card.removeAttribute('data-ev-card-state');
      var btn = card.querySelector('[data-ev-drawer-pick]');
      if (btn) btn.removeAttribute('aria-busy');
    });
  }

  function setBusy(busy) {
    if (checkoutPreparing) return;
    var next = !!busy;
    var changed = submitting !== next;
    submitting = next;
    if (drawer) drawer.classList.toggle('is-busy', submitting);
    updateFooter(lastCart);
    /* Re-enable Select buttons after persist — cards may have rendered while busy. */
    if (changed && !submitting && drawer && isOpen) {
      updateWalletCardStates();
    }
  }

  function findVariantMeta(variantId) {
    var id = parseInt(variantId, 10);
    if (!id) return null;
    for (var i = 0; i < walletProducts.length; i++) {
      var product = walletProducts[i];
      var variants = product.variants || [];
      for (var j = 0; j < variants.length; j++) {
        var vid = parseInt(variants[j].variant_id || variants[j].id, 10);
        if (vid === id) {
          return { product: product, variant: variants[j] };
        }
      }
    }
    return null;
  }

  /**
   * Authoritative compare_at from Liquid (same source PDP uses).
   * Ajax /cart.js omits compare_at; /products/*.js is unreliable in some contexts.
   * Order: Liquid map → data-ev-product-variants → wallet catalog JSON.
   */
  function readLiquidCompareAtMap() {
    if (liquidCompareAtMapReady) return liquidCompareAtMapCache || {};
    liquidCompareAtMapReady = true;
    var node = document.querySelector('[data-ev-variant-compare-map]');
    if (!node) {
      liquidCompareAtMapCache = {};
      return liquidCompareAtMapCache;
    }
    try {
      var parsed = JSON.parse(node.textContent || '{}') || {};
      liquidCompareAtMapCache = typeof parsed === 'object' && parsed ? parsed : {};
    } catch (e) {
      liquidCompareAtMapCache = {};
    }
    return liquidCompareAtMapCache;
  }

  function compareAtFromProductVariantsJson(variantId) {
    var id = parseInt(variantId, 10);
    if (!id) return 0;
    var node = document.querySelector('[data-ev-product-variants]');
    if (!node) return 0;
    try {
      var list = JSON.parse(node.textContent || '[]') || [];
      for (var i = 0; i < list.length; i++) {
        if (parseInt(list[i].id, 10) === id) {
          return parseInt(list[i].compare_at_price, 10) || 0;
        }
      }
    } catch (e) { /* ignore */ }
    return 0;
  }

  function compareAtFromWalletCatalogJson(variantId) {
    var meta = findVariantMeta(variantId);
    if (!meta || !meta.variant) return 0;
    return parseInt(meta.variant.compare_at_price, 10) || 0;
  }

  function resolveVariantCompareAtCents(variantId, unitPriceCents) {
    var id = parseInt(variantId, 10);
    var price = parseInt(unitPriceCents, 10) || 0;
    if (!id) return 0;

    var map = readLiquidCompareAtMap();
    var fromMap = parseInt(map[String(id)], 10) || 0;
    if (fromMap > price) return fromMap;

    var fromPdp = compareAtFromProductVariantsJson(id);
    if (fromPdp > price) return fromPdp;

    var fromCatalog = compareAtFromWalletCatalogJson(id);
    if (fromCatalog > price) return fromCatalog;

    return 0;
  }

  function selectionEntryFromMeta(meta, fallback) {
    if (!meta && !fallback) return null;
    if (meta) {
      return enrichSelectionCompareAt({
        variantId: meta.variant.variant_id || meta.variant.id,
        productId: meta.product.id,
        productTitle: meta.product.title,
        variantTitle: meta.variant.title,
        price: meta.variant.price,
        compareAt: meta.variant.compare_at_price,
        image: meta.variant.image || meta.product.image
      });
    }
    return enrichSelectionCompareAt({
      variantId: fallback.variantId,
      productId: fallback.productId || 0,
      productTitle: fallback.productTitle || '',
      variantTitle: fallback.variantTitle || '',
      price: fallback.price || 0,
      compareAt: fallback.compareAt || null,
      image: fallback.image || ''
    });
  }

  function resetSelections(buildContext) {
    walletSelections = { 1: null, 2: null, 3: null };
    activeSlot = 2;
    if (buildContext) {
      walletSelections[1] = enrichSelectionCompareAt({
        variantId: buildContext.variantId,
        productId: buildContext.productId || 0,
        productTitle: buildContext.productTitle || '',
        variantTitle: buildContext.variantTitle || '',
        price: buildContext.price || 0,
        compareAt: buildContext.compareAt || null,
        image: buildContext.image || ''
      });
    }
  }

  function requiredSlots() {
    if (mode === 'gift') return 2;
    if (mode === 'executive') return 3;
    return 2; // individual upsell: wallet 1 in cart + wallet 2 pending
  }

  function selectedBuilderCount() {
    var count = walletSelections[1] ? 1 : 0;
    if (walletSelections[2]) count += 1;
    if (mode === 'executive' && walletSelections[3]) count += 1;
    return count;
  }

  function builderComplete() {
    if (mode === 'gift') return !!(walletSelections[1] && walletSelections[2]);
    if (mode === 'executive') return !!(walletSelections[1] && walletSelections[2] && walletSelections[3]);
    return false;
  }

  function builderFilledEntries(limit) {
    var entries = [];
    if (walletSelections[1]) entries.push(walletSelections[1]);
    if (walletSelections[2]) entries.push(walletSelections[2]);
    if (mode === 'executive' && walletSelections[3]) entries.push(walletSelections[3]);
    if (typeof limit === 'number' && limit > 0) entries = entries.slice(0, limit);
    return entries;
  }

  /** Dynamic EXTRA bundle discount in cents — Shopify cart state only (never invent). */
  function calcBuilderSavingsCents(limit) {
    var entries = builderFilledEntries(limit);
    if (entries.length < 2) return 0;

    var shopify = resolveActiveBundleShopifyDiscount(lastCart);
    if (shopify && shopify.applied) return shopify.amount;
    return 0;
  }

  function getActiveBundleCartLines(cart) {
    cart = cart || lastCart;
    if (!activeBundleId || !cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(function (item) {
      var props = lineBundleProps(item);
      return props[PROP_BUNDLE_ID] === activeBundleId;
    });
  }

  /**
   * Authoritative Shopify automatic discount for the active Gift/Executive set.
   * Uses discount_allocations, line_level_total_discount, and cart.total_discount.
   * Never claims a % from quantity alone.
   */
  function resolveActiveBundleShopifyDiscount(cart) {
    cart = cart || lastCart;
    var lines = getActiveBundleCartLines(cart);
    if (!lines.length) {
      return { applied: false, amount: 0, percent: 0, saleSubtotal: 0, title: '', kind: 'unknown' };
    }
    var api = getDiscountApi();
    if (api && typeof api.resolveShopifyDiscountForItems === 'function') {
      return api.resolveShopifyDiscountForItems(cart, lines);
    }

    var amount = 0;
    var saleSubtotal = 0;
    lines.forEach(function (item) {
      var allocated = 0;
      (item.discount_allocations || []).forEach(function (alloc) {
        allocated += parseInt(alloc.amount, 10) || 0;
      });
      if (!(allocated > 0)) {
        allocated = parseInt(item.line_level_total_discount, 10) || parseInt(item.total_discount, 10) || 0;
      }
      if (!(allocated > 0)) {
        var original = parseInt(item.original_line_price, 10) || 0;
        var final = parseInt(item.final_line_price, 10) || 0;
        if (original > final) allocated = original - final;
      }
      amount += allocated;
      var origLine = parseInt(item.original_line_price, 10);
      if (origLine > 0) saleSubtotal += origLine;
      else saleSubtotal += (parseInt(item.price, 10) || 0) * (parseInt(item.quantity, 10) || 1);
    });
    if (
      amount <= 0 &&
      cart &&
      Array.isArray(cart.items) &&
      lines.length === cart.items.length
    ) {
      amount = parseInt(cart.total_discount, 10) || 0;
    }
    var percent = amount > 0 && saleSubtotal > 0 ? Math.round((amount / saleSubtotal) * 100) : 0;
    return {
      applied: amount > 0,
      amount: amount,
      percent: percent,
      saleSubtotal: saleSubtotal,
      title: '',
      kind: 'unknown'
    };
  }

  /** Unit regular value from Shopify Liquid compare_at when > price; else selling price. */
  function regularUnitCentsForCartItem(item) {
    var price = parseInt(item && item.price, 10) || 0;
    var compareAt = parseInt(item && item.compare_at_price, 10) || 0;
    var vid = parseInt(item && item.variant_id, 10) || 0;

    if (!(compareAt > price) && vid) {
      var resolved = resolveVariantCompareAtCents(vid, price);
      if (resolved > price) compareAt = resolved;
    }

    if (!(compareAt > price) && vid) {
      var meta = findVariantMeta(vid);
      if (meta && meta.variant) {
        var catalogPrice = parseInt(meta.variant.price, 10) || 0;
        if (catalogPrice > 0) price = catalogPrice;
        var catalogCompare = parseInt(meta.variant.compare_at_price, 10) || 0;
        if (catalogCompare > price) compareAt = catalogCompare;
      }
    }

    if (!(compareAt > price) && vid) {
      [1, 2, 3].forEach(function (slot) {
        var sel = enrichSelectionCompareAt(walletSelections[slot]);
        if (sel && variantIdOf(sel) === vid) {
          var selPrice = parseInt(sel.price, 10) || 0;
          var selCompare = parseInt(sel.compareAt, 10) || 0;
          if (selPrice > 0) price = selPrice;
          if (selCompare > price) compareAt = selCompare;
        }
      });
    }
    return compareAt > price ? compareAt : price;
  }

  /**
   * TOTAL SAVINGS for a completed Gift/Executive builder.
   * REGULAR = sum of Liquid compare_at when > price, else price.
   * FINAL PAYABLE = Shopify cart.total_price when the active bundle is the cart,
   * otherwise sum of bundle line final_line_price.
   * EXTRA (header) comes only from Shopify-applied cart discounts.
   */
  function calcBuilderTotalBreakdown(cart) {
    if (!(mode === 'gift' || mode === 'executive') || !builderComplete()) return null;
    cart = cart || lastCart;
    var required = requiredSlots();
    var lines = getActiveBundleCartLines(cart);
    if (lines.length < required) return null;

    var saleSubtotal = 0;
    var regularValue = 0;
    var finalFromLines = 0;

    lines.forEach(function (item) {
      var qty = parseInt(item.quantity, 10) || 1;
      var unit = parseInt(item.price, 10) || 0;
      var originalLine = parseInt(item.original_line_price, 10);
      saleSubtotal += originalLine > 0 ? originalLine : unit * qty;
      regularValue += regularUnitCentsForCartItem(item) * qty;
      finalFromLines += parseInt(item.final_line_price, 10) || 0;
    });

    if (finalFromLines <= 0) return null;

    var cartItemCount = cart && Array.isArray(cart.items) ? cart.items.length : 0;
    var finalTotal =
      cart &&
      lines.length === cartItemCount &&
      parseInt(cart.total_price, 10) > 0
        ? parseInt(cart.total_price, 10)
        : finalFromLines;

    var shopify = resolveActiveBundleShopifyDiscount(cart);
    var extraDiscount = shopify.applied ? shopify.amount : 0;
    var effectivePct = shopify.applied ? shopify.percent : 0;

    var totalSavings = Math.max(0, regularValue - finalTotal);
    var totalSavingsPercent =
      regularValue > 0 ? Math.round((totalSavings / regularValue) * 100) : 0;

    return {
      regularValue: regularValue,
      saleSubtotal: saleSubtotal,
      bundleDiscount: extraDiscount,
      finalTotal: finalTotal,
      totalSavings: totalSavings,
      totalSavingsPercent: totalSavingsPercent,
      pct: effectivePct,
      shopifyTitle: shopify.title || '',
      shopifyKind: shopify.kind || 'unknown',
      shopifyApplied: !!shopify.applied
    };
  }

  function hideTotalSavingsSummary() {
    var el = drawer && drawer.querySelector('[data-ev-cart-drawer-total-savings]');
    if (!el) return;
    var regularEl = el.querySelector('[data-ev-total-savings-regular]');
    var finalEl = el.querySelector('[data-ev-total-savings-final]');
    var youEl = el.querySelector('[data-ev-total-savings-you]');
    if (regularEl) regularEl.textContent = '';
    if (finalEl) finalEl.textContent = '';
    if (youEl) youEl.textContent = '';
    /* Incomplete Gift/Executive: fully hide — do not reserve a blank TOTAL SAVINGS block */
    el.classList.remove('is-reserved');
    el.hidden = true;
    el.setAttribute('hidden', '');
  }

  function updateTotalSavingsSummary(cart) {
    var el = drawer && drawer.querySelector('[data-ev-cart-drawer-total-savings]');
    if (!el) return;

    /* Only show TOTAL SAVINGS when the Gift/Executive set is complete */
    if ((mode === 'gift' || mode === 'executive') && !builderComplete()) {
      hideTotalSavingsSummary();
      return null;
    }

    var breakdown = calcBuilderTotalBreakdown(cart || lastCart);
    if (!breakdown || breakdown.finalTotal <= 0) {
      hideTotalSavingsSummary();
      return null;
    }

    var regularEl = el.querySelector('[data-ev-total-savings-regular]');
    var finalEl = el.querySelector('[data-ev-total-savings-final]');
    var youEl = el.querySelector('[data-ev-total-savings-you]');

    if (regularEl) {
      if (breakdown.regularValue > breakdown.finalTotal) {
        regularEl.textContent = formatMoney(breakdown.regularValue);
        regularEl.hidden = false;
        regularEl.removeAttribute('hidden');
      } else {
        regularEl.textContent = '';
        regularEl.hidden = true;
        regularEl.setAttribute('hidden', '');
      }
    }
    if (finalEl) finalEl.textContent = formatMoney(breakdown.finalTotal);
    if (youEl) {
      youEl.textContent =
        'You save ' +
        formatMoney(breakdown.totalSavings) +
        ' · ' +
        breakdown.totalSavingsPercent +
        '%';
    }

    el.classList.remove('is-reserved');
    el.hidden = false;
    el.removeAttribute('hidden');
    return breakdown;
  }

  function activeBundlePercent() {
    return mode === 'executive' ? executivePct() : giftPct();
  }

  function activeBundleTitle() {
    if (mode === 'executive') return '3-Wallet Bundle';
    return '2-Wallet Bundle';
  }

  /** Live savings for the current builder / individual pair selection. */
  function liveBundleSavingsCents() {
    if (mode === 'gift' || mode === 'executive') {
      return calcBuilderSavingsCents();
    }
    if (isIndividualMode() || mode === 'complete') {
      return calcIndividualPairSavings();
    }
    return 0;
  }

  function formatSavingsLine(pct, cents) {
    if (cents > 0) {
      return 'SAVE ' + pct + '% · You save ' + formatMoney(cents);
    }
    return 'SAVE ' + pct + '%';
  }

  function builderHeaderEyebrow() {
    if (mode === 'gift') return 'YOUR GIFT SET';
    if (mode === 'executive') return 'YOUR EXECUTIVE SET';
    return '';
  }

  function builderHeaderStatus() {
    if (mode === 'gift') {
      return builderComplete() ? 'Set Complete' : 'Choose Wallet 2';
    }
    if (mode === 'executive') {
      if (builderComplete()) return 'Set Complete';
      if (walletSelections[2]) return 'Choose Wallet 3';
      return 'Choose Wallet 2';
    }
    return '';
  }

  /**
   * Compact header savings for Individual + Gift + Executive.
   * Completed Gift/Executive EXTRA line uses Shopify-applied discount only.
   */
  function updatePremiumSavingsUI() {
    var headerSavings = drawer.querySelector('[data-ev-cart-drawer-header-savings]');
    var headerPct = drawer.querySelector('[data-ev-header-savings-pct]');
    var headerAmt = drawer.querySelector('[data-ev-header-savings-amount]');
    var legacySavings = drawer.querySelector('[data-ev-cart-drawer-savings]');
    var ctaSave = drawer.querySelector('[data-ev-cart-drawer-cta-save]');
    var confirmBadge = drawer.querySelector('[data-ev-cart-drawer-confirm-badge]');
    var offerBadge = drawer.querySelector('[data-ev-cart-drawer-offer-badge]');
    var isBuilder = mode === 'gift' || mode === 'executive';
    var complete = isBuilder ? builderComplete() : hasIndividualSecondWallet();
    var cents = 0;
    var pct = 0;

    if (isBuilder && complete) {
      var shopifyExtra = resolveActiveBundleShopifyDiscount(lastCart);
      if (shopifyExtra && shopifyExtra.applied) {
        cents = shopifyExtra.amount;
        pct = shopifyExtra.percent;
      }
    } else if (!isBuilder) {
      cents = liveBundleSavingsCents();
      pct = activeBundlePercent();
    }

    if (confirmBadge) {
      confirmBadge.hidden = true;
      confirmBadge.setAttribute('hidden', '');
      confirmBadge.textContent = '';
    }
    if (offerBadge) {
      offerBadge.hidden = true;
      offerBadge.setAttribute('hidden', '');
    }
    if (legacySavings) {
      legacySavings.hidden = true;
      legacySavings.setAttribute('hidden', '');
    }
    if (ctaSave) {
      ctaSave.hidden = true;
      ctaSave.setAttribute('hidden', '');
      ctaSave.textContent = '';
    }

    if (headerSavings) {
      /* Individual selecting: no CURRENT SELECTION / status clutter in the header. */
      if (isIndividualSelecting() && !hasIndividualSecondWallet()) {
        headerSavings.hidden = true;
        headerSavings.setAttribute('hidden', '');
        headerSavings.classList.remove('is-selection-status');
        headerSavings.classList.remove('is-reserved');
        var labelEl0 = headerSavings.querySelector('.ev-cart-drawer__header-savings-label');
        if (labelEl0) labelEl0.textContent = 'YOUR EXTRA SAVINGS';
        if (headerPct) headerPct.textContent = '';
        if (headerAmt) headerAmt.textContent = '';
        hideTotalSavingsSummary();
      } else if (complete && cents > 0 && pct > 0) {
        headerSavings.hidden = false;
        headerSavings.removeAttribute('hidden');
        headerSavings.classList.remove('is-selection-status');
        headerSavings.classList.remove('is-reserved');
        var labelEl2 = headerSavings.querySelector('.ev-cart-drawer__header-savings-label');
        if (labelEl2) labelEl2.textContent = 'YOUR EXTRA SAVINGS';
        if (headerPct) headerPct.textContent = pct + '% OFF';
        if (headerAmt) headerAmt.textContent = 'You save ' + formatMoney(cents);
        var iconEl2 = headerSavings.querySelector('.ev-cart-drawer__header-savings-icon');
        var sepEl2 = headerSavings.querySelector('.ev-cart-drawer__header-savings-sep');
        if (iconEl2) iconEl2.hidden = false;
        if (sepEl2) sepEl2.hidden = false;
        if (isBuilder) updateTotalSavingsSummary(lastCart);
      } else if (isBuilder && complete) {
        /* Set complete but EXTRA not yet applied — show TOTAL when valid; keep EXTRA slot reserved */
        headerSavings.hidden = false;
        headerSavings.removeAttribute('hidden');
        headerSavings.classList.remove('is-selection-status');
        headerSavings.classList.add('is-reserved');
        var labelElC = headerSavings.querySelector('.ev-cart-drawer__header-savings-label');
        if (labelElC) labelElC.textContent = 'YOUR EXTRA SAVINGS';
        if (headerPct) headerPct.textContent = '';
        if (headerAmt) headerAmt.textContent = '';
        updateTotalSavingsSummary(lastCart);
      } else if (isBuilder) {
        /* Incomplete: reserve EXTRA band only — never reserve blank TOTAL SAVINGS */
        headerSavings.hidden = false;
        headerSavings.removeAttribute('hidden');
        headerSavings.classList.remove('is-selection-status');
        headerSavings.classList.add('is-reserved');
        var labelElR = headerSavings.querySelector('.ev-cart-drawer__header-savings-label');
        if (labelElR) labelElR.textContent = 'YOUR EXTRA SAVINGS';
        if (headerPct) headerPct.textContent = '';
        if (headerAmt) headerAmt.textContent = '';
        var iconElR = headerSavings.querySelector('.ev-cart-drawer__header-savings-icon');
        var sepElR = headerSavings.querySelector('.ev-cart-drawer__header-savings-sep');
        if (iconElR) iconElR.hidden = false;
        if (sepElR) sepElR.hidden = false;
        hideTotalSavingsSummary();
      } else {
        headerSavings.hidden = true;
        headerSavings.setAttribute('hidden', '');
        headerSavings.classList.remove('is-selection-status');
        headerSavings.classList.remove('is-reserved');
        var labelEl3 = headerSavings.querySelector('.ev-cart-drawer__header-savings-label');
        if (labelEl3) labelEl3.textContent = 'YOUR EXTRA SAVINGS';
        if (headerPct) headerPct.textContent = '';
        if (headerAmt) headerAmt.textContent = '';
        hideTotalSavingsSummary();
      }
    }
  }

  function suppressConfirmationSummary() {
    var applied = drawer.querySelector('[data-ev-cart-drawer-applied]');
    var setComplete = drawer.querySelector('[data-ev-cart-drawer-set-complete]');
    if (applied) {
      applied.hidden = true;
      applied.setAttribute('hidden', '');
    }
    if (setComplete) {
      setComplete.hidden = true;
      setComplete.setAttribute('hidden', '');
    }
    hideTotals();
  }

  function slotLabelsForVariant(variantId) {
    var labels = [];
    var id = parseInt(variantId, 10);
    [1, 2, 3].forEach(function (slot) {
      if (variantIdOf(walletSelections[slot]) === id) {
        labels.push('WALLET ' + slot + ' SELECTED');
      }
    });
    if (mode === 'complete' && variantIdOf(walletSelections[2]) === id && !labels.length) {
      labels = ['WALLET 2 SELECTED'];
    }
    return labels;
  }

  function hasIndividualSecondWallet() {
    return !!(walletSelections[2] || individualUpsell.wallet2);
  }

  function setProductsSubtext(text) {
    var sub = drawer.querySelector('[data-ev-cart-drawer-products-sub]');
    if (!sub) return;
    if (text) {
      sub.textContent = text;
      sub.classList.remove('is-reserved');
      sub.hidden = false;
      sub.removeAttribute('hidden');
    } else {
      /* No invisible reserved band — incomplete Gift/Executive must not leave blank gaps */
      sub.textContent = '';
      sub.classList.remove('is-reserved');
      sub.hidden = true;
      sub.setAttribute('hidden', '');
    }
  }

  function advanceActiveSlot() {
    if (mode === 'gift') {
      activeSlot = 2;
      return;
    }
    if (mode === 'executive') {
      if (!walletSelections[2]) activeSlot = 2;
      else if (!walletSelections[3]) activeSlot = 3;
      else activeSlot = 3;
    }
  }

  function moneyPairHtml(price, compareAt) {
    var html = '<span class="ev-drawer-card__price-row">' +
      '<span class="ev-drawer-card__price">' + formatMoney(price) + '</span>';
    if (compareAt && parseInt(compareAt, 10) > parseInt(price, 10)) {
      html += '<span class="ev-drawer-card__price-compare">' + formatMoney(compareAt) + '</span>';
    }
    html += '</span>';
    return html;
  }

  /* ---------- Totals ---------- */

  function hideTotals() {
    var totals = drawer.querySelector('[data-ev-cart-drawer-totals]');
    var note = drawer.querySelector('[data-ev-cart-drawer-totals-note]');
    var applied = drawer.querySelector('[data-ev-cart-drawer-applied]');
    if (totals) {
      totals.hidden = true;
      totals.setAttribute('hidden', '');
    }
    if (note) {
      note.hidden = true;
      note.setAttribute('hidden', '');
    }
    ['data-ev-drawer-regular', 'data-ev-drawer-discount', 'data-ev-drawer-final'].forEach(function (sel) {
      var el = drawer.querySelector('[' + sel + ']');
      if (el) el.textContent = '';
    });
    if (applied && mode !== 'complete') {
      applied.hidden = true;
      applied.setAttribute('hidden', '');
    }
  }

  function fillCartTotals(cart, savings) {
    var totals = drawer.querySelector('[data-ev-cart-drawer-totals]');
    var note = drawer.querySelector('[data-ev-cart-drawer-totals-note]');
    if (!totals || !cart) return;
    var discountApi = getDiscountApi();
    var regular = parseInt(cart.original_total_price, 10);
    if (!regular && discountApi && discountApi.getEligibleSubtotalCents) {
      regular = discountApi.getEligibleSubtotalCents(cart);
    }
    if (!regular) {
      regular = parseInt(cart.items_subtotal_price, 10) || 0;
    }
    var final = parseInt(cart.total_price, 10) || 0;
    var disc = parseInt(cart.total_discount, 10);
    if (!disc || disc < 0) {
      disc = parseInt(savings, 10) || Math.max(0, regular - final);
    }
    var regEl = drawer.querySelector('[data-ev-drawer-regular]');
    var discEl = drawer.querySelector('[data-ev-drawer-discount]');
    var finEl = drawer.querySelector('[data-ev-drawer-final]');
    if (regEl) regEl.textContent = formatMoney(regular);
    if (discEl) discEl.textContent = disc > 0 ? '−' + formatMoney(disc) : formatMoney(0);
    if (finEl) finEl.textContent = formatMoney(final);
    totals.hidden = false;
    totals.removeAttribute('hidden');
    if (note) {
      note.hidden = true;
      note.setAttribute('hidden', '');
    }
  }

  function fillPreviewTotals() {
    suppressConfirmationSummary();
    updatePremiumSavingsUI();
  }

  /* ---------- Shared wallet cards ---------- */

  function pickDisplayVariant(product) {
    var variants = product.variants || [];
    var discountApi = getDiscountApi();
    if (discountApi && discountApi.pickRecommendVariant) {
      var picked = discountApi.pickRecommendVariant(product, lastCart || { items: [] });
      if (picked) return picked;
    }
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].available !== false) return variants[i];
    }
    return variants[0] || null;
  }

  function isConfirmMode() {
    return mode === 'confirm';
  }

  function hideConfirmPanel() {
    var panelEl = drawer && drawer.querySelector('[data-ev-cart-drawer-confirm-offer]');
    if (!panelEl) return;
    panelEl.hidden = true;
    panelEl.setAttribute('hidden', '');
  }

  function renderConfirmPanel(cart) {
    var panelEl = drawer.querySelector('[data-ev-cart-drawer-confirm-offer]');
    if (!panelEl || !confirmContext) {
      hideConfirmPanel();
      return;
    }

    var qty = parseInt(confirmContext.quantity, 10) || 1;
    var offerKey = confirmContext.offer || (qty <= 1 ? 'individual' : qty === 2 ? 'gift' : 'executive');
    var isIndividual = offerKey === 'individual';
    var unit = parseInt(confirmContext.unitPrice, 10) || 0;
    var compareAt = parseInt(confirmContext.compareAt, 10) || 0;
    var image = confirmContext.image || '';
    var title = shortProductTitle(confirmContext.productName || 'Your wallet');
    var pct = parseInt(confirmContext.discountPercent, 10) || 0;
    var cartDiscount = cart ? parseInt(cart.total_discount, 10) || 0 : 0;
    var cartTotal = cart ? parseInt(cart.total_price, 10) || 0 : 0;
    var cartItem = cart && cart.items && cart.items[0] ? cart.items[0] : null;
    if ((!unit || unit <= 0) && cartItem) {
      unit = parseInt(cartItem.final_price != null ? cartItem.final_price : cartItem.price, 10) || unit;
    }
    if ((!compareAt || compareAt <= unit) && cartItem) {
      var lineCompare = parseInt(cartItem.compare_at_price, 10) || 0;
      if (lineCompare > unit) compareAt = lineCompare;
    }
    /* Ajax /cart.js omits compare_at_price — resolve from catalog JSON */
    if ((!compareAt || compareAt <= unit) && confirmContext.variantId) {
      var catalogMeta = findVariantMeta(confirmContext.variantId);
      if (catalogMeta && catalogMeta.variant) {
        var catalogCompare = parseInt(catalogMeta.variant.compare_at_price, 10) || 0;
        if (catalogCompare > unit) compareAt = catalogCompare;
      }
    }
    if ((!compareAt || compareAt <= unit) && cartItem && cartItem.variant_id) {
      var cartMeta = findVariantMeta(cartItem.variant_id);
      if (cartMeta && cartMeta.variant) {
        var cartCatalogCompare = parseInt(cartMeta.variant.compare_at_price, 10) || 0;
        if (cartCatalogCompare > unit) compareAt = cartCatalogCompare;
      }
    }
    var imagesEl = panelEl.querySelector('[data-ev-confirm-images]');
    var saveEl = panelEl.querySelector('[data-ev-confirm-save]');
    var saveAmtEl = panelEl.querySelector('[data-ev-confirm-save-amount]');
    var titleEl = panelEl.querySelector('[data-ev-confirm-title]');
    var qtyEl = panelEl.querySelector('[data-ev-confirm-qty]');
    var pricesEl = panelEl.querySelector('[data-ev-confirm-prices]');
    var priceEl = panelEl.querySelector('[data-ev-confirm-price]');
    var compareEl = panelEl.querySelector('[data-ev-confirm-compare]');
    var unitEl = panelEl.querySelector('[data-ev-confirm-unit]');
    var totalEl = panelEl.querySelector('[data-ev-confirm-total]');
    var messageEl = panelEl.querySelector('[data-ev-confirm-message]');

    panelEl.classList.toggle('ev-cart-drawer__confirm-offer--individual', isIndividual);
    drawer.classList.toggle('ev-cart-drawer--confirm-individual', isIndividual);

    if (imagesEl) {
      var imgHtml = '';
      var walletList = confirmContext.wallets && confirmContext.wallets.length
        ? confirmContext.wallets
        : null;
      var n = walletList
        ? Math.min(walletList.length, 3)
        : Math.max(1, Math.min(qty, 3));
      for (var i = 0; i < n; i++) {
        var slotImg = walletList ? (walletList[i].image || '') : image;
        var slotTitle = walletList
          ? shortProductTitle(walletList[i].productTitle || title)
          : title;
        if (slotImg) {
          imgHtml +=
            '<figure class="ev-cart-drawer__confirm-figure">' +
            '<img class="ev-cart-drawer__confirm-image" src="' + escapeHtml(slotImg) + '" alt="' + escapeHtml(slotTitle) + '" loading="lazy" width="480" height="480">' +
            '</figure>';
        } else {
          imgHtml += '<figure class="ev-cart-drawer__confirm-figure ev-cart-drawer__confirm-figure--empty" aria-hidden="true"></figure>';
        }
      }
      imagesEl.innerHTML = imgHtml;
      imagesEl.setAttribute('data-count', String(n));
      panelEl.setAttribute('data-ev-confirm-count', String(n));
      panelEl.classList.toggle('ev-cart-drawer__confirm-offer--count-2', n === 2);
      panelEl.classList.toggle('ev-cart-drawer__confirm-offer--count-3', n === 3);
    } else {
      panelEl.removeAttribute('data-ev-confirm-count');
      panelEl.classList.remove(
        'ev-cart-drawer__confirm-offer--count-2',
        'ev-cart-drawer__confirm-offer--count-3'
      );
    }

    if (titleEl) {
      if (walletList && walletList.length > 1 && (offerKey === 'gift' || offerKey === 'executive')) {
        titleEl.textContent = confirmContext.offerName || (offerKey === 'executive' ? 'Executive Set' : 'Gift Set');
      } else {
        titleEl.textContent = title;
      }
    }

    var finalTotal = cartTotal || confirmContext.displayedTotal || unit * qty;
    var regularSubtotal = parseInt(confirmContext.displayedRegular, 10) || unit * qty;

    if (unitEl) {
      unitEl.textContent = '';
      unitEl.hidden = true;
      unitEl.setAttribute('hidden', '');
    }

    if (isIndividual) {
      if (qtyEl) qtyEl.textContent = 'Individual · 1 Wallet';
      if (pricesEl && priceEl) {
        priceEl.textContent = formatMoney(unit || finalTotal);
        pricesEl.hidden = false;
        pricesEl.removeAttribute('hidden');
      }
      if (compareEl) {
        if (compareAt > unit && unit > 0) {
          compareEl.textContent = formatMoney(compareAt);
          compareEl.hidden = false;
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
          compareEl.setAttribute('hidden', '');
        }
      }
      if (totalEl) {
        totalEl.textContent = '';
        totalEl.hidden = true;
        totalEl.setAttribute('hidden', '');
      }
      if (messageEl) {
        messageEl.textContent = 'Your selected wallet is ready for checkout.';
        messageEl.hidden = false;
        messageEl.removeAttribute('hidden');
      }
      if (saveEl && saveAmtEl) {
        if (cartDiscount > 0) {
          saveEl.textContent = 'SAVE';
          saveEl.hidden = false;
          saveEl.removeAttribute('hidden');
          saveAmtEl.textContent = 'You save ' + formatMoney(cartDiscount);
          saveAmtEl.hidden = false;
          saveAmtEl.removeAttribute('hidden');
        } else {
          saveEl.textContent = '';
          saveEl.hidden = true;
          saveEl.setAttribute('hidden', '');
          saveAmtEl.textContent = '';
          saveAmtEl.hidden = true;
          saveAmtEl.setAttribute('hidden', '');
        }
      }
    } else {
      if (qtyEl) {
        if (walletList && walletList.length) {
          var nameBits = walletList.map(function (w) {
            var nm = shortProductTitle(w.productTitle || 'Wallet');
            if (w.variantTitle && w.variantTitle !== 'Default Title') {
              nm += ' (' + w.variantTitle + ')';
            }
            return nm;
          });
          qtyEl.textContent = nameBits.join(' · ');
        } else {
          qtyEl.textContent = qty + ' Wallets';
        }
      }
      if (pricesEl) {
        pricesEl.hidden = true;
        pricesEl.setAttribute('hidden', '');
      }
      if (compareEl) {
        compareEl.textContent = '';
        compareEl.hidden = true;
        compareEl.setAttribute('hidden', '');
      }
      if (messageEl) {
        messageEl.textContent = '';
        messageEl.hidden = true;
        messageEl.setAttribute('hidden', '');
      }
      if (unitEl && regularSubtotal > finalTotal) {
        unitEl.textContent = 'Original ' + formatMoney(regularSubtotal);
        unitEl.hidden = false;
        unitEl.removeAttribute('hidden');
      }
      if (totalEl) {
        totalEl.textContent = 'Final price ' + formatMoney(finalTotal);
        totalEl.hidden = false;
        totalEl.removeAttribute('hidden');
      }
      if (saveEl && saveAmtEl) {
        if (pct > 0 || cartDiscount > 0) {
          saveEl.textContent = 'SAVE ' + (pct || (qty === 2 ? giftPct() : executivePct())) + '%';
          saveEl.hidden = false;
          saveEl.removeAttribute('hidden');
          var saveCents = cartDiscount || Math.max(0, regularSubtotal - finalTotal);
          saveAmtEl.textContent = 'You save ' + formatMoney(saveCents);
          saveAmtEl.hidden = false;
          saveAmtEl.removeAttribute('hidden');
        } else {
          saveEl.textContent = '';
          saveEl.hidden = true;
          saveEl.setAttribute('hidden', '');
          saveAmtEl.textContent = '';
          saveAmtEl.hidden = true;
          saveAmtEl.setAttribute('hidden', '');
        }
      }
    }

    panelEl.hidden = false;
    panelEl.removeAttribute('hidden');
  }

  function updateProductsHeading() {
    var heading = drawer.querySelector('[data-ev-cart-drawer-products-heading]');
    if (!heading) return;
    if (isConfirmMode()) {
      heading.textContent = '';
      heading.hidden = true;
      heading.setAttribute('hidden', '');
      setProductsSubtext('');
      return;
    }
    if (isIndividualMode()) {
      if (hasIndividualSecondWallet()) {
        heading.textContent = 'Your Gift Set Wallets';
        setProductsSubtext('Your set is complete. Choose another wallet to change Wallet 2.');
      } else {
        heading.textContent = 'Choose Your Second Wallet';
        setProductsSubtext(
          'Select any wallet below to unlock an extra ' + giftPct() + '% savings.'
        );
      }
    } else if (mode === 'complete') {
      heading.textContent = 'Choose Your Second Wallet';
      setProductsSubtext('Mix styles or select another of the same wallet.');
    } else if (mode === 'gift') {
      if (walletSelections[2]) {
        heading.textContent = 'Your Gift Set Wallets';
        setProductsSubtext('Your set is complete. Choose another wallet to change Wallet 2.');
      } else {
        /* Step title lives in sticky header only — do not duplicate "Choose Wallet 2" */
        heading.textContent = '';
        setProductsSubtext('Choose any wallet below.');
      }
    } else if (mode === 'executive') {
      if (!walletSelections[2]) {
        heading.textContent = '';
        setProductsSubtext('Choose any wallet below.');
      } else if (!walletSelections[3]) {
        heading.textContent = '';
        setProductsSubtext('Choose any wallet below.');
      } else {
        heading.textContent = 'Your Executive Set Wallets';
        setProductsSubtext('Your set is complete. Choose another wallet to change your selection.');
      }
    } else {
      heading.textContent = 'Choose Wallet ' + activeSlot;
      setProductsSubtext('');
    }

    if (heading.textContent) {
      heading.hidden = false;
      heading.removeAttribute('hidden');
    } else {
      heading.hidden = true;
      heading.setAttribute('hidden', '');
    }
  }

  function getDrawerScrollContainers() {
    return {
      body: drawer.querySelector('[data-ev-cart-drawer-body]'),
      products: drawer.querySelector('[data-ev-cart-drawer-products]'),
      viewport: drawer.querySelector('[data-ev-cart-drawer-viewport]'),
      panel: drawer.querySelector('.ev-cart-drawer__panel')
    };
  }

  function resetDrawerScroll() {
    var nodes = getDrawerScrollContainers();
    if (nodes.body) nodes.body.scrollTop = 0;
    if (nodes.products) nodes.products.scrollTop = 0;
    if (nodes.viewport) {
      nodes.viewport.scrollTop = 0;
      nodes.viewport.scrollLeft = 0;
    }
    if (nodes.panel) nodes.panel.scrollTop = 0;
    var slotsViewport = getSlotsViewport();
    if (slotsViewport) slotsViewport.scrollLeft = 0;
  }

  function setSlotBadge(media, text) {
    if (!media) return;
    var badge = media.querySelector('.ev-drawer-card__slot-badge');
    var incart = media.querySelector('.ev-drawer-card__incart-badge');
    if (incart) incart.parentNode.removeChild(incart);

    if (!text) {
      if (badge) badge.parentNode.removeChild(badge);
      return;
    }

    var html =
      '<span class="ev-drawer-card__check" aria-hidden="true">✓</span> ' + escapeHtml(text);
    if (badge) {
      if (badge.getAttribute('data-badge-text') === text) return;
      badge.setAttribute('data-badge-text', text);
      badge.style.animation = 'none';
      badge.style.opacity = '1';
      badge.innerHTML = html;
      return;
    }

    badge = document.createElement('span');
    badge.className = 'ev-drawer-card__slot-badge';
    badge.setAttribute('data-badge-text', text);
    badge.style.animation = 'none';
    badge.style.opacity = '1';
    badge.innerHTML = html;
    media.appendChild(badge);
  }

  function syncPickButton(body, options) {
    if (!body) return;
    var btn = body.querySelector('[data-ev-drawer-pick]');
    var staticLabel = body.querySelector('.ev-drawer-card__incart-label');
    if (staticLabel && staticLabel.textContent === 'Sold Out') return;

    if (!btn) {
      if (staticLabel) staticLabel.parentNode.removeChild(staticLabel);
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ev-drawer-card__btn';
      btn.setAttribute('data-ev-drawer-pick', '');
      body.appendChild(btn);
    }

    btn.setAttribute('data-variant-id', String(options.variantId || ''));
    if (options.productId) btn.setAttribute('data-product-id', String(options.productId));
    var nextText = options.text || 'Select';
    if (btn.textContent !== nextText) btn.textContent = nextText;
    btn.disabled = !!options.disabled;
    if (options.pressed) btn.setAttribute('aria-pressed', 'true');
    else btn.removeAttribute('aria-pressed');
  }

  function desiredCardState(vid, productId, wallet1Id, wallet2Id, wallet3Id, busy) {
    var pendingVid = selectPending && selectPending.variantId ? selectPending.variantId : 0;
    if (pendingVid && vid === pendingVid) {
      return {
        kind: 'pending',
        badge: '',
        text: 'Adding…',
        pressed: false,
        disabled: true,
        variantId: vid,
        productId: productId
      };
    }

    var lockOthers = !!pendingVid || !!busy;

    if (isIndividualActiveFlow()) {
      if (wallet2Id && vid === wallet2Id) {
        return {
          kind: 'selected',
          badge: 'WALLET 2 SELECTED',
          text: 'Selected',
          pressed: true,
          disabled: lockOthers,
          variantId: vid,
          productId: productId
        };
      }
      if (wallet1Id && vid === wallet1Id) {
        return {
          kind: 'wallet1',
          badge: 'CURRENT WALLET',
          text: 'Select',
          pressed: false,
          disabled: lockOthers,
          variantId: vid,
          productId: productId
        };
      }
      return {
        kind: 'none',
        badge: '',
        text: 'Select',
        pressed: false,
        disabled: lockOthers,
        variantId: vid,
        productId: productId
      };
    }

    var label = '';
    if (wallet1Id && vid === wallet1Id) label = 'WALLET 1 SELECTED';
    else if (wallet2Id && vid === wallet2Id) label = 'WALLET 2 SELECTED';
    else if (wallet3Id && vid === wallet3Id) label = 'WALLET 3 SELECTED';

    if (label) {
      return {
        kind: 'selected',
        badge: label,
        text: 'Selected',
        pressed: true,
        disabled: lockOthers,
        variantId: vid,
        productId: productId
      };
    }

    return {
      kind: 'none',
      badge: '',
      text: 'Select',
      pressed: false,
      disabled: lockOthers,
      variantId: vid,
      productId: productId
    };
  }

  function applyCardState(card, state) {
    if (!card || !state) return;
    var key = [state.kind, state.badge || '', state.text || '', state.disabled ? '1' : '0'].join('|');
    if (card.getAttribute('data-ev-card-state') === key) return;
    card.setAttribute('data-ev-card-state', key);

    var media = card.querySelector('.ev-drawer-card__media');
    var body = card.querySelector('.ev-drawer-card__body');
    if (!media || !body) return;

    card.classList.toggle('is-selected', state.kind === 'selected');
    card.classList.toggle('is-wallet1', state.kind === 'wallet1');
    card.classList.toggle('is-pending', state.kind === 'pending');
    card.setAttribute(
      'aria-selected',
      state.kind === 'selected' || state.kind === 'wallet1' ? 'true' : 'false'
    );

    setSlotBadge(media, state.badge || '');
    syncPickButton(body, {
      variantId: state.variantId,
      productId: state.productId,
      text: state.text,
      pressed: state.pressed,
      disabled: state.disabled
    });
  }

  function updateWalletCardStates() {
    var track = drawer.querySelector('[data-ev-cart-drawer-track]');
    if (!track) return;
    var cards = track.querySelectorAll('.ev-drawer-card[data-variant-id]');
    if (!cards.length) {
      renderWalletCards({ rebuild: true });
      return;
    }

    var wallet1Id = variantIdOf(individualUpsell.wallet1 || walletSelections[1]) || 0;
    var wallet2Id = variantIdOf(walletSelections[2] || individualUpsell.wallet2) || 0;
    var wallet3Id = variantIdOf(walletSelections[3]) || 0;
    var busy = submitting || isIndividualSubmitting();

    cards.forEach(function (card) {
      if (card.classList.contains('is-sold-out')) return;
      var vid = parseInt(card.getAttribute('data-variant-id'), 10) || 0;
      var productId = card.getAttribute('data-product-id') || '';
      if (!vid) return;
      applyCardState(
        card,
        desiredCardState(vid, productId, wallet1Id, wallet2Id, wallet3Id, busy)
      );
    });

    track.classList.add('is-stable');
  }

  function renderWalletCards(options) {
    options = options || {};
    var track = drawer.querySelector('[data-ev-cart-drawer-track]');
    var productsWrap = drawer.querySelector('[data-ev-cart-drawer-products]');
    var viewport = drawer.querySelector('[data-ev-cart-drawer-viewport]');
    var bodyScroll = getPrimaryScrollEl();
    if (!track || !productsWrap) return;

    var savedScrollTop = bodyScroll ? (bodyScroll.scrollTop || 0) : (productsWrap.scrollTop || 0);
    var savedScrollLeft = viewport ? (viewport.scrollLeft || 0) : 0;
    var shouldResetScroll = !!options.resetScroll;

    if (!walletProducts.length) {
      ensureWalletProducts();
    }

    // Individual completed: hide entire selection UI
    if (isIndividualCompleted()) {
      productsWrap.hidden = true;
      productsWrap.setAttribute('hidden', '');
      track.innerHTML = '';
      track.classList.remove('is-stable');
      setProductsSubtext('');
      rebuildCarouselDots();
      return;
    }

    var discountApi = getDiscountApi();
    var offerState = discountApi
      ? discountApi.getOfferState(lastCart || { items: [] })
      : { state: 'none', walletUnits: 0 };

    if (mode === 'complete' && offerState.walletUnits >= 2) {
      productsWrap.hidden = true;
      productsWrap.setAttribute('hidden', '');
      track.innerHTML = '';
      track.classList.remove('is-stable');
      setProductsSubtext('');
      rebuildCarouselDots();
      return;
    }

    productsWrap.hidden = false;
    productsWrap.removeAttribute('hidden');
    updateProductsHeading();

    if (!options.rebuild && track.querySelector('.ev-drawer-card[data-variant-id]')) {
      updateWalletCardStates();
      if (shouldResetScroll) {
        resetDrawerScroll();
        window.requestAnimationFrame(function () {
          syncCarouselMetrics();
          centerFirstCarouselCard();
          rebuildCarouselDots();
        });
      } else {
        restoreProductsScroll(productsWrap, viewport, savedScrollTop, savedScrollLeft);
        syncCarouselDots();
        window.requestAnimationFrame(ensureSelectedCarouselCardVisible);
      }
      return;
    }

    if (!walletProducts.length) {
      track.classList.remove('is-stable');
      track.innerHTML =
        '<li class="ev-drawer-card ev-drawer-card--empty">' +
          'No eligible wallets available. Set Theme settings → Bundle Offers → Eligible wallet collection.' +
        '</li>';
      if (shouldResetScroll) resetDrawerScroll();
      else restoreProductsScroll(productsWrap, viewport, savedScrollTop, savedScrollLeft);
      rebuildCarouselDots();
      return;
    }

    var wallet1Id = variantIdOf(individualUpsell.wallet1 || walletSelections[1]) || 0;
    var wallet2Id = variantIdOf(walletSelections[2] || individualUpsell.wallet2) || 0;
    var showInCartBadges = individualUpsell.cartMutationCommitted === true;

    var busy = submitting || isIndividualSubmitting();
    var wallet3Id = variantIdOf(walletSelections[3]) || 0;
    var html = '';
    walletProducts.forEach(function (product, index) {
      var variant = pickDisplayVariant(product);
      if (!variant) return;

      var isSoldOut = variant.available === false;
      var vid = parseInt(variant.variant_id, 10);
      var labels = slotLabelsForVariant(vid);
      var isSelectedAsWallet2 = wallet2Id && vid === wallet2Id;
      var isWallet1Variant = wallet1Id && vid === wallet1Id;
      var isInCart = showInCartBadges && isWallet1Variant && isIndividualActiveFlow();

      if (mode === 'complete' && !isIndividualMode()) {
        isSelectedAsWallet2 = variantIdOf(walletSelections[2]) === vid;
      }

      var displayTitle = shortProductTitle(product.title);
      var imageSrc = variant.image || product.image;
      var imageHtml = imageSrc
        ? '<img class="ev-drawer-card__image" src="' + escapeHtml(imageSrc) + '" alt="' + escapeHtml(displayTitle) + '" loading="lazy" width="480" height="480">'
        : '<div class="ev-drawer-card__image ev-drawer-card__image--placeholder" aria-hidden="true"></div>';

      var galleryEnabled = mode === 'gift' || mode === 'executive';
      var mediaOpen = galleryEnabled
        ? '<button type="button" class="ev-drawer-card__media ev-drawer-card__media--gallery" data-ev-wallet-gallery data-product-id="' +
          product.id +
          '" aria-label="View images of ' + escapeHtml(displayTitle) + '">'
        : '<div class="ev-drawer-card__media">';
      var mediaClose = galleryEnabled ? '</button>' : '</div>';

      var badgeHtml = '';
      var btnHtml = '';
      var cardState = null;
      var ariaSelected = 'false';

      if (isSoldOut) {
        btnHtml = '<span class="ev-drawer-card__incart-label">Sold Out</span>';
      } else if (isIndividualActiveFlow()) {
        cardState = desiredCardState(vid, product.id, wallet1Id, wallet2Id, wallet3Id, busy);
        if (cardState.badge) {
          badgeHtml =
            '<span class="ev-drawer-card__slot-badge" data-badge-text="' + escapeHtml(cardState.badge) + '">' +
              '<span class="ev-drawer-card__check" aria-hidden="true">✓</span> ' + escapeHtml(cardState.badge) +
            '</span>';
        }
        btnHtml =
          '<button type="button" class="ev-drawer-card__btn" data-ev-drawer-pick' +
            ' data-variant-id="' + vid + '"' +
            ' data-product-id="' + product.id + '"' +
            (cardState.pressed ? ' aria-pressed="true"' : '') +
            (cardState.disabled ? ' disabled' : '') +
            '>' + escapeHtml(cardState.text) + '</button>';
        ariaSelected = cardState.kind === 'selected' || cardState.kind === 'wallet1' ? 'true' : 'false';
      } else if (isInCart) {
        badgeHtml =
          '<span class="ev-drawer-card__incart-badge">' +
            '<span class="ev-drawer-card__check" aria-hidden="true">✓</span> In Your Cart' +
          '</span>';
        btnHtml = '<span class="ev-drawer-card__incart-label">Already in Cart</span>';
      } else if (labels.length) {
        cardState = desiredCardState(vid, product.id, wallet1Id, wallet2Id, wallet3Id, busy);
        badgeHtml =
          '<span class="ev-drawer-card__slot-badge" data-badge-text="' + escapeHtml(labels[0]) + '">' +
            '<span class="ev-drawer-card__check" aria-hidden="true">✓</span> ' + escapeHtml(labels[0]) +
          '</span>';
        btnHtml =
          '<button type="button" class="ev-drawer-card__btn" data-ev-drawer-pick' +
            ' data-variant-id="' + vid + '"' +
            ' data-product-id="' + product.id + '"' +
            ' aria-pressed="true"' +
            (submitting ? ' disabled' : '') +
            '>Selected</button>';
        ariaSelected = 'true';
      } else {
        cardState = desiredCardState(vid, product.id, wallet1Id, wallet2Id, wallet3Id, busy);
        btnHtml =
          '<button type="button" class="ev-drawer-card__btn" data-ev-drawer-pick' +
            ' data-variant-id="' + vid + '"' +
            ' data-product-id="' + product.id + '"' +
            (busy ? ' disabled' : '') +
            '>Select</button>';
      }

      var stateAttr = cardState
        ? ' data-ev-card-state="' + escapeHtml([cardState.kind, cardState.badge || '', cardState.text || '', cardState.disabled ? '1' : '0'].join('|')) + '"'
        : '';

      html +=
        '<li class="ev-drawer-card' +
          (isSelectedAsWallet2 || (labels.length && !isIndividualActiveFlow()) ? ' is-selected' : '') +
          (isWallet1Variant && isIndividualActiveFlow() ? ' is-wallet1' : '') +
          (isSoldOut ? ' is-sold-out' : '') +
          '" data-variant-id="' + vid + '"' +
          ' data-product-id="' + product.id + '"' +
          stateAttr +
          ' aria-selected="' + ariaSelected + '"' +
          ' style="--ev-drawer-stagger:' + index + '">' +
          mediaOpen + imageHtml + badgeHtml + mediaClose +
          '<div class="ev-drawer-card__body">' +
            '<h4 class="ev-drawer-card__title">' + escapeHtml(displayTitle) + '</h4>' +
            moneyPairHtml(variant.price, variant.compare_at_price) +
            btnHtml +
          '</div>' +
        '</li>';
    });

    track.classList.remove('is-stable');
    track.innerHTML = html || '<li class="ev-drawer-card ev-drawer-card--empty">No eligible wallets available.</li>';

    if (shouldResetScroll) {
      resetDrawerScroll();
    } else {
      restoreProductsScroll(productsWrap, viewport, savedScrollTop, savedScrollLeft);
    }

    window.requestAnimationFrame(function () {
      track.classList.add('is-stable');
      syncCarouselMetrics();
      rebuildCarouselDots();
      if (shouldResetScroll) {
        resetDrawerScroll();
        centerFirstCarouselCard();
      } else {
        syncCarouselDots();
      }
    });
  }

  function restoreProductsScroll(productsWrap, viewport, scrollTop, scrollLeft) {
    var body = getPrimaryScrollEl();
    if (body) body.scrollTop = scrollTop;
    else if (productsWrap) productsWrap.scrollTop = scrollTop;
    if (viewport) viewport.scrollLeft = scrollLeft;
  }

  function isMobileCarouselViewport() {
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  }

  /** Gift/Executive + Individual upgrade flow: two square cards on mobile. */
  function isTwoUpCarousel() {
    if (!isMobileCarouselViewport()) return false;
    if (mode === 'gift' || mode === 'executive') return true;
    /* Individual upgrade + completed Individual Gift Set keep two-up product cards */
    if (isIndividualMode() && (
      isIndividualSelecting() ||
      isIndividualSubmitting() ||
      hasIndividualSecondWallet()
    )) return true;
    /* Class fallback if mode briefly out of sync during refresh */
    if (drawer && (
      drawer.classList.contains('ev-cart-drawer--gift') ||
      drawer.classList.contains('ev-cart-drawer--executive') ||
      drawer.classList.contains('ev-cart-drawer--individual-selecting') ||
      drawer.classList.contains('ev-cart-drawer--individual-gift')
    )) return true;
    return false;
  }

  function getCarouselCards() {
    var track = drawer && drawer.querySelector('[data-ev-cart-drawer-track]');
    if (!track) return [];
    return Array.prototype.slice.call(track.querySelectorAll('.ev-drawer-card[data-variant-id]'));
  }

  /**
   * Size mobile carousel cards from the actual drawer viewport width
   * (not 100vw), so gutters + peek stay accurate inside the panel.
   */
  function readCssLengthPx(value, fallback) {
    var raw = String(value || '').trim();
    if (!raw) return fallback;
    var n = parseFloat(raw);
    if (isNaN(n)) return fallback;
    if (raw.indexOf('rem') !== -1) {
      var root = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      return n * root;
    }
    return n;
  }

  /**
   * Measure sticky footer and expose CSS vars so scrollable body can clear it.
   * Grid layout: footer is a sibling track → small pad.
   * Overlay/sticky overlap: pad by full footer height + 20px.
   * Skip while drawer is closed / off-screen — measurements are stale until is-open.
   */
  function syncStickyFooterMetrics() {
    if (!drawer) return;
    if (!isOpen || !drawer.classList.contains('is-open')) return;
    if (drawer.classList.contains('is-hydrating')) return;
    var footer = drawer.querySelector('[data-ev-cart-drawer-footer]');
    var body = drawer.querySelector('[data-ev-cart-drawer-body]');
    var h = 0;
    var pad = 36;
    if (footer && !footer.hidden) {
      var cs = window.getComputedStyle(footer);
      if (cs.display !== 'none' && cs.visibility !== 'hidden') {
        h = Math.round(footer.getBoundingClientRect().height || footer.offsetHeight || 0);
        if (body && h > 0) {
          var fr = footer.getBoundingClientRect();
          var br = body.getBoundingClientRect();
          /* Footer top above body bottom ⇒ overlaps scrollport (sticky mobile) */
          if (fr.top < br.bottom - 2) {
            pad = h + 32;
          } else {
            /* Grid footer: keep Select buttons clear of the footer seam */
            pad = Math.max(52, Math.round(h * 0.35));
          }
        }
      }
    }
    drawer.style.setProperty('--ev-cart-sticky-footer-height', Math.max(h, 0) + 'px');
    drawer.style.setProperty('--ev-cart-body-scroll-pad', Math.max(pad, 36) + 'px');
  }

  function setDrawerHydrating(on) {
    if (!drawer) return;
    var next = !!on;
    drawer.classList.toggle('is-hydrating', next);
    drawer.setAttribute('aria-busy', next ? 'true' : 'false');
    var shell = drawer.querySelector('[data-ev-cart-drawer-hydrate]');
    if (shell) {
      if (next) {
        shell.hidden = false;
        shell.removeAttribute('hidden');
        shell.setAttribute('aria-hidden', 'false');
      } else {
        shell.hidden = true;
        shell.setAttribute('hidden', '');
        shell.setAttribute('aria-hidden', 'true');
      }
    }
  }

  function finishDrawerHydration(cart, options) {
    options = options || {};
    refreshUI(cart, {
      rebuildCards: options.rebuildCards !== false,
      resetScroll: options.resetScroll !== false
    });
    resetDrawerScroll();
    setDrawerHydrating(false);
    if (isOpen) {
      scheduleInitializeCartDrawerUI({
        centerProducts: true,
        centerSlots: mode === 'gift' || mode === 'executive' || hasIndividualSecondWallet()
      });
    }
  }

  /**
   * Single idempotent layout pipeline for mobile drawer chrome.
   * Safe to call many times — no duplicate listeners / carts / analytics.
   * Must run only after the drawer panel is visible (is-open).
   */
  function initializeCartDrawerUI(options) {
    options = options || {};
    if (!drawer || !isOpen || !drawer.classList.contains('is-open')) return;
    /* Skip metrics while the loading shell is up — layout is intentionally hidden */
    if (drawer.classList.contains('is-hydrating')) return;

    syncStickyCompact();
    syncStickyFooterMetrics();
    syncCarouselMetrics();
    rebuildCarouselDots();
    syncSlotsMetrics();
    rebuildSlotsDots();
    scheduleCarouselDotsSync();
    scheduleSlotsDotsSync();

    if (options.centerProducts) {
      centerFirstCarouselCard();
    }
    if (options.centerSlots) {
      var slot = options.centerSlot || lastFilledSlotsCenterSlot();
      centerSlotsBySlot(slot, options.slotsBehavior || 'auto');
    }
  }

  function cancelScheduledCartDrawerUI() {
    uiInitGeneration += 1;
    if (uiInitRaf1 !== null) {
      window.cancelAnimationFrame(uiInitRaf1);
      uiInitRaf1 = null;
    }
    if (uiInitRaf2 !== null) {
      window.cancelAnimationFrame(uiInitRaf2);
      uiInitRaf2 = null;
    }
  }

  /**
   * Double-rAF after paint, then optional image/font settle pass.
   * Replaces ad-hoc timeouts; cancels prior scheduled runs.
   */
  function scheduleInitializeCartDrawerUI(options) {
    options = options || {};
    if (!drawer) return;
    cancelScheduledCartDrawerUI();
    var gen = uiInitGeneration;
    var run = function () {
      if (gen !== uiInitGeneration || !isOpen) return;
      initializeCartDrawerUI(options);
    };

    uiInitRaf1 = window.requestAnimationFrame(function () {
      uiInitRaf1 = null;
      if (gen !== uiInitGeneration || !isOpen) return;
      uiInitRaf2 = window.requestAnimationFrame(function () {
        uiInitRaf2 = null;
        if (gen !== uiInitGeneration || !isOpen) return;
        if (!drawer.classList.contains('is-open')) return;
        run();
        whenDrawerLayoutAssetsReady(function () {
          if (gen !== uiInitGeneration || !isOpen) return;
          initializeCartDrawerUI(options);
        });
      });
    });
  }

  function whenDrawerLayoutAssetsReady(callback) {
    if (!drawer || typeof callback !== 'function') return;
    var pending = 0;
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      window.requestAnimationFrame(callback);
    };
    var onOne = function () {
      pending -= 1;
      if (pending <= 0) finish();
    };

    var imgs = drawer.querySelectorAll(
      '.ev-builder-pick__image[src], .ev-drawer-card__image[src]'
    );
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete && img.naturalWidth) continue;
      pending += 1;
      img.addEventListener('load', onOne, { once: true });
      img.addEventListener('error', onOne, { once: true });
    }

    if (document.fonts && document.fonts.status === 'loading' && document.fonts.ready) {
      pending += 1;
      document.fonts.ready.then(onOne).catch(onOne);
    }

    if (pending <= 0) {
      finish();
      return;
    }
    /* Safety: never block forever if a load event is missed */
    window.setTimeout(finish, 1200);
  }

  function bindDrawerLayoutObservers() {
    if (!drawer || uiInitViewportBound) return;
    uiInitViewportBound = true;
    var onViewportChange = function () {
      if (!isOpen) return;
      scheduleInitializeCartDrawerUI({});
    };
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewportChange);
    }
  }

  function syncCarouselMetrics() {
    var viewport = drawer && drawer.querySelector('[data-ev-cart-drawer-viewport]');
    if (!viewport) return;
    syncStickyFooterMetrics();
    if (!isMobileCarouselViewport()) {
      viewport.style.removeProperty('--ev-carousel-card');
      viewport.style.removeProperty('--ev-carousel-card-h');
      viewport.style.removeProperty('height');
      viewport.removeAttribute('data-ev-carousel');
      if (drawer) drawer.classList.remove('ev-cart-drawer--two-up');
      return;
    }

    var twoUp = isTwoUpCarousel();
    if (drawer) drawer.classList.toggle('ev-cart-drawer--two-up', twoUp);
    viewport.setAttribute('data-ev-carousel', twoUp ? 'two-up' : 'one-up');

    var styles = window.getComputedStyle(viewport);
    var gutter = readCssLengthPx(styles.getPropertyValue('--ev-carousel-gutter'), twoUp ? 12 : 17);
    var gap = readCssLengthPx(styles.getPropertyValue('--ev-carousel-gap'), twoUp ? 8 : 12);
    var width = viewport.clientWidth || 0;
    /* Drawer is full-bleed on mobile — prefer panel/client width, fall back to window */
    if (width < 200) {
      var panelEl = drawer.querySelector('.ev-cart-drawer__panel');
      width = (panelEl && panelEl.clientWidth) || window.innerWidth || 0;
    }
    if (width < 200) return;

    if (twoUp) {
      /* Exact two-card width + small safety so edges are not clipped while swiping */
      var card = Math.floor((width - gutter * 2 - gap - 10) / 2);
      card = Math.max(136, Math.min(card, Math.floor(width * 0.46)));
      viewport.style.setProperty('--ev-carousel-card', card + 'px');
      viewport.style.removeProperty('--ev-carousel-card-h');
      viewport.style.setProperty('height', 'auto');
      return;
    }

    var peek = readCssLengthPx(styles.getPropertyValue('--ev-carousel-peek'), 14);
    var oneCard = Math.round(width - gutter - peek);
    viewport.style.setProperty('--ev-carousel-card', Math.max(220, oneCard) + 'px');
    viewport.style.removeProperty('height');
    syncCarouselCardHeight(viewport);
  }

  /**
   * Fill unused mobile height with the product image while keeping
   * title / price / Select fixed and a compact dots→footer gap.
   */
  function syncCarouselCardHeight(viewport) {
    viewport = viewport || (drawer && drawer.querySelector('[data-ev-cart-drawer-viewport]'));
    var panelEl = drawer && drawer.querySelector('.ev-cart-drawer__panel');
    if (!viewport || !panelEl || !isMobileCarouselViewport()) return;
    /* Two-up Gift/Executive cards size to square media + body — no forced fill height */
    if (isTwoUpCarousel()) {
      viewport.style.removeProperty('--ev-carousel-card-h');
      return;
    }

    function visibleBlockHeight(el) {
      if (!el || el.hidden || el.getAttribute('hidden') !== null) return 0;
      var cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') return 0;
      return el.offsetHeight || 0;
    }

    var panelH = panelEl.clientHeight || 0;
    if (panelH < 320) return;

    var reserve = 20; /* target dots→footer gap (~16–24px) */
    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-sticky-head]'));
    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-progress]'));
    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-set-complete]'));
    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-applied]'));
    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-builder]'));
    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-products-heading]'));
    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-products-sub]'));

    var dots = drawer.querySelector('[data-ev-cart-drawer-dots]');
    if (dots && !dots.hidden) {
      reserve += Math.max(visibleBlockHeight(dots), 12) + 6;
    } else {
      reserve += 18; /* space reserved for dots once rendered */
    }

    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-error]'));
    reserve += visibleBlockHeight(drawer.querySelector('[data-ev-cart-drawer-footer]'));

    var products = drawer.querySelector('[data-ev-cart-drawer-products]');
    if (products) {
      var pcs = window.getComputedStyle(products);
      reserve += (parseFloat(pcs.paddingTop) || 0) + (parseFloat(pcs.paddingBottom) || 0);
    }

    var cardH = Math.round(panelH - reserve);
    var minH = 300;
    var maxH = Math.round(Math.min(panelH * 0.68, 560));
    if (cardH < minH) cardH = minH;
    if (cardH > maxH) cardH = maxH;
    viewport.style.setProperty('--ev-carousel-card-h', cardH + 'px');
  }

  function centerCarouselCard(index, behavior) {
    var viewport = drawer && drawer.querySelector('[data-ev-cart-drawer-viewport]');
    var cards = getCarouselCards();
    var card = cards[index];
    if (!viewport || !card) return;
    syncCarouselMetrics();
    if (isTwoUpCarousel()) {
      var styles = window.getComputedStyle(viewport);
      var gutter = readCssLengthPx(styles.getPropertyValue('--ev-carousel-gutter'), 17);
      var left = Math.max(0, card.offsetLeft - gutter);
      viewport.scrollTo({
        left: left,
        behavior: behavior || 'auto'
      });
      return;
    }
    var left = card.offsetLeft - (viewport.clientWidth - card.offsetWidth) / 2;
    viewport.scrollTo({
      left: Math.max(0, left),
      behavior: behavior || 'auto'
    });
  }

  function indexOfFirstSelectableCarouselCard() {
    var cards = getCarouselCards();
    if (!cards.length) return -1;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.classList.contains('is-sold-out')) continue;
      var btn = card.querySelector('[data-ev-drawer-pick]');
      if (btn && btn.disabled) continue;
      return i;
    }
    return 0;
  }

  function centerFirstCarouselCard() {
    if (!isMobileCarouselViewport()) return;
    syncCarouselMetrics();
    var index = indexOfFirstSelectableCarouselCard();
    if (index < 0) index = 0;
    centerCarouselCard(index, 'auto');
  }

  var carouselDotsRaf = null;
  function scheduleCarouselDotsSync() {
    if (carouselDotsRaf !== null) return;
    carouselDotsRaf = window.requestAnimationFrame(function () {
      carouselDotsRaf = null;
      syncCarouselDots();
    });
  }

  function syncCarouselDots() {
    var dotsNav = drawer && drawer.querySelector('[data-ev-cart-drawer-dots]');
    var viewport = drawer && drawer.querySelector('[data-ev-cart-drawer-viewport]');
    if (!dotsNav || !viewport || dotsNav.hidden) return;
    var cards = getCarouselCards();
    var buttons = dotsNav.querySelectorAll('[data-ev-carousel-dot]');
    if (!cards.length || !buttons.length) return;

    var twoUp = isTwoUpCarousel();
    var pageSize = twoUp ? 2 : 1;
    var nearest = 0;
    var best = Infinity;
    var gutter = twoUp
      ? readCssLengthPx(window.getComputedStyle(viewport).getPropertyValue('--ev-carousel-gutter'), 17)
      : 0;
    var viewLeft = viewport.scrollLeft;
    var viewCenter = viewLeft + viewport.clientWidth / 2;

    for (var i = 0; i < cards.length; i++) {
      var dist;
      if (twoUp) {
        dist = Math.abs(cards[i].offsetLeft - (viewLeft + gutter));
      } else {
        var cardCenter = cards[i].offsetLeft + cards[i].offsetWidth / 2;
        dist = Math.abs(cardCenter - viewCenter);
      }
      if (dist < best) {
        best = dist;
        nearest = i;
      }
    }

    var activePage = Math.floor(nearest / pageSize);
    for (var d = 0; d < buttons.length; d++) {
      var active = d === activePage;
      buttons[d].classList.toggle('is-active', active);
      if (active) buttons[d].setAttribute('aria-current', 'true');
      else buttons[d].removeAttribute('aria-current');
    }
  }

  function rebuildCarouselDots() {
    var dotsNav = drawer && drawer.querySelector('[data-ev-cart-drawer-dots]');
    if (!dotsNav) return;

    if (!isMobileCarouselViewport()) {
      dotsNav.hidden = true;
      dotsNav.setAttribute('hidden', '');
      dotsNav.innerHTML = '';
      return;
    }

    var cards = getCarouselCards();
    if (!cards.length) {
      dotsNav.hidden = true;
      dotsNav.setAttribute('hidden', '');
      dotsNav.innerHTML = '';
      return;
    }

    var twoUp = isTwoUpCarousel();
    var pageSize = twoUp ? 2 : 1;
    var pageCount = Math.ceil(cards.length / pageSize);

    /* Rebuild only when the page count changes — avoid wiping focus on every sync */
    var existing = dotsNav.querySelectorAll('[data-ev-carousel-dot]');
    if (existing.length === pageCount) {
      syncCarouselDots();
      return;
    }

    var html = '';
    for (var i = 0; i < pageCount; i++) {
      html +=
        '<button type="button" class="ev-cart-drawer__carousel-dot" data-ev-carousel-dot="' +
        i +
        '" aria-label="' +
        (twoUp ? 'View wallets page ' : 'View product ') +
        (i + 1) +
        '"></button>';
    }
    dotsNav.innerHTML = html;
    dotsNav.hidden = false;
    dotsNav.removeAttribute('hidden');
    syncCarouselDots();
    syncCarouselCardHeight();
  }

  function ensureSelectedCarouselCardVisible() {
    if (!isMobileCarouselViewport()) return;
    var viewport = drawer.querySelector('[data-ev-cart-drawer-viewport]');
    var track = drawer.querySelector('[data-ev-cart-drawer-track]');
    if (!viewport || !track) return;
    var selected = track.querySelector('.ev-drawer-card.is-selected');
    if (!selected) return;
    var viewLeft = viewport.scrollLeft;
    var viewRight = viewLeft + viewport.clientWidth;
    var cardLeft = selected.offsetLeft;
    var cardRight = cardLeft + selected.offsetWidth;
    if (cardLeft < viewLeft + 8 || cardRight > viewRight - 8) {
      var cards = getCarouselCards();
      var idx = cards.indexOf(selected);
      if (idx >= 0) centerCarouselCard(idx, 'smooth');
    }
  }

  function bindCarouselControls() {
    var viewport = drawer.querySelector('[data-ev-cart-drawer-viewport]');
    var dotsNav = drawer.querySelector('[data-ev-cart-drawer-dots]');
    if (viewport && viewport.dataset.evCarouselBound !== '1') {
      viewport.dataset.evCarouselBound = '1';
      viewport.addEventListener(
        'scroll',
        function () {
          scheduleCarouselDotsSync();
        },
        { passive: true }
      );
    }
    if (dotsNav && dotsNav.dataset.evDotsBound !== '1') {
      dotsNav.dataset.evDotsBound = '1';
      dotsNav.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-ev-carousel-dot]');
        if (!btn || !dotsNav.contains(btn)) return;
        var page = parseInt(btn.getAttribute('data-ev-carousel-dot'), 10);
        if (isNaN(page)) return;
        var pageSize = isTwoUpCarousel() ? 2 : 1;
        centerCarouselCard(page * pageSize, 'smooth');
      });
    }
    if (drawer.dataset.evCarouselResizeBound !== '1') {
      drawer.dataset.evCarouselResizeBound = '1';
      /* Resize handled by bindDrawerLayoutObservers → scheduleInitializeCartDrawerUI */
    }
  }

  function getPrimaryScrollEl() {
    return drawer && drawer.querySelector('[data-ev-cart-drawer-body]');
  }

  function getVerticalScrollTop() {
    var body = getPrimaryScrollEl();
    if (body) return body.scrollTop || 0;
    /* Fallback — legacy mobile panel scroll if body missing */
    if (panel) return panel.scrollTop || 0;
    return 0;
  }

  var pendingSlotsCenterSlot = null;
  var slotsDotsRaf = null;

  function getSlotsViewport() {
    return drawer && drawer.querySelector('[data-ev-cart-drawer-slots-viewport]');
  }

  function getSlotsTrack() {
    return drawer && drawer.querySelector('[data-ev-cart-drawer-slots]');
  }

  function getSlotCards() {
    var track = getSlotsTrack();
    if (!track) return [];
    return Array.prototype.slice.call(track.querySelectorAll('.ev-builder-pick[data-builder-slot]'));
  }

  /** Highest filled wallet slot — used for first-open centering (not the empty Selecting… slide). */
  function lastFilledSlotsCenterSlot() {
    if (walletSelections[3]) return 3;
    if (walletSelections[2]) return 2;
    if (walletSelections[1]) return 1;
    return activeSlot || 1;
  }

  function syncSlotsMetrics() {
    var viewport = getSlotsViewport();
    if (!viewport) return;
    if (!isMobileCarouselViewport()) {
      viewport.style.removeProperty('--ev-slot-card');
      return;
    }
    var styles = window.getComputedStyle(viewport);
    var gutter = readCssLengthPx(styles.getPropertyValue('--ev-slot-gutter'), 16);
    var peek = readCssLengthPx(styles.getPropertyValue('--ev-slot-peek'), 10);
    var width = viewport.clientWidth || 0;
    if (width < 200) return;
    /* Nearly full-width active card; ~8–12px intentional peek only */
    var card = Math.round(width - gutter - peek);
    viewport.style.setProperty('--ev-slot-card', Math.max(260, card) + 'px');
  }

  function centerSlotsCard(index, behavior) {
    var viewport = getSlotsViewport();
    var cards = getSlotCards();
    var card = cards[index];
    if (!viewport || !card || !isMobileCarouselViewport()) return;
    syncSlotsMetrics();
    var styles = window.getComputedStyle(viewport);
    var gutter = readCssLengthPx(styles.getPropertyValue('--ev-slot-gutter'), 16);
    /* Start-align so only a tiny right-edge peek remains */
    var left = Math.max(0, card.offsetLeft - gutter);
    var mode = behavior || 'smooth';
    /* iOS: scrollTo(behavior) + mandatory snap often lands on the wrong slide */
    if (mode === 'auto' || mode === 'instant') {
      viewport.scrollLeft = left;
      return;
    }
    try {
      viewport.scrollTo({ left: left, behavior: mode });
    } catch (err) {
      viewport.scrollLeft = left;
    }
  }

  function syncSlotsSingleState() {
    if (!drawer) return;
    var cards = getSlotCards();
    /* Single only when there is truly one slide — multi-slot Gift/Exec keep peek + dots. */
    if (cards.length <= 1) {
      drawer.setAttribute('data-ev-slots-single', 'true');
    } else {
      drawer.removeAttribute('data-ev-slots-single');
    }
  }

  function centerSlotsBySlot(slot, behavior) {
    var cards = getSlotCards();
    for (var i = 0; i < cards.length; i++) {
      if (parseInt(cards[i].getAttribute('data-builder-slot'), 10) === slot) {
        centerSlotsCard(i, behavior || 'smooth');
        return;
      }
    }
  }

  function syncSlotsDots() {
    var dotsNav = drawer && drawer.querySelector('[data-ev-cart-drawer-slots-dots]');
    var viewport = getSlotsViewport();
    if (!dotsNav || !viewport || dotsNav.hidden) return;
    var cards = getSlotCards();
    var buttons = dotsNav.querySelectorAll('[data-ev-slots-dot]');
    if (!cards.length || !buttons.length) return;

    var styles = window.getComputedStyle(viewport);
    var gutter = readCssLengthPx(styles.getPropertyValue('--ev-slot-gutter'), 16);
    var targetLeft = viewport.scrollLeft + gutter;
    var nearest = 0;
    var best = Infinity;
    for (var i = 0; i < cards.length; i++) {
      var dist = Math.abs(cards[i].offsetLeft - targetLeft);
      if (dist < best) {
        best = dist;
        nearest = i;
      }
    }

    for (var d = 0; d < buttons.length; d++) {
      var active = d === nearest;
      buttons[d].classList.toggle('is-active', active);
      if (active) buttons[d].setAttribute('aria-current', 'true');
      else buttons[d].removeAttribute('aria-current');
    }
  }

  function scheduleSlotsDotsSync() {
    if (slotsDotsRaf !== null) return;
    slotsDotsRaf = window.requestAnimationFrame(function () {
      slotsDotsRaf = null;
      syncSlotsDots();
    });
  }

  function rebuildSlotsDots() {
    var dotsNav = drawer && drawer.querySelector('[data-ev-cart-drawer-slots-dots]');
    var slotsViewport = getSlotsViewport();
    if (!dotsNav) return;

    var cards = getSlotCards();
    if (!isMobileCarouselViewport() || cards.length < 2) {
      dotsNav.hidden = true;
      dotsNav.setAttribute('hidden', '');
      dotsNav.innerHTML = '';
      if (slotsViewport) {
        slotsViewport.removeAttribute('aria-roledescription');
        slotsViewport.removeAttribute('aria-label');
      }
      return;
    }

    if (slotsViewport) {
      slotsViewport.setAttribute('aria-roledescription', 'carousel');
      slotsViewport.setAttribute('aria-label', 'Selected wallets');
    }

    var existing = dotsNav.querySelectorAll('[data-ev-slots-dot]');
    if (existing.length !== cards.length) {
      var html = '';
      for (var i = 0; i < cards.length; i++) {
        var slot = cards[i].getAttribute('data-builder-slot') || String(i + 1);
        html +=
          '<button type="button" class="ev-cart-drawer__slots-dot" data-ev-slots-dot="' +
          i +
          '" aria-label="View wallet ' +
          slot +
          '"></button>';
      }
      dotsNav.innerHTML = html;
    }

    dotsNav.hidden = false;
    dotsNav.removeAttribute('hidden');
    syncSlotsDots();
  }

  function refreshSlotsCarousel(centerSlot, behavior) {
    if (!isMobileCarouselViewport()) {
      syncSlotsSingleState();
      rebuildSlotsDots();
      return;
    }
    syncSlotsSingleState();
    var viewportNow = getSlotsViewport();
    var mode = behavior || 'smooth';
    if (viewportNow && (!centerSlot || centerSlot === 1 || mode === 'auto' || mode === 'instant')) {
      viewportNow.scrollLeft = 0;
    }
    window.requestAnimationFrame(function () {
      syncSlotsMetrics();
      syncSlotsSingleState();
      rebuildSlotsDots();
      var vp = getSlotsViewport();
      if (centerSlot) {
        centerSlotsBySlot(centerSlot, mode);
      } else if (vp) {
        vp.scrollLeft = 0;
      }
      window.requestAnimationFrame(function () {
        syncSlotsDots();
        /*
         * iOS snap can drift onto empty Selecting… after first paint.
         * Re-pin to the intended filled slot (or single-card zero).
         */
        if (vp && drawer && drawer.getAttribute('data-ev-slots-single') === 'true') {
          vp.scrollLeft = 0;
        } else if (vp && centerSlot && (mode === 'auto' || mode === 'instant')) {
          centerSlotsBySlot(centerSlot, 'auto');
        }
      });
      syncCarouselMetrics();
    });
  }

  function bindSlotsCarousel() {
    var viewport = getSlotsViewport();
    var dotsNav = drawer && drawer.querySelector('[data-ev-cart-drawer-slots-dots]');
    if (viewport && viewport.dataset.evSlotsBound !== '1') {
      viewport.dataset.evSlotsBound = '1';
      viewport.addEventListener(
        'scroll',
        function () {
          scheduleSlotsDotsSync();
        },
        { passive: true }
      );
    }
    if (dotsNav && dotsNav.dataset.evSlotsDotsBound !== '1') {
      dotsNav.dataset.evSlotsDotsBound = '1';
      dotsNav.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-ev-slots-dot]');
        if (!btn || !dotsNav.contains(btn)) return;
        var index = parseInt(btn.getAttribute('data-ev-slots-dot'), 10);
        if (isNaN(index)) return;
        centerSlotsCard(index, 'smooth');
      });
    }
    if (drawer.dataset.evSlotsResizeBound !== '1') {
      drawer.dataset.evSlotsResizeBound = '1';
      /* Resize handled by bindDrawerLayoutObservers → scheduleInitializeCartDrawerUI */
    }
  }

  function renderInCartChip() {
    if (isIndividualActiveFlow() || isIndividualCompleted()) {
      if (individualUpsell.wallet1) {
        walletSelections[1] = individualUpsell.wallet1;
      } else if (walletSelections[1]) {
        individualUpsell.wallet1 = walletSelections[1];
      }
      if (individualUpsell.wallet2) {
        walletSelections[2] = individualUpsell.wallet2;
      }
    }
  }

  function individualUpgradeCardHtml() {
    var pct = giftPct();
    var icon = function (paths) {
      return (
        '<span class="ev-individual-upgrade__perk-icon" aria-hidden="true">' +
          '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" focusable="false">' +
            paths +
          '</svg>' +
        '</span>'
      );
    };
    return (
      '<aside class="ev-individual-upgrade" aria-label="Upgrade to Gift Set">' +
        '<p class="ev-individual-upgrade__eyebrow">UPGRADE TO GIFT SET</p>' +
        '<div class="ev-individual-upgrade__headline-row">' +
          '<h3 class="ev-individual-upgrade__title">Add One More Wallet</h3>' +
          '<span class="ev-individual-upgrade__save-sep" aria-hidden="true"> — </span>' +
          '<span class="ev-individual-upgrade__save">Save Extra ' + pct + '%</span>' +
        '</div>' +
        '<p class="ev-individual-upgrade__blurb">Perfect for gifting, travel, work, or everyday rotation.</p>' +
        '<p class="ev-individual-upgrade__perfect">Perfect for:</p>' +
        '<ul class="ev-individual-upgrade__perks">' +
          '<li>' + icon('<path d="M4 10.5h12v6.25a1.25 1.25 0 0 1-1.25 1.25H5.25A1.25 1.25 0 0 1 4 16.75V10.5Z" stroke="currentColor" stroke-width="1.2"/><path d="M10 10.5V7.5M7.25 7.5h5.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M10 7.5c0-1.2 2-1.8 2 0M10 7.5c0-1.2-2-1.8-2 0" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>') + '<span>Gifting</span></li>' +
          '<li>' + icon('<path d="M3.5 12.5h9.5l3.5-3.25H17.5v5.25H15.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="7" cy="14.75" r="1.25" stroke="currentColor" stroke-width="1.2"/><circle cx="14.25" cy="14.75" r="1.25" stroke="currentColor" stroke-width="1.2"/><path d="M3.5 12.5V7.75h6.25" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>') + '<span>Travel</span></li>' +
          '<li>' + icon('<rect x="4.5" y="5.5" width="11" height="9.5" rx="1.25" stroke="currentColor" stroke-width="1.2"/><path d="M7.25 5.5V4.75a2.75 2.75 0 0 1 5.5 0V5.5" stroke="currentColor" stroke-width="1.2"/>') + '<span>Work &amp; personal</span></li>' +
          '<li>' + icon('<path d="M4.5 10a5.5 5.5 0 0 1 9.35-3.9M15.5 10a5.5 5.5 0 0 1-9.35 3.9" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M14.25 4.75V7.5h-2.75M5.75 15.25V12.5H8.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>') + '<span>Everyday rotation</span></li>' +
        '</ul>' +
      '</aside>'
    );
  }

  function individualHeroCardHtml(entry) {
    if (!entry) return '';
    entry = enrichSelectionCompareAt(entry);
    var title = shortProductTitle(entry.productTitle || 'Wallet');
    var img = entry.image
      ? '<img class="ev-builder-pick__image" src="' + escapeHtml(entry.image) + '" alt="" width="140" height="140" loading="lazy">'
      : '<div class="ev-builder-pick__image ev-builder-pick__image--placeholder" aria-hidden="true"></div>';
    var priceHtml = moneyPairHtml(entry.price || entry.originalLinePrice || 0, entry.compareAt);
    priceHtml = priceHtml
      .replace(/ev-drawer-card__price-row/g, 'ev-builder-pick__price-row')
      .replace(/ev-drawer-card__price-compare/g, 'ev-builder-pick__price-compare')
      .replace(/ev-drawer-card__price/g, 'ev-builder-pick__price');

    /* Premium Wallet 1 summary — pairs with right-side Gift Set upgrade card */
    return (
      '<article class="ev-builder-pick is-filled ev-builder-pick--individual-w1" data-builder-slot="1">' +
        '<div class="ev-builder-pick__meta">' +
          '<span class="ev-builder-pick__label">WALLET 1</span>' +
          '<span class="ev-builder-pick__check" aria-hidden="true">✓</span>' +
        '</div>' +
        '<div class="ev-builder-pick__body">' +
          img +
          '<div class="ev-builder-pick__copy">' +
            '<p class="ev-builder-pick__title">' + escapeHtml(title) + '</p>' +
            priceHtml +
            '<span class="ev-builder-pick__ready-badge">' +
              '<span aria-hidden="true">✓</span> Ready for Checkout' +
            '</span>' +
          '</div>' +
          '<div class="ev-builder-pick__action">' +
            '<span class="ev-builder-pick__replace ev-builder-pick__replace--spacer" aria-hidden="true"></span>' +
          '</div>' +
        '</div>' +
      '</article>'
    );
  }

  function summaryCardHtml(entry, positionLabel) {
    if (!entry) return '';
    entry = enrichSelectionCompareAt(entry);
    var title = shortProductTitle(entry.productTitle || 'Wallet');
    var variant = entry.variantTitle && entry.variantTitle !== 'Default Title'
      ? '<p class="ev-wallet-summary__variant">' + escapeHtml(entry.variantTitle) + '</p>'
      : '';
    var img = entry.image
      ? '<img class="ev-wallet-summary__image" src="' + escapeHtml(entry.image) + '" alt="" width="120" height="120" loading="lazy">'
      : '<div class="ev-wallet-summary__image ev-wallet-summary__image--placeholder" aria-hidden="true"></div>';
    var priceHtml = moneyPairHtml(entry.price || entry.originalLinePrice || 0, entry.compareAt);
    priceHtml = priceHtml
      .replace(/ev-drawer-card__price-row/g, 'ev-wallet-summary__price-row')
      .replace(/ev-drawer-card__price-compare/g, 'ev-wallet-summary__compare')
      .replace(/ev-drawer-card__price/g, 'ev-wallet-summary__price');

    return (
      '<article class="ev-wallet-summary">' +
        '<div class="ev-wallet-summary__top">' +
          '<span class="ev-wallet-summary__label">' + escapeHtml(positionLabel) + '</span>' +
          '<span class="ev-wallet-summary__check" aria-hidden="true">✓</span>' +
        '</div>' +
        '<div class="ev-wallet-summary__body">' +
          img +
          '<div class="ev-wallet-summary__copy">' +
            '<p class="ev-wallet-summary__title">' + escapeHtml(title) + '</p>' +
            variant +
            priceHtml +
            '<span class="ev-wallet-summary__selected-badge">Selected</span>' +
          '</div>' +
        '</div>' +
      '</article>'
    );
  }

  function renderCompletedSet(cart) {
    /* Confirmation summary removed — keep selection + sticky checkout CTA instead. */
    suppressConfirmationSummary();
  }

  function renderLockedWallet() {
    /* Wallet 1 now renders inside the selected strip — no locked placeholder. */
  }

  function builderEmptyPrompt(slot) {
    if (mode === 'gift' && slot === 2) return 'Choose your second wallet';
    if (mode === 'executive' && slot === 2) return 'Choose wallet 2';
    if (mode === 'executive' && slot === 3) {
      return walletSelections[2] ? 'Choose wallet 3' : 'Choose wallet 3 after wallet 2';
    }
    return 'Choose a wallet';
  }

  function builderEmptyHtml(slot, status) {
    var statusText = builderEmptyPrompt(slot);
    /* Same article structure as filled picks so mobile grid (image | label/copy) matches. */
    return (
      '<article class="ev-builder-pick ev-builder-pick--' + status + '" data-builder-slot="' + slot + '">' +
        '<div class="ev-builder-pick__meta">' +
          '<span class="ev-builder-pick__label">WALLET ' + slot + '</span>' +
        '</div>' +
        '<div class="ev-builder-pick__body">' +
          '<div class="ev-builder-pick__image ev-builder-pick__image--placeholder" aria-hidden="true"></div>' +
          '<div class="ev-builder-pick__copy">' +
            '<p class="ev-builder-pick__waiting">' + escapeHtml(statusText) + '</p>' +
          '</div>' +
          '<div class="ev-builder-pick__action">' +
            '<span class="ev-builder-pick__replace ev-builder-pick__replace--spacer" aria-hidden="true"></span>' +
          '</div>' +
        '</div>' +
      '</article>'
    );
  }

  function builderPickHtml(entry, slot) {
    if (!entry) {
      var status = activeSlot === slot ? 'selecting' : 'waiting';
      return builderEmptyHtml(slot, status);
    }
    entry = enrichSelectionCompareAt(entry);
    var title = shortProductTitle(entry.productTitle || 'Wallet');
    var img = entry.image
      ? '<img class="ev-builder-pick__image" src="' + escapeHtml(entry.image) + '" alt="" width="128" height="128" loading="lazy">'
      : '<div class="ev-builder-pick__image ev-builder-pick__image--placeholder" aria-hidden="true"></div>';
    /* Always reserve the action column so Wallet 1 matches Wallet 2/3 alignment */
    var replaceHtml = slot > 1
      ? '<button type="button" class="ev-builder-pick__replace" data-replace-slot="' + slot + '">Change</button>'
      : '<span class="ev-builder-pick__replace ev-builder-pick__replace--spacer" aria-hidden="true"></span>';
    var priceHtml = moneyPairHtml(entry.price || 0, entry.compareAt);
    priceHtml = priceHtml
      .replace(/ev-drawer-card__price-row/g, 'ev-builder-pick__price-row')
      .replace(/ev-drawer-card__price-compare/g, 'ev-builder-pick__price-compare')
      .replace(/ev-drawer-card__price/g, 'ev-builder-pick__price');

    return (
      '<article class="ev-builder-pick is-filled" data-builder-slot="' + slot + '">' +
        '<div class="ev-builder-pick__meta">' +
          '<span class="ev-builder-pick__label">WALLET ' + slot + '</span>' +
          '<span class="ev-builder-pick__check" aria-hidden="true">✓</span>' +
        '</div>' +
        '<div class="ev-builder-pick__body">' +
          img +
          '<div class="ev-builder-pick__copy">' +
            '<p class="ev-builder-pick__title">' + escapeHtml(title) + '</p>' +
            priceHtml +
          '</div>' +
          '<div class="ev-builder-pick__action">' + replaceHtml + '</div>' +
        '</div>' +
      '</article>'
    );
  }

  function renderBuilderSlots(options) {
    options = options || {};
    var slotsEl = drawer.querySelector('[data-ev-cart-drawer-slots]');
    var builder = drawer.querySelector('[data-ev-cart-drawer-builder]');
    var slotsViewport = getSlotsViewport();
    if (!slotsEl || !builder) return;

    var centerSlot = pendingSlotsCenterSlot;
    pendingSlotsCenterSlot = null;
    var preserveScroll = !!options.preserveScroll;
    var savedSlotsLeft = slotsViewport ? slotsViewport.scrollLeft || 0 : 0;

    var showIndividualBuilder = isIndividualActiveFlow();
    if (mode === 'complete' || isIndividualCompleted()) {
      if (!showIndividualBuilder) {
        builder.hidden = true;
        builder.setAttribute('hidden', '');
        slotsEl.innerHTML = '';
        rebuildSlotsDots();
        if (isMobileCarouselViewport()) {
          window.requestAnimationFrame(syncCarouselMetrics);
        }
        return;
      }
    }

    /* Individual initial state: Wallet 1 + premium Gift Set upgrade offer (two-column) */
    if (showIndividualBuilder && !hasIndividualSecondWallet()) {
      builder.hidden = false;
      builder.removeAttribute('hidden');
      if (walletSelections[1]) {
        individualUpsell.wallet1 = walletSelections[1];
      }
      var current = individualUpsell.wallet1 || walletSelections[1];
      if (current) {
        slotsEl.innerHTML =
          '<div class="ev-individual-upsell">' +
            '<div class="ev-individual-upsell__wallet">' +
              individualHeroCardHtml(current) +
            '</div>' +
            individualUpgradeCardHtml() +
          '</div>';
      } else {
        slotsEl.innerHTML = '';
      }
      if (slotsViewport) slotsViewport.scrollLeft = 0;
      rebuildSlotsDots();
      if (isMobileCarouselViewport()) {
        window.requestAnimationFrame(syncCarouselMetrics);
      }
      return;
    }

    /* Individual after second wallet: same horizontal summary carousel as Gift Set */
    if (showIndividualBuilder && hasIndividualSecondWallet()) {
      builder.hidden = false;
      builder.removeAttribute('hidden');
      if (walletSelections[1]) individualUpsell.wallet1 = walletSelections[1];
      if (walletSelections[2]) individualUpsell.wallet2 = walletSelections[2];
      slotsEl.innerHTML =
        builderPickHtml(walletSelections[1], 1) +
        builderPickHtml(walletSelections[2], 2);
      if (preserveScroll && slotsViewport) {
        slotsViewport.scrollLeft = savedSlotsLeft;
        rebuildSlotsDots();
      } else {
        refreshSlotsCarousel(centerSlot || 2);
      }
      return;
    }

    if (mode === 'gift' || mode === 'executive') {
      builder.hidden = false;
      builder.removeAttribute('hidden');

      var maxSlot = mode === 'executive' ? 3 : 2;
      var html = '';
      /*
       * Always render all set slots (filled + Selecting/Waiting).
       * Gift = 2 slides + dots; Executive = 3 — same arrangement, different count.
       * First-open centers on the last FILLED card (not empty Selecting…) via
       * lastFilledSlotsCenterSlot + direct scrollLeft on iOS.
       */
      for (var slot = 1; slot <= maxSlot; slot++) {
        html += builderPickHtml(walletSelections[slot], slot);
      }
      slotsEl.innerHTML = html;
      syncSlotsSingleState();
      var viewportEl = getSlotsViewport();
      if (preserveScroll && viewportEl) {
        viewportEl.scrollLeft = savedSlotsLeft;
        rebuildSlotsDots();
        window.requestAnimationFrame(function () {
          if (viewportEl) viewportEl.scrollLeft = savedSlotsLeft;
        });
      } else {
        if (viewportEl) viewportEl.scrollLeft = 0;
        var slotTarget = centerSlot || lastFilledSlotsCenterSlot();
        refreshSlotsCarousel(slotTarget, centerSlot ? 'smooth' : 'auto');
      }
      return;
    }

    builder.hidden = true;
    builder.setAttribute('hidden', '');
    slotsEl.innerHTML = '';
    rebuildSlotsDots();
    if (isMobileCarouselViewport()) {
      window.requestAnimationFrame(syncCarouselMetrics);
    }
  }

  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    var pageX = window.scrollX || window.pageXOffset || 0;
    var pageY = window.scrollY || window.pageYOffset || 0;
    var scrollEl = getPrimaryScrollEl();
    var catalogTop = scrollEl ? scrollEl.scrollTop : 0;
    try {
      el.focus({ preventScroll: true });
    } catch (err) {
      try { el.focus(); } catch (err2) { /* ignore */ }
    }
    /* Never leave a bare focus() scroll side-effect behind */
    if ((window.scrollX || window.pageXOffset || 0) !== pageX ||
        (window.scrollY || window.pageYOffset || 0) !== pageY) {
      window.scrollTo(pageX, pageY);
    }
    if (scrollEl && scrollEl.scrollTop !== catalogTop) {
      scrollEl.scrollTop = catalogTop;
    }
  }

  /**
   * Scroll only inside the drawer body — never document.scrollIntoView
   * (that scrolls the page behind and fights the body lock on mobile).
   */
  function smoothScrollToEl(el) {
    if (!el || !drawer) return;
    var scrollEl = getPrimaryScrollEl();
    if (!scrollEl || !scrollEl.contains(el)) return;

    var elTop = el.getBoundingClientRect().top;
    var boxTop = scrollEl.getBoundingClientRect().top;
    scrollEl.scrollTop = Math.max(0, scrollEl.scrollTop + (elTop - boxTop) - 12);
  }

  function scrollToBuilderSlot(slot) {
    window.requestAnimationFrame(function () {
      var el = drawer && drawer.querySelector('[data-builder-slot="' + slot + '"]');
      if (el) {
        smoothScrollToEl(el);
        return;
      }
      var products = drawer && drawer.querySelector('[data-ev-cart-drawer-products]');
      if (products) smoothScrollToEl(products);
    });
  }

  function showBuilderIncompleteError() {
    if (mode === 'executive') {
      if (!walletSelections[2]) {
        activeSlot = 2;
        showError('Select Wallet 2 to complete your Executive Set.');
        scrollToBuilderSlot(2);
        refreshUI({ items: [] }, { cards: 'states' });
        return;
      }
      if (!walletSelections[3]) {
        activeSlot = 3;
        showError('Select Wallet 3 to complete your Executive Set.');
        scrollToBuilderSlot(3);
        refreshUI({ items: [] }, { cards: 'states' });
        return;
      }
    }
    if (!walletSelections[2]) {
      activeSlot = 2;
      showError('Select Wallet 2 to complete your Gift Set.');
      scrollToBuilderSlot(2);
      refreshUI({ items: [] }, { cards: 'states' });
      return;
    }
    showError('Select all wallets before continuing.');
  }

  function scrollAfterBuilderSelection() {
    window.requestAnimationFrame(function () {
      if (mode === 'executive' && walletSelections[2] && !walletSelections[3]) {
        var slot3 = drawer.querySelector('[data-builder-slot="3"]');
        var products = drawer.querySelector('[data-ev-cart-drawer-products]');
        smoothScrollToEl(slot3 || products);
      }
    });
  }

  function renderProgress() {
    var el = drawer.querySelector('[data-ev-cart-drawer-progress]');
    if (!el) return;
    /* Selected wallets are the progress — hide the developer counter. */
    el.hidden = true;
    el.setAttribute('hidden', '');
    el.textContent = '';
  }

  /* ---------- Offer copy + footer ---------- */

  function updateOfferCopy(cart) {
    var offer = drawer.querySelector('[data-ev-cart-drawer-offer]');
    var applied = drawer.querySelector('[data-ev-cart-drawer-applied]');
    var offerLabel = drawer.querySelector('[data-ev-cart-drawer-offer-label]');
    var offerMessage = drawer.querySelector('[data-ev-cart-drawer-offer-message]');
    var offerSupport = drawer.querySelector('[data-ev-cart-drawer-offer-support]');
    var eyebrow = drawer.querySelector('[data-ev-cart-drawer-eyebrow]');
    var titleNode = drawer.querySelector('[data-ev-cart-drawer-product-name]');
    var confirm = drawer.querySelector('[data-ev-cart-drawer-success]');

    drawer.classList.remove(
      'ev-cart-drawer--unlock',
      'ev-cart-drawer--pending',
      'ev-cart-drawer--applied',
      'ev-cart-drawer--gift',
      'ev-cart-drawer--executive',
      'ev-cart-drawer--complete',
      'ev-cart-drawer--individual',
      'ev-cart-drawer--individual-gift',
      'ev-cart-drawer--individual-completed',
      'ev-cart-drawer--individual-selecting',
      'ev-cart-drawer--individual-submitting',
      'ev-cart-drawer--builder-compact',
      'ev-cart-drawer--confirm',
      'ev-cart-drawer--confirm-individual',
      'ev-cart-drawer--two-up'
    );
    drawer.removeAttribute('data-ev-gift-awaiting-wallet-2');
    drawer.removeAttribute('data-ev-executive-awaiting-wallet-2');
    drawer.removeAttribute('data-ev-executive-awaiting-wallet-3');
    drawer.removeAttribute('data-ev-individual-upgrade-state');
    drawer.removeAttribute('data-ev-gift-complete');
    drawer.removeAttribute('data-ev-executive-complete');
    drawer.removeAttribute('data-ev-slots-single');

    if (isConfirmMode()) {
      drawer.classList.add('ev-cart-drawer--confirm');
      if (confirm) {
        confirm.hidden = false;
        confirm.removeAttribute('hidden');
      }
      if (eyebrow) eyebrow.textContent = 'Added to Cart';
      if (titleNode) {
        titleNode.textContent = shortProductTitle((confirmContext && confirmContext.productName) || 'Your wallet');
      }
      if (offer) {
        offer.hidden = true;
        offer.setAttribute('hidden', '');
      }
      if (applied) {
        applied.hidden = true;
        applied.setAttribute('hidden', '');
      }
      var headerSavings = drawer.querySelector('[data-ev-cart-drawer-header-savings]');
      if (headerSavings) {
        headerSavings.hidden = true;
        headerSavings.setAttribute('hidden', '');
      }
      var confirmBadge = drawer.querySelector('[data-ev-cart-drawer-confirm-badge]');
      if (confirmBadge) {
        confirmBadge.hidden = true;
        confirmBadge.setAttribute('hidden', '');
      }
      suppressConfirmationSummary();
      renderConfirmPanel(cart);
      return;
    }

    hideConfirmPanel();

    if (mode === 'gift' || mode === 'executive') {
      drawer.classList.add(mode === 'gift' ? 'ev-cart-drawer--gift' : 'ev-cart-drawer--executive');
      drawer.classList.add('ev-cart-drawer--builder-compact');
      drawer.classList.toggle('ev-cart-drawer--two-up', isTwoUpCarousel());
      /* Incomplete / complete selection hooks */
      if (mode === 'gift' && !walletSelections[2]) {
        drawer.setAttribute('data-ev-gift-awaiting-wallet-2', 'true');
      } else if (mode === 'gift' && walletSelections[2]) {
        drawer.setAttribute('data-ev-gift-complete', 'true');
      } else if (mode === 'executive' && !walletSelections[2]) {
        drawer.setAttribute('data-ev-executive-awaiting-wallet-2', 'true');
      } else if (mode === 'executive' && walletSelections[2] && !walletSelections[3]) {
        drawer.setAttribute('data-ev-executive-awaiting-wallet-3', 'true');
      } else if (mode === 'executive' && walletSelections[3]) {
        drawer.setAttribute('data-ev-executive-complete', 'true');
      }
      if (confirm) {
        confirm.hidden = false;
        confirm.removeAttribute('hidden');
      }
      if (eyebrow) eyebrow.textContent = builderHeaderEyebrow();
      if (titleNode) titleNode.textContent = builderHeaderStatus();
      if (offer) {
        offer.hidden = true;
        offer.setAttribute('hidden', '');
      }
      if (applied) {
        applied.hidden = true;
        applied.setAttribute('hidden', '');
      }
      suppressConfirmationSummary();
      updatePremiumSavingsUI();
      return;
    }

    if (confirm) {
      confirm.hidden = false;
      confirm.removeAttribute('hidden');
    }

    // Individual pre-cart configurator
    if (isIndividualMode()) {
      drawer.classList.add('ev-cart-drawer--individual');
      drawer.classList.add('ev-cart-drawer--builder-compact');
      drawer.classList.toggle('ev-cart-drawer--individual-completed', isIndividualCompleted());
      drawer.classList.toggle('ev-cart-drawer--individual-selecting', isIndividualSelecting() || isIndividualSubmitting());
      drawer.classList.toggle('ev-cart-drawer--individual-submitting', isIndividualSubmitting());
      drawer.classList.toggle('ev-cart-drawer--individual-gift', hasIndividualSecondWallet());
      drawer.classList.toggle('ev-cart-drawer--two-up', isTwoUpCarousel());

      if (isIndividualSelecting() && !hasIndividualSecondWallet()) {
        drawer.setAttribute('data-ev-individual-upgrade-state', 'true');
      } else if (hasIndividualSecondWallet()) {
        drawer.setAttribute('data-ev-gift-complete', 'true');
      }

      if (eyebrow) {
        eyebrow.textContent = hasIndividualSecondWallet() ? 'YOUR GIFT SET' : 'YOUR WALLET';
      }
      if (titleNode) {
        titleNode.textContent = hasIndividualSecondWallet() ? 'Set Complete' : 'Added to Cart';
      }

      /* Upgrade offer lives in builder slots — hide legacy sticky offer strip */
      if (offer) {
        offer.hidden = true;
        offer.setAttribute('hidden', '');
      }
      if (applied) {
        applied.hidden = true;
        applied.setAttribute('hidden', '');
      }
      suppressConfirmationSummary();
      updatePremiumSavingsUI();
      return;
    }

    // Legacy complete mode (post-cart opens from other ATC paths)
    drawer.classList.add('ev-cart-drawer--complete');
    drawer.classList.toggle('ev-cart-drawer--individual-completed', mode === 'complete' && individualUpsell.status === 'completed');
    drawer.classList.toggle('ev-cart-drawer--individual-selecting', false);
    drawer.classList.toggle('ev-cart-drawer--individual-submitting', false);

    if (eyebrow) {
      eyebrow.textContent = individualUpsell.status === 'completed' ? 'Ready for Checkout' : 'Added to Cart';
    }
    if (offerLabel) offerLabel.textContent = 'ADD A SECOND WALLET';

    if (individualUpsell.status === 'completed') {
      drawer.classList.remove('ev-cart-drawer--unlock', 'ev-cart-drawer--applied', 'ev-cart-drawer--pending');
      if (offer) { offer.hidden = true; offer.setAttribute('hidden', ''); }
      suppressConfirmationSummary();
      updatePremiumSavingsUI();
      return;
    }

    drawer.classList.add('ev-cart-drawer--unlock');
    if (offer) { offer.hidden = false; offer.removeAttribute('hidden'); }
    if (applied) { applied.hidden = true; applied.setAttribute('hidden', ''); }
    suppressConfirmationSummary();
    updatePremiumSavingsUI();
  }

  function hideLegacyCheckoutLink() {
    var checkout = drawer.querySelector('[data-ev-cart-drawer-checkout]');
    if (!checkout) return;
    checkout.hidden = true;
    checkout.setAttribute('hidden', '');
    checkout.setAttribute('aria-hidden', 'true');
    if (checkout.parentNode) checkout.parentNode.removeChild(checkout);
  }

  var CHECKOUT_LOADING_LABEL = 'Preparing Secure Checkout…';
  var CHECKOUT_READY_LABEL = 'Proceed to Secure Checkout';
  var CHECKOUT_ERROR_MESSAGE = 'We couldn’t prepare checkout. Please try again.';

  function beginCheckoutLoading() {
    checkoutPreparing = true;
    submitting = true;
    showError('');
    var primary = drawer && drawer.querySelector('[data-ev-cart-drawer-primary]');
    if (!primary) return;
    primary.disabled = true;
    primary.setAttribute('aria-busy', 'true');
    primary.setAttribute('aria-disabled', 'true');
    primary.classList.add('is-loading');
    primary.innerHTML =
      '<span class="ev-cart-drawer__checkout-spinner" aria-hidden="true"></span>' +
      '<span class="ev-cart-drawer__checkout-label">' + CHECKOUT_LOADING_LABEL + '</span>';
    try {
      primary.focus({ preventScroll: true });
    } catch (e) {
      try { primary.focus(); } catch (e2) { /* ignore */ }
    }
  }

  function endCheckoutLoading() {
    checkoutPreparing = false;
    submitting = false;
    var primary = drawer && drawer.querySelector('[data-ev-cart-drawer-primary]');
    if (primary) {
      primary.classList.remove('is-loading');
      primary.removeAttribute('aria-busy');
    }
    updateFooter(lastCart);
  }

  function goToCheckout() {
    try {
      var url = '/checkout';
      /*
       * FOLLOW10: Shopify discount route sets a session cookie; ?discount= is a
       * reliable checkout handoff when the customer explicitly applied the code.
       */
      if (readSocialFlag(SOCIAL_LS.applied) === '1') {
        url = '/checkout?discount=' + encodeURIComponent(getSocialCode());
      }
      window.location.assign(url);
    } catch (err) {
      console.error('[EV Cart Drawer]', err);
      endCheckoutLoading();
      showError(CHECKOUT_ERROR_MESSAGE);
    }
  }

  /**
   * Fast checkout path: immediate loading UI → one /cart/add.js → /checkout.
   * Skips cart refresh, drawer rerender, and sync work that only delays navigation.
   */
  function commitItemsAndCheckout(items) {
    if (checkoutPreparing || submitting) return;
    if (!items || !items.length) return;

    beginCheckoutLoading();

    addItems(items)
      .then(function () {
        goToCheckout();
      })
      .catch(function (error) {
        console.error('[EV Cart Drawer]', error);
        endCheckoutLoading();
        showError(CHECKOUT_ERROR_MESSAGE);
      });
  }

  function calcIndividualPairSavings() {
    var w1 = individualUpsell.wallet1 || walletSelections[1];
    var w2 = individualUpsell.wallet2 || walletSelections[2];
    if (!w1 || !w2) return 0;
    var regular = (parseInt(w1.price, 10) || 0) + (parseInt(w2.price, 10) || 0);
    return Math.round(regular * (giftPct() / 100));
  }

  function continueWithOneWallet(btn) {
    if (!btn || btn.disabled || submitting || isIndividualSubmitting()) return;
    if (!isIndividualMode()) {
      goToCheckout();
      return;
    }
    commitIndividualSingle({ closeAfter: true });
  }

  function updateFooter(cart) {
    var primary = drawer.querySelector('[data-ev-cart-drawer-primary]');
    var secondary = drawer.querySelector('[data-ev-cart-drawer-secondary]');
    if (!primary) return;

    /* Keep preparing-checkout UI intact — do not rebuild the button mid-flight */
    if (checkoutPreparing) {
      primary.disabled = true;
      primary.setAttribute('aria-busy', 'true');
      primary.setAttribute('aria-disabled', 'true');
      primary.classList.add('is-loading');
      if (!primary.querySelector('.ev-cart-drawer__checkout-label')) {
        primary.innerHTML =
          '<span class="ev-cart-drawer__checkout-spinner" aria-hidden="true"></span>' +
          '<span class="ev-cart-drawer__checkout-label">' + CHECKOUT_LOADING_LABEL + '</span>';
      }
      primary.onclick = function (e) {
        e.preventDefault();
      };
      if (secondary) {
        secondary.disabled = true;
        secondary.setAttribute('aria-disabled', 'true');
        secondary.onclick = function (e) {
          e.preventDefault();
        };
      }
      return;
    }

    hideLegacyCheckoutLink();

    primary.onclick = null;
    primary.removeAttribute('href');
    primary.removeAttribute('aria-busy');
    primary.classList.remove('is-loading');
    if (secondary) {
      secondary.onclick = null;
      secondary.removeAttribute('aria-busy');
      secondary.removeAttribute('data-ev-cart-drawer-close');
    }

    var busy = submitting || isIndividualSubmitting();
    updatePremiumSavingsUI();

    if (isConfirmMode()) {
      hideTotalSavingsSummary();
      primary.hidden = false;
      primary.removeAttribute('hidden');
      primary.disabled = !!busy;
      primary.textContent = CHECKOUT_READY_LABEL;
      primary.setAttribute('aria-disabled', primary.disabled ? 'true' : 'false');
      primary.onclick = function (e) {
        e.preventDefault();
        if (primary.disabled || checkoutPreparing) return;
        beginCheckoutLoading();
        goToCheckout();
      };
      if (secondary) {
        secondary.hidden = false;
        secondary.removeAttribute('hidden');
        secondary.disabled = false;
        secondary.textContent = 'Continue Shopping';
        secondary.setAttribute('aria-disabled', 'false');
        secondary.onclick = function (e) {
          e.preventDefault();
          closeDrawer();
        };
      }
      return;
    }

    if (mode === 'gift' || mode === 'executive') {
      var sourceCart = cart || lastCart || getUsableCachedCart() || {};
      var totalBreakdown = updateTotalSavingsSummary(sourceCart);
      var payableCents = parseInt(sourceCart.total_price, 10) || 0;
      var moneySuffix = payableCents > 0 ? ' — ' + formatMoney(payableCents) : '';
      primary.hidden = false;
      primary.removeAttribute('hidden');

      if (builderComplete()) {
        primary.textContent =
          (totalBreakdown && totalBreakdown.finalTotal > 0
            ? CHECKOUT_READY_LABEL + ' — ' + formatMoney(totalBreakdown.finalTotal)
            : CHECKOUT_READY_LABEL + moneySuffix);
      } else if (
        mode === 'executive' &&
        walletSelections[1] &&
        walletSelections[2] &&
        !walletSelections[3]
      ) {
        var shopifyPartial = resolveActiveBundleShopifyDiscount(sourceCart);
        if (shopifyPartial && shopifyPartial.applied) {
          primary.textContent = 'Checkout Gift Set' + moneySuffix;
        } else {
          primary.textContent = 'Checkout Individual' + moneySuffix;
        }
      } else {
        primary.textContent = 'Checkout Individual' + moneySuffix;
      }

      primary.disabled = !!busy;
      primary.setAttribute('aria-disabled', primary.disabled ? 'true' : 'false');
      primary.onclick = function (e) {
        e.preventDefault();
        if (primary.disabled || checkoutPreparing || submitting) return;
        if (!builderComplete()) {
          /* Real cart already holds current lines — checkout as-is (no fake set completion) */
          beginCheckoutLoading();
          goToCheckout();
          return;
        }
        commitBuild({ goCheckout: true });
      };
      if (secondary) {
        secondary.hidden = false;
        secondary.removeAttribute('hidden');
        secondary.textContent = 'Continue Shopping';
        secondary.disabled = !!busy;
        secondary.setAttribute('aria-disabled', secondary.disabled ? 'true' : 'false');
        secondary.onclick = function (e) {
          e.preventDefault();
          if (secondary.disabled || checkoutPreparing || submitting) return;
          /* Selections are already real cart lines — close only. */
          closeDrawer();
        };
      }
      /* Footer height changes when TOTAL SAVINGS appears — remeasure pad, don't reserve blank */
      if (isOpen) syncStickyFooterMetrics();
      return;
    }

    // ——— Individual: mutually exclusive footers ———
    hideTotalSavingsSummary();

    if (isIndividualCompleted() || (mode === 'complete' && individualUpsell.status === 'completed')) {
      var donePayable = parseInt((cart || lastCart || {}).total_price, 10) || 0;
      primary.hidden = false;
      primary.removeAttribute('hidden');
      primary.disabled = false;
      primary.textContent = donePayable > 0
        ? CHECKOUT_READY_LABEL + ' — ' + formatMoney(donePayable)
        : CHECKOUT_READY_LABEL;
      primary.setAttribute('aria-disabled', 'false');
      primary.onclick = function (e) {
        e.preventDefault();
        if (checkoutPreparing) return;
        goToCheckout();
      };
      if (secondary) {
        secondary.hidden = false;
        secondary.removeAttribute('hidden');
        secondary.disabled = false;
        secondary.textContent = 'Continue Shopping';
        secondary.setAttribute('aria-disabled', 'false');
        secondary.onclick = function (e) {
          e.preventDefault();
          closeDrawer();
        };
      }
      return;
    }

    if (isIndividualSubmitting()) {
      primary.hidden = false;
      primary.removeAttribute('hidden');
      primary.textContent = CHECKOUT_LOADING_LABEL;
      primary.disabled = true;
      primary.setAttribute('aria-disabled', 'true');
      primary.setAttribute('aria-busy', 'true');
      primary.onclick = null;
      if (secondary) {
        secondary.hidden = false;
        secondary.removeAttribute('hidden');
        secondary.textContent = 'Continue Shopping';
        secondary.disabled = true;
        secondary.setAttribute('aria-disabled', 'true');
        secondary.onclick = null;
      }
      return;
    }

    // Selecting — checkout with Shopify payable (or Proceed when already upgraded)
    var hasWallet2 = hasIndividualSecondWallet();
    var sourceCart = cart || lastCart || getUsableCachedCart() || {};
    var payableCents = parseInt(sourceCart.total_price, 10) || 0;
    primary.hidden = false;
    primary.removeAttribute('hidden');
    if (hasWallet2) {
      primary.textContent = CHECKOUT_READY_LABEL;
    } else if (payableCents > 0) {
      primary.textContent = CHECKOUT_READY_LABEL + ' — ' + formatMoney(payableCents);
    } else {
      primary.textContent = CHECKOUT_READY_LABEL;
    }
    primary.disabled = !!busy || !walletSelections[1];
    primary.setAttribute('aria-disabled', primary.disabled ? 'true' : 'false');
    primary.onclick = function (e) {
      e.preventDefault();
      if (primary.disabled || checkoutPreparing || submitting) return;
      if (hasWallet2) {
        commitIndividualPair({ goCheckout: true });
      } else {
        commitIndividualSingle({ goCheckout: true });
      }
    };

    if (secondary) {
      secondary.hidden = false;
      secondary.removeAttribute('hidden');
      secondary.textContent = 'Continue Shopping';
      secondary.disabled = !!busy || !walletSelections[1];
      secondary.setAttribute('aria-disabled', secondary.disabled ? 'true' : 'false');
      secondary.onclick = function (e) {
        e.preventDefault();
        if (secondary.disabled || checkoutPreparing || submitting) return;
        if (hasWallet2) {
          commitIndividualPair({ closeAfter: true });
          return;
        }
        var vid = variantIdOf(walletSelections[1] || individualUpsell.wallet1);
        var alreadyInCart = !!(lastCart && vid && findStandaloneVariantLine(lastCart, vid));
        if (individualUpsell.cartMutationCommitted || alreadyInCart) {
          individualUpsell.cartMutationCommitted = true;
          closeDrawer();
          return;
        }
        commitIndividualSingle({ closeAfter: true });
      };
    }
  }

  function focusCompletedState() {
    var primary = drawer.querySelector('[data-ev-cart-drawer-primary]');
    if (primary) focusEl(primary);
  }

  function syncStickyCompact() {
    var nameEl = drawer.querySelector('[data-ev-cart-drawer-sticky-name]');
    var badgeEl = drawer.querySelector('[data-ev-cart-drawer-sticky-badge]');
    var titleEl = drawer.querySelector('[data-ev-cart-drawer-product-name]');
    var eyebrowEl = drawer.querySelector('[data-ev-cart-drawer-eyebrow]');
    var headerPct = drawer.querySelector('[data-ev-header-savings-pct]');
    var headerSavings = drawer.querySelector('[data-ev-cart-drawer-header-savings]');
    if (nameEl) {
      if (mode === 'gift' || mode === 'executive') {
        nameEl.textContent = (eyebrowEl && eyebrowEl.textContent) || (titleEl && titleEl.textContent) || 'Your Bundle';
      } else if (titleEl) {
        nameEl.textContent = titleEl.textContent || 'Your Wallet';
      }
    }
    if (badgeEl) {
      if (
        (mode === 'gift' || mode === 'executive') &&
        headerSavings &&
        !headerSavings.hidden &&
        headerPct &&
        headerPct.textContent
      ) {
        badgeEl.textContent = headerPct.textContent;
        badgeEl.hidden = false;
        badgeEl.removeAttribute('hidden');
      } else {
        badgeEl.textContent = '';
        badgeEl.hidden = true;
        badgeEl.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Elevate the pinned summary chrome while the product grid scrolls.
   * Keeps the full two-column summary visible — never swaps to the legacy compact strip.
   */
  function setStickyStuck(stuck) {
    var head = drawer.querySelector('[data-ev-cart-drawer-sticky-head]');
    if (!head) return;
    var next = !!stuck;
    if (head.classList.contains('is-stuck') === next) return;
    head.classList.toggle('is-stuck', next);
    head.classList.remove('is-collapsed');
  }

  function setStickyCollapsed(collapsed) {
    /* Legacy alias — collapse UI removed; map to elevated stuck state only. */
    setStickyStuck(collapsed);
  }

  /**
   * Sticky summary elevates after ~20px of vertical scroll inside the drawer body.
   */
  function onProductsScroll() {
    var body = getPrimaryScrollEl();
    if (!body) {
      setStickyStuck(false);
      return;
    }
    var top = getVerticalScrollTop();
    var head = drawer.querySelector('[data-ev-cart-drawer-sticky-head]');
    var stuck = head && head.classList.contains('is-stuck');
    if (stuck) {
      if (top < 8) setStickyStuck(false);
    } else if (top > 20) {
      setStickyStuck(true);
    }
  }

  function bindStickyHeader() {
    var body = getPrimaryScrollEl();
    if (body && body.dataset.evStickyBound !== '1') {
      body.dataset.evStickyBound = '1';
      body.addEventListener('scroll', onProductsScroll, { passive: true });
    }
  }

  function lockPageScroll() {
    if (scrollLocked) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    scrollLocked = true;
    document.documentElement.classList.add('ev-cart-drawer-open');
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + lockedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockPageScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    document.documentElement.classList.remove('ev-cart-drawer-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, lockedScrollY);
  }

  function refreshUI(cart, options) {
    options = options || {};
    lastCart = cart || lastCart || { items: [], item_count: 0, total_price: 0 };
    var quiet = !!options.quiet;
    var scrollSnap = quiet || options.preserveScroll ? captureDrawerScroll() : null;

    if (mode === 'complete' || (isIndividualMode() && individualUpsell.cartMutationCommitted)) {
      if (!isIndividualSubmitting()) {
        syncIndividualUpsellFromCart(lastCart);
      }
    }

    if (isConfirmMode()) {
      var builder = drawer.querySelector('[data-ev-cart-drawer-builder]');
      var products = drawer.querySelector('[data-ev-cart-drawer-products]');
      if (builder) {
        builder.hidden = true;
        builder.setAttribute('hidden', '');
      }
      if (products) {
        products.hidden = true;
        products.setAttribute('hidden', '');
      }
      renderProgress();
      updateOfferCopy(lastCart);
      renderConfirmPanel(lastCart);
      updateFooter(lastCart);
      syncStickyCompact();
      syncSocialRewardUI();
      if (options.resetScroll) resetDrawerScroll();
      if (isOpen) scheduleInitializeCartDrawerUI({});
      return;
    }

    hideConfirmPanel();
    renderProgress();
    renderInCartChip(lastCart);
    renderLockedWallet();
    renderBuilderSlots({ preserveScroll: quiet || !!options.preserveScroll });
    updateOfferCopy(lastCart);
    renderCompletedSet(lastCart);
    if (options.cards === 'states' || (quiet && !options.rebuildCards)) {
      updateProductsHeading();
      updateWalletCardStates();
    } else {
      renderWalletCards({
        rebuild: !!options.rebuildCards,
        resetScroll: !!options.resetScroll && !quiet
      });
    }
    updateFooter(lastCart);
    syncStickyCompact();
    onProductsScroll();
    syncSocialRewardUI();
    if (options.resetScroll && !quiet) {
      resetDrawerScroll();
    } else if (scrollSnap) {
      restoreDrawerScroll(scrollSnap);
    }
    /* Layout metrics — skip recentering on quiet Select/CHANGE updates */
    if (isOpen && !drawer.classList.contains('is-hydrating')) {
      if (quiet) {
        syncStickyFooterMetrics();
        if (scrollSnap) restoreDrawerScroll(scrollSnap);
      } else {
        scheduleInitializeCartDrawerUI({
          centerProducts: !!options.resetScroll,
          centerSlots: mode === 'gift' || mode === 'executive' || hasIndividualSecondWallet()
        });
      }
    }
  }

  function assignToActiveSlot(variantId) {
    if (isIndividualCompleted() || isIndividualSubmitting()) return;
    if (selectPending) return;
    var meta = findVariantMeta(variantId);
    var entry = selectionEntryFromMeta(meta, { variantId: variantId });
    if (!entry || !entry.variantId) return;

    /* Individual + Wallet 2 → real Gift Set after Shopify cart sync */
    if (isIndividualMode() || mode === 'complete') {
      if (submitting) return;
      beginSelectPending(entry.variantId, 2, null);
      showError('');
      transitionIndividualToGiftSet(entry)
        .then(function (cart) {
          endSelectPending();
          advanceActiveSlot();
          refreshUI(cart, { cards: 'states', quiet: true, preserveScroll: true });
        })
        .catch(function (error) {
          console.error('[EV Cart Drawer]', error);
          endSelectPending();
          walletSelections[2] = null;
          individualUpsell.wallet2 = null;
          mode = 'individual';
          individualUpsell.status = 'selecting';
          individualUpsell.cartMutationCommitted = true;
          showError(error.message || 'Could not upgrade to Gift Set.');
          refreshUI(lastCart, { cards: 'states', quiet: true, preserveScroll: true });
        });
      return;
    }

    /* Completed Gift + another wallet → real Executive Set after cart sync */
    if (mode === 'gift' && builderComplete()) {
      beginSelectPending(entry.variantId, 3, null);
      showError('');
      transitionGiftToExecutiveSet(entry)
        .then(function (cart) {
          endSelectPending();
          refreshUI(cart, { cards: 'states', quiet: true, preserveScroll: true });
        })
        .catch(function (error) {
          console.error('[EV Cart Drawer]', error);
          endSelectPending();
          showError(error.message || 'Could not upgrade to Executive Set.');
          refreshUI(lastCart, { cards: 'states', quiet: true, preserveScroll: true });
        });
      return;
    }

    var slot = activeSlot;
    if (slot !== 2 && slot !== 3) slot = 2;
    var previousEntry = walletSelections[slot] || null;
    beginSelectPending(entry.variantId, slot, previousEntry);
    showError('');
    /* Do not mutate walletSelections / advance slot until Shopify confirms */
    persistBuilderSlot(slot, entry)
      .then(function (cart) {
        walletSelections[slot] = entry;
        if (slot === 2) individualUpsell.wallet2 = entry;
        endSelectPending();
        advanceActiveSlot();
        refreshUI(cart, { cards: 'states', quiet: true, preserveScroll: true });
      })
      .catch(function (error) {
        console.error('[EV Cart Drawer]', error);
        walletSelections[slot] = previousEntry;
        if (slot === 2) individualUpsell.wallet2 = previousEntry;
        endSelectPending();
        showError(error.message || 'Could not update cart.');
        refreshUI(lastCart, { cards: 'states', quiet: true, preserveScroll: true });
      });
  }

  function commitIndividualSingle(options) {
    options = options || {};
    var w1 = enrichSelectionCompareAt(individualUpsell.wallet1 || walletSelections[1]);
    var vid = variantIdOf(w1);
    if (!vid || submitting || checkoutPreparing || isIndividualSubmitting()) return;

    individualUpsell.wallet1 = w1;
    walletSelections[1] = w1;

    /* Already stamped into cart as standalone — never add again. */
    if (individualUpsell.cartMutationCommitted) {
      if (options.goCheckout) {
        beginCheckoutLoading();
        goToCheckout();
        return;
      }
      if (options.closeAfter) {
        closeDrawer();
        return;
      }
      individualUpsell.status = 'completed';
      refreshUI(lastCart);
      focusCompletedState();
      return;
    }

    if (options.goCheckout) {
      commitItemsAndCheckout([{ id: vid, quantity: 1 }]);
      return;
    }

    showError('');
    individualUpsell.status = 'submitting_single';
    setBusy(true);
    updateFooter(lastCart);
    updateWalletCardStates();

    addItems([{ id: vid, quantity: 1 }])
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        individualUpsell.cartMutationCommitted = true;
        individualUpsell.status = 'completed';
        individualUpsell.wallet1 = enrichSelectionCompareAt(w1);
        individualUpsell.wallet2 = null;
        walletSelections[1] = individualUpsell.wallet1;
        walletSelections[2] = null;
        lastCart = cart;
        if (window.EV && typeof window.EV.syncCartState === 'function') {
          window.EV.syncCartState(cart);
        }
        if (options.closeAfter) {
          closeDrawer();
          return;
        }
        refreshUI(cart);
        focusCompletedState();
      })
      .catch(function (error) {
        console.error('[EV Cart Drawer]', error);
        individualUpsell.status = 'selecting';
        individualUpsell.cartMutationCommitted = false;
        showError(error.message || 'Could not add to cart.');
        refreshUI({ items: [] });
      })
      .finally(function () {
        setBusy(false);
      });
  }

  function commitIndividualPair(options) {
    options = options || {};
    var w1 = individualUpsell.wallet1 || walletSelections[1];
    var w2 = individualUpsell.wallet2 || walletSelections[2];
    var id1 = variantIdOf(w1);
    var id2 = variantIdOf(w2);
    if (!id1 || !id2 || submitting || checkoutPreparing || isIndividualSubmitting()) return;
    if (isIndividualCompleted()) return;

    function missingStandaloneItems(cart) {
      var items = [];
      if (!findStandaloneVariantLine(cart, id1)) {
        items.push({ id: id1, quantity: 1 });
      }
      if (id2 !== id1 && !findStandaloneVariantLine(cart, id2)) {
        items.push({ id: id2, quantity: 1 });
      }
      return items;
    }

    if (options.goCheckout) {
      beginCheckoutLoading();
      fetchCart()
        .then(function (cart) {
          var missing = missingStandaloneItems(cart);
          if (!missing.length) {
            goToCheckout();
            return null;
          }
          return addItems(missing).then(function () {
            goToCheckout();
          });
        })
        .catch(function (error) {
          console.error('[EV Cart Drawer]', error);
          endCheckoutLoading();
          showError(CHECKOUT_ERROR_MESSAGE);
        });
      return;
    }

    showError('');
    individualUpsell.status = 'submitting_pair';
    setBusy(true);
    updateFooter(lastCart);
    updateWalletCardStates();

    function finishPairSuccess(cart) {
      individualUpsell.cartMutationCommitted = true;
      individualUpsell.status = 'completed';
      individualUpsell.wallet1 = w1;
      individualUpsell.wallet2 = w2;
      walletSelections[1] = w1;
      walletSelections[2] = w2;
      if (window.EV && typeof window.EV.syncCartState === 'function') {
        window.EV.syncCartState(cart);
      }
      if (options.closeAfter) {
        closeDrawer();
        return;
      }
      refreshUI(cart);
      focusCompletedState();
    }

    function failPair(error) {
      console.error('[EV Cart Drawer]', error);
      individualUpsell.status = 'selecting';
      showError(error.message || 'Could not add wallets to cart.');
      refreshUI(lastCart || { items: [] });
    }

    fetchCart()
      .then(function (cart) {
        var missing = missingStandaloneItems(cart);
        if (!missing.length) {
          return cart;
        }
        return addItems(missing).then(function () { return fetchCart(); });
      })
      .then(finishPairSuccess)
      .catch(failPair)
      .finally(function () {
        setBusy(false);
      });
  }

  function buildConfirmContextFromSelections(cart, buildMode) {
    var offer = buildMode === 'executive' ? 'executive' : 'gift';
    var entries = [];
    if (walletSelections[1]) entries.push(walletSelections[1]);
    if (walletSelections[2]) entries.push(walletSelections[2]);
    if (offer === 'executive' && walletSelections[3]) entries.push(walletSelections[3]);

    var wallets = entries.map(function (entry) {
      entry = enrichSelectionCompareAt(entry);
      return {
        productTitle: entry.productTitle || '',
        variantTitle: entry.variantTitle || '',
        image: entry.image || '',
        price: parseInt(entry.price, 10) || 0,
        compareAt: parseInt(entry.compareAt, 10) || 0,
        variantId: entry.variantId
      };
    });

    var regular = wallets.reduce(function (sum, w) {
      return sum + (w.price || 0);
    }, 0);
    var pct = offer === 'executive' ? executivePct() : giftPct();
    var estimatedSave = Math.round(regular * (pct / 100));
    var cartDiscount = cart ? parseInt(cart.total_discount, 10) || 0 : 0;
    var cartTotal = cart ? parseInt(cart.total_price, 10) || 0 : 0;
    var first = wallets[0] || {};

    return {
      offer: offer,
      offerName: offer === 'executive' ? 'Executive Set' : 'Gift Set',
      quantity: wallets.length,
      unitPrice: first.price || 0,
      compareAt: first.compareAt || 0,
      image: first.image || '',
      productName: first.productTitle || (offer === 'executive' ? 'Executive Set' : 'Gift Set'),
      variantId: first.variantId || null,
      discountPercent: pct,
      displayedRegular: regular,
      displayedTotal: cartTotal || Math.max(0, regular - (cartDiscount || estimatedSave)),
      savingsCents: cartDiscount || estimatedSave,
      wallets: wallets
    };
  }

  function commitBuild(options) {
    options = options || {};
    if (submitting || checkoutPreparing || !walletSelections[1]) return;
    if (!builderComplete()) {
      showBuilderIncompleteError();
      return;
    }

    /* Selections are already persisted as cart lines — never re-add on checkout. */
    if (options.goCheckout) {
      if (checkoutPreparing || submitting) return;
      beginCheckoutLoading();
      setBusy(true);
      ensureBuilderSelectionsInCart()
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('ev:bundle:commit', {
            detail: {
              offer: mode === 'executive' ? 'executive' : 'gift',
              offerName: mode === 'executive' ? 'Executive Set' : 'Gift Set',
              quantity: builderFilledEntries().length,
              totalPrice: cart && cart.total_price,
              totalDiscount: cart && cart.total_discount,
              persisted: true
            }
          }));
          goToCheckout();
        })
        .catch(function (error) {
          console.error('[EV Cart Drawer]', error);
          endCheckoutLoading();
          showError(CHECKOUT_ERROR_MESSAGE);
        })
        .finally(function () {
          setBusy(false);
        });
      return;
    }

    closeDrawer();
  }

  /**
   * Open drawer chrome immediately (animation) without waiting on network.
   */
  function ensureDrawerChromeOpen(opts) {
    opts = opts || {};
    if (isOpen) {
      if (!drawer.classList.contains('is-hydrating') && opts.centerAfter !== false) {
        scheduleInitializeCartDrawerUI({
          centerProducts: !!opts.centerProducts,
          centerSlots: !!opts.centerSlots
        });
      }
      return;
    }

    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    if (openRaf !== null) {
      window.cancelAnimationFrame(openRaf);
      openRaf = null;
    }

    lastFocus = document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    lockPageScroll();
    isOpen = true;
    drawerGeneration += 1;
    var openGen = drawerGeneration;
    resetDrawerScroll();
    openRaf = window.requestAnimationFrame(function () {
      openRaf = null;
      if (!isOpen || openGen !== drawerGeneration) return;
      drawer.classList.add('is-open');
      setStickyCollapsed(false);
      syncStickyCompact();
      resetDrawerScroll();
      if (!drawer.classList.contains('is-hydrating') && opts.centerAfter !== false) {
        scheduleInitializeCartDrawerUI({
          centerProducts: opts.centerProducts !== false,
          centerSlots: !!opts.centerSlots
        });
      }
      window.requestAnimationFrame(function () {
        if (!isOpen || openGen !== drawerGeneration) return;
        resetDrawerScroll();
        focusEl(getPrimaryScrollEl() || panel);
      });
    });
  }

  /**
   * Background cart reconcile after instant local paint.
   * Updates savings/persistence quietly — never blocks first paint.
   */
  function reconcileDrawerCartInBackground(options) {
    options = options || {};
    var openGen = drawerGeneration;
    var requestedMode = mode;

    enqueueBuilderPersist(function () {
      return fetchCart()
        .then(function (cart) {
          if (openGen !== drawerGeneration || !isOpen) return cart;

          if (requestedMode === 'individual') {
            var resolved = resolveDrawerModeFromCart(cart, { allowIncompleteBuilder: false });
            if (resolved.mode === 'gift' || resolved.mode === 'executive') {
              mode = resolved.mode;
              resetIndividualUpsell();
              if (resolved.bundleId) {
                activeBundleId = resolved.bundleId;
                hydrateSelectionsFromBundle(cart, resolved.bundleId);
                return syncThemeCartState(cart);
              }
              activeBundleId = createBuilderBundleId();
              walletSelections = { 1: null, 2: null, 3: null };
              if (resolved.wallets && resolved.wallets[0]) walletSelections[1] = resolved.wallets[0];
              if (resolved.wallets && resolved.wallets[1]) walletSelections[2] = resolved.wallets[1];
              if (resolved.mode === 'executive' && resolved.wallets && resolved.wallets[2]) {
                walletSelections[3] = resolved.wallets[2];
              }
              if (!walletSelections[1] && options.buildContext) {
                resetSelections(options.buildContext);
              }
              var ids = [];
              if (variantIdOf(walletSelections[1])) ids.push(variantIdOf(walletSelections[1]));
              if (variantIdOf(walletSelections[2])) ids.push(variantIdOf(walletSelections[2]));
              if (variantIdOf(walletSelections[3])) ids.push(variantIdOf(walletSelections[3]));
              return removeStandaloneVariantLines(ids, cart)
                .then(function (nextCart) {
                  return persistBuilderSlotUnlocked(1, walletSelections[1], nextCart);
                })
                .then(function (nextCart) {
                  if (!walletSelections[2]) return nextCart;
                  return persistBuilderSlotUnlocked(2, walletSelections[2], nextCart);
                })
                .then(function (nextCart) {
                  if (mode !== 'executive' || !walletSelections[3]) return nextCart;
                  return persistBuilderSlotUnlocked(3, walletSelections[3], nextCart);
                })
                .then(function (nextCart) {
                  hydrateSelectionsFromBundle(nextCart, activeBundleId);
                  return syncThemeCartState(nextCart);
                });
            }

            /*
             * Stale incomplete gift/exec tags (e.g. Gift→1 after remove while closed):
             * promote/normalize before Individual bootstrap to avoid duplicate add.
             */
            var staleGift = findLargestBundleIdForOffer(cart, 'gift');
            var staleExec = findLargestBundleIdForOffer(cart, 'executive');
            if (
              (staleGift && staleGift.count > 0 && staleGift.count < 2) ||
              (staleExec && staleExec.count > 0 && staleExec.count < 3)
            ) {
              return syncDrawerModeAfterCartMutation(cart);
            }

            individualUpsell.status = 'selecting';
            individualUpsell.cartMutationCommitted = false;
            individualUpsell.wallet1 = walletSelections[1];
            individualUpsell.wallet2 = null;
            return bootstrapIndividualCart(cart, options.buildContext || null);
          }

          if (requestedMode === 'gift' || requestedMode === 'executive') {
            /*
             * Intentional incomplete builder (PDP clicked Gift/Exec) may stay incomplete.
             * Reopen / cart-persisted reconcile uses strict count → downgrade when needed.
             */
            var intentionalBuilder = !!options.buildContext;
            var resolvedB = resolveDrawerModeFromCart(cart, {
              allowIncompleteBuilder: intentionalBuilder
            });
            var walletUnits = countEligibleWalletUnits(cart);

            if (!intentionalBuilder) {
              if (
                (requestedMode === 'gift' && walletUnits < 2) ||
                (requestedMode === 'executive' && walletUnits < 3)
              ) {
                return syncDrawerModeAfterCartMutation(cart);
              }
            }

            if (resolvedB.mode === 'executive' && resolvedB.bundleId) {
              mode = 'executive';
              activeBundleId = resolvedB.bundleId;
              hydrateSelectionsFromBundle(cart, resolvedB.bundleId);
              return syncThemeCartState(cart);
            }
            if (resolvedB.mode === 'gift' && resolvedB.bundleId) {
              /* Completed gift in cart while opening Exec builder → keep intentional Exec bootstrap */
              if (intentionalBuilder && requestedMode === 'executive') {
                return bootstrapBuilderCart(cart, options.buildContext || null);
              }
              mode = 'gift';
              activeBundleId = resolvedB.bundleId;
              hydrateSelectionsFromBundle(cart, resolvedB.bundleId);
              return syncThemeCartState(cart);
            }
            return bootstrapBuilderCart(cart, options.buildContext || null);
          }

          return cart;
        })
        .then(function (cart) {
          if (openGen !== drawerGeneration || !isOpen) return;
          setDrawerHydrating(false);
          var track = drawer.querySelector('[data-ev-cart-drawer-track]');
          var hasCards = !!(track && track.querySelector('.ev-drawer-card[data-variant-id]'));
          refreshUI(cart, {
            cards: hasCards ? 'states' : undefined,
            rebuildCards: !hasCards,
            quiet: hasCards,
            preserveScroll: hasCards,
            resetScroll: !hasCards
          });
          if (!hasCards) {
            scheduleInitializeCartDrawerUI({
              centerProducts: true,
              centerSlots: mode === 'gift' || mode === 'executive' || hasIndividualSecondWallet()
            });
          }
        })
        .catch(function (error) {
          console.error('[EV Cart Drawer]', error);
          if (openGen !== drawerGeneration || !isOpen) return;
          setDrawerHydrating(false);
          showError(error.message || 'Could not sync cart.');
          var cachedErr = getUsableCachedCart() || {
            items: [],
            item_count: 0,
            total_price: 0,
            items_subtotal_price: 0
          };
          refreshUI(cachedErr, { cards: 'states', quiet: true, preserveScroll: true });
        });
    });
  }

  function paintDrawerFromLocalState(cartHint, options) {
    options = options || {};
    hideConfirmPanel();
    var paintCart = cartHint || getUsableCachedCart() || {
      items: [],
      item_count: 0,
      total_price: 0,
      items_subtotal_price: 0
    };
    refreshUI(paintCart, {
      rebuildCards: options.rebuildCards !== false,
      resetScroll: options.resetScroll !== false
    });
    setDrawerHydrating(false);
  }

  function openDrawer(options) {
    if (!drawer || !panel) return;
    options = options || {};
    var requestedMode = options.mode || 'individual';
    if (requestedMode === 'complete' && options.buildContext) {
      requestedMode = 'individual';
    }
    var buildContext = options.buildContext || null;

    mode = requestedMode;
    confirmContext = mode === 'confirm' ? (options.confirm || null) : null;

    /* Seed known PDP context immediately — do not wait for Shopify. */
    resetSelections(buildContext);
    resetIndividualUpsell();
    if (mode === 'individual') {
      individualUpsell.status = 'selecting';
      individualUpsell.cartMutationCommitted = false;
      individualUpsell.wallet1 = walletSelections[1];
      individualUpsell.wallet2 = null;
    }
    activeSlot = 2;
    showError('');
    ensureWalletProducts();

    var cached = getUsableCachedCart();
    /*
     * Reopen (no buildContext): upgrade Individual → Gift/Exec from cached cart.
     * PDP open WITH buildContext must NOT adopt stale gift/exec from lastCart —
     * that caused false Gift/Exec paint after /cart/clear.js until reconcile ran.
     */
    if (!buildContext && cached && cached.item_count > 0) {
      var resolvedCached = resolveDrawerModeFromCart(cached, {
        allowIncompleteBuilder: mode === 'gift' || mode === 'executive'
      });
      if (mode === 'individual' && (resolvedCached.mode === 'gift' || resolvedCached.mode === 'executive')) {
        mode = resolvedCached.mode;
        resetIndividualUpsell();
        if (resolvedCached.bundleId) {
          activeBundleId = resolvedCached.bundleId;
          hydrateSelectionsFromBundle(cached, resolvedCached.bundleId);
        } else if (resolvedCached.wallets && resolvedCached.wallets.length) {
          walletSelections = { 1: null, 2: null, 3: null };
          if (resolvedCached.wallets[0]) walletSelections[1] = resolvedCached.wallets[0];
          if (resolvedCached.wallets[1]) walletSelections[2] = resolvedCached.wallets[1];
          if (resolvedCached.mode === 'executive' && resolvedCached.wallets[2]) {
            walletSelections[3] = resolvedCached.wallets[2];
          }
        }
      } else if ((mode === 'gift' || mode === 'executive') && resolvedCached.bundleId) {
        activeBundleId = resolvedCached.bundleId;
        hydrateSelectionsFromBundle(cached, resolvedCached.bundleId);
      }
    } else if ((mode === 'gift' || mode === 'executive') && cached && cached.item_count > 0) {
      var resolvedBuilderOpen = resolveDrawerModeFromCart(cached, { allowIncompleteBuilder: true });
      if (resolvedBuilderOpen.bundleId) {
        activeBundleId = resolvedBuilderOpen.bundleId;
        hydrateSelectionsFromBundle(cached, resolvedBuilderOpen.bundleId);
      }
    }

    var canInstantPaint =
      !isConfirmMode() &&
      (mode === 'individual' || mode === 'gift' || mode === 'executive') &&
      (!!buildContext || !!cached);

    /* Full hydrate shell only when there is no usable local state to paint. */
    if (!isConfirmMode() && !canInstantPaint) {
      setDrawerHydrating(true);
    } else {
      setDrawerHydrating(false);
    }

    /* Shell + animation must start immediately — never wait on /cart.js. */
    ensureDrawerChromeOpen({
      centerAfter: canInstantPaint,
      centerProducts: true,
      centerSlots: mode === 'gift' || mode === 'executive' || hasIndividualSecondWallet()
    });

    if (isConfirmMode()) {
      setDrawerHydrating(false);
      fetchCart()
        .then(function (cart) {
          if (options.productName && confirmContext && !confirmContext.productName) {
            confirmContext.productName = options.productName;
          }
          refreshUI(cart, { rebuildCards: false, resetScroll: true });
          resetDrawerScroll();
          if (window.EV && typeof window.EV.syncCartState === 'function' && cart.item_count) {
            window.EV.syncCartState(cart);
          }
        })
        .catch(function () {
          refreshUI({ items: [], item_count: 0, total_price: 0 }, { rebuildCards: false, resetScroll: true });
          resetDrawerScroll();
        });
      return;
    }

    if (mode === 'individual' || mode === 'gift' || mode === 'executive') {
      if (canInstantPaint) {
        paintDrawerFromLocalState(cached, { rebuildCards: true, resetScroll: true });
      }
      /* Cart bootstrap / persistence / authoritative totals — background only */
      reconcileDrawerCartInBackground(options);
      return;
    }

    hideConfirmPanel();
    setDrawerHydrating(false);
    fetchCart()
      .then(function (cart) {
        syncIndividualUpsellFromCart(cart);
        if (options.productName) {
          var titleNode = drawer.querySelector('[data-ev-cart-drawer-product-name]');
          if (titleNode) titleNode.textContent = shortProductTitle(options.productName);
        }
        refreshUI(cart, { rebuildCards: true, resetScroll: true });
        resetDrawerScroll();
        if (window.EV && typeof window.EV.syncCartState === 'function' && cart.item_count) {
          window.EV.syncCartState(cart);
        }
      })
      .catch(function () {
        refreshUI({ items: [], item_count: 0, total_price: 0 }, { rebuildCards: true, resetScroll: true });
        resetDrawerScroll();
      });
  }

  function closeDrawer() {
    if (!drawer || !isOpen) return;
    if (isIndividualSubmitting()) return;
    if (galleryOpen) closeWalletGallery();

    var shouldResetUncommitted = isIndividualMode() &&
      !individualUpsell.cartMutationCommitted &&
      individualUpsell.status !== 'completed';

    if (openRaf !== null) {
      window.cancelAnimationFrame(openRaf);
      openRaf = null;
    }
    cancelScheduledCartDrawerUI();
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    setStickyCollapsed(false);
    setDrawerHydrating(false);
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockPageScroll();
    isOpen = false;
    resetDrawerScroll();
    drawerGeneration += 1;
    var closeGen = drawerGeneration;
    closeTimer = window.setTimeout(function () {
      closeTimer = null;
      if (closeGen !== drawerGeneration) return;
      drawer.hidden = true;
      if (shouldResetUncommitted) {
        resetIndividualUpsell();
        resetSelections(null);
        walletSelections = { 1: null, 2: null, 3: null };
      }
      focusEl(lastFocus);
    }, 320);
  }

  function trapFocus(event) {
    if (galleryOpen) {
      trapGalleryFocus(event);
      return;
    }
    if (!isOpen || !panel || event.key !== 'Tab') return;
    var focusable = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      focusEl(last);
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      focusEl(first);
    }
  }

  /** Page Up/Down / arrows scroll the drawer body when focus is inside the drawer. */
  function onDrawerKeyScroll(event) {
    if (!isOpen) return;
    var body = getPrimaryScrollEl();
    if (!body || !panel || !panel.contains(event.target)) return;
    if (document.activeElement === body) return;

    var key = event.key;
    if (key !== 'PageDown' && key !== 'PageUp' && key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') {
      return;
    }

    var tag = (event.target && event.target.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (event.target && event.target.isContentEditable) return;

    if ((key === 'ArrowDown' || key === 'ArrowUp') &&
        event.target.closest('button, a, [role="button"], [data-ev-drawer-pick]')) {
      return;
    }

    var max = body.scrollHeight - body.clientHeight;
    if (max <= 0) return;

    var amount = 0;
    if (key === 'PageDown') amount = Math.max(120, body.clientHeight * 0.85);
    else if (key === 'PageUp') amount = -Math.max(120, body.clientHeight * 0.85);
    else if (key === 'ArrowDown') amount = 48;
    else if (key === 'ArrowUp') amount = -48;
    else if (key === 'Home') {
      body.scrollTop = 0;
      event.preventDefault();
      return;
    } else if (key === 'End') {
      body.scrollTop = max;
      event.preventDefault();
      return;
    }

    var next = Math.max(0, Math.min(max, (body.scrollTop || 0) + amount));
    if (next === body.scrollTop) return;
    body.scrollTop = next;
    event.preventDefault();
  }

  function getGalleryModal() {
    return drawer && drawer.querySelector('[data-ev-wallet-gallery-modal]');
  }

  function findWalletProductById(productId) {
    var id = parseInt(productId, 10);
    if (!id) return null;
    walletProducts = ensureWalletProducts();
    for (var i = 0; i < walletProducts.length; i++) {
      if (parseInt(walletProducts[i].id, 10) === id) return walletProducts[i];
    }
    return null;
  }

  function galleryImagesForProduct(product) {
    if (!product) return [];
    if (Array.isArray(product.images) && product.images.length) return product.images.slice();
    if (product.image) {
      return [{
        src: product.image,
        src_sm: product.image,
        width: null,
        height: null,
        alt: product.title || ''
      }];
    }
    return [];
  }

  function syncGalleryChrome() {
    var modal = getGalleryModal();
    if (!modal) return;
    var counter = modal.querySelector('[data-ev-wallet-gallery-counter]');
    var prev = modal.querySelector('[data-ev-wallet-gallery-prev]');
    var next = modal.querySelector('[data-ev-wallet-gallery-next]');
    var dots = modal.querySelector('[data-ev-wallet-gallery-dots]');
    var count = galleryImages.length;

    if (counter) {
      if (count > 1) {
        counter.hidden = false;
        counter.removeAttribute('hidden');
        counter.textContent = (galleryIndex + 1) + ' / ' + count;
      } else {
        counter.hidden = true;
        counter.setAttribute('hidden', '');
      }
    }

    if (prev && next) {
      var showNav = count > 1;
      prev.hidden = !showNav;
      next.hidden = !showNav;
      if (showNav) {
        prev.removeAttribute('hidden');
        next.removeAttribute('hidden');
      } else {
        prev.setAttribute('hidden', '');
        next.setAttribute('hidden', '');
      }
    }

    if (dots) {
      if (count <= 1) {
        dots.hidden = true;
        dots.setAttribute('hidden', '');
        dots.innerHTML = '';
      } else {
        var existing = dots.querySelectorAll('[data-ev-gallery-dot]');
        if (existing.length !== count) {
          var html = '';
          for (var i = 0; i < count; i++) {
            html +=
              '<button type="button" class="ev-wallet-gallery__dot" data-ev-gallery-dot="' +
              i +
              '" aria-label="Image ' +
              (i + 1) +
              '"></button>';
          }
          dots.innerHTML = html;
        }
        dots.hidden = false;
        dots.removeAttribute('hidden');
        var buttons = dots.querySelectorAll('[data-ev-gallery-dot]');
        for (var d = 0; d < buttons.length; d++) {
          var active = d === galleryIndex;
          buttons[d].classList.toggle('is-active', active);
          if (active) buttons[d].setAttribute('aria-current', 'true');
          else buttons[d].removeAttribute('aria-current');
        }
      }
    }
  }

  function renderGalleryTrack(eagerIndex) {
    var modal = getGalleryModal();
    if (!modal) return;
    var track = modal.querySelector('[data-ev-wallet-gallery-track]');
    var viewport = modal.querySelector('[data-ev-wallet-gallery-viewport]');
    if (!track) return;
    var slideW = viewport ? Math.max(240, viewport.clientWidth || 0) : 0;
    var html = '';
    galleryImages.forEach(function (img, i) {
      var eager = i === eagerIndex;
      var w = img.width || 1200;
      var h = img.height || 1200;
      var src = eager ? (img.src || img.src_sm) : (img.src_sm || img.src);
      var srcset = img.src_sm && img.src
        ? escapeHtml(img.src_sm) + ' 600w, ' + escapeHtml(img.src) + ' 1200w'
        : '';
      html +=
        '<li class="ev-wallet-gallery__slide' + (i === galleryIndex ? ' is-active' : '') + '" data-ev-gallery-slide="' + i + '"' +
          (slideW ? ' style="flex:0 0 ' + slideW + 'px;width:' + slideW + 'px;min-width:' + slideW + 'px"' : '') +
        '>' +
          '<img class="ev-wallet-gallery__image"' +
            ' src="' + escapeHtml(src) + '"' +
            (srcset ? ' srcset="' + srcset + '" sizes="(max-width: 767px) 92vw, 560px"' : '') +
            ' alt="' + escapeHtml(img.alt || (galleryProduct && galleryProduct.title) || '') + '"' +
            ' width="' + w + '" height="' + h + '"' +
            (eager ? ' loading="eager" fetchpriority="high"' : ' loading="lazy"') +
          '>' +
        '</li>';
    });
    track.innerHTML = html || '<li class="ev-wallet-gallery__slide is-active"><div class="ev-wallet-gallery__empty">No images available</div></li>';
  }

  function scrollGalleryToIndex(index, behavior) {
    var modal = getGalleryModal();
    if (!modal) return;
    var viewport = modal.querySelector('[data-ev-wallet-gallery-viewport]');
    var slides = modal.querySelectorAll('[data-ev-gallery-slide]');
    if (!viewport || !slides.length) return;
    galleryIndex = Math.max(0, Math.min(index, slides.length - 1));
    var slide = slides[galleryIndex];
    if (!slide) return;
    viewport.scrollTo({
      left: slide.offsetLeft,
      behavior: behavior || 'auto'
    });
    for (var i = 0; i < slides.length; i++) {
      slides[i].classList.toggle('is-active', i === galleryIndex);
    }
    syncGalleryChrome();
  }

  function openWalletGallery(productId, triggerEl) {
    if (mode !== 'gift' && mode !== 'executive') return;
    var product = findWalletProductById(productId);
    if (!product) return;
    var images = galleryImagesForProduct(product);
    if (!images.length) return;

    var modal = getGalleryModal();
    if (!modal) return;

    var viewport = drawer.querySelector('[data-ev-cart-drawer-viewport]');
    galleryScrollLeft = viewport ? (viewport.scrollLeft || 0) : 0;
    galleryTrigger = triggerEl || null;
    galleryProduct = product;
    galleryImages = images;
    galleryIndex = 0;
    galleryOpen = true;

    var dialog = modal.querySelector('[data-ev-wallet-gallery-dialog]');
    var title = shortProductTitle(product.title) || 'Product';
    if (dialog) dialog.setAttribute('aria-label', title + ' images');

    modal.hidden = false;
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    if (drawer) drawer.classList.add('ev-cart-drawer--gallery-open');

    window.requestAnimationFrame(function () {
      renderGalleryTrack(0);
      syncGalleryChrome();
      scrollGalleryToIndex(0, 'auto');
      focusEl(modal.querySelector('[data-ev-wallet-gallery-close]') || dialog);
    });

    try {
      document.dispatchEvent(new CustomEvent('ev:wallet-gallery-open', {
        detail: {
          productId: product.id,
          handle: product.handle || null,
          slot: activeSlot,
          mode: mode
        }
      }));
    } catch (e) { /* ignore */ }
  }

  function closeWalletGallery() {
    if (!galleryOpen) return;
    var modal = getGalleryModal();
    galleryOpen = false;

    if (modal) {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      modal.hidden = true;
      modal.setAttribute('hidden', '');
      var track = modal.querySelector('[data-ev-wallet-gallery-track]');
      if (track) track.innerHTML = '';
    }
    if (drawer) drawer.classList.remove('ev-cart-drawer--gallery-open');

    /* Restore carousel scroll without rebuilding drawer state */
    var viewport = drawer && drawer.querySelector('[data-ev-cart-drawer-viewport]');
    if (viewport) {
      viewport.scrollLeft = galleryScrollLeft;
      window.requestAnimationFrame(function () {
        if (viewport) viewport.scrollLeft = galleryScrollLeft;
        syncCarouselDots();
      });
    }

    var restoreTarget = galleryTrigger;
    galleryTrigger = null;
    galleryProduct = null;
    galleryImages = [];
    galleryIndex = 0;
    focusEl(restoreTarget || getPrimaryScrollEl() || panel);
  }

  function stepGallery(delta) {
    if (!galleryOpen || galleryImages.length < 2) return;
    var next = galleryIndex + delta;
    if (next < 0) next = galleryImages.length - 1;
    if (next >= galleryImages.length) next = 0;
    scrollGalleryToIndex(next, 'smooth');
  }

  function trapGalleryFocus(event) {
    if (!galleryOpen || event.key !== 'Tab') return;
    var modal = getGalleryModal();
    if (!modal) return;
    var dialog = modal.querySelector('[data-ev-wallet-gallery-dialog]') || modal;
    var focusable = dialog.querySelectorAll(
      'button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])'
    );
    var list = [];
    for (var i = 0; i < focusable.length; i++) {
      if (focusable[i].offsetParent !== null || focusable[i] === document.activeElement) {
        list.push(focusable[i]);
      }
    }
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      focusEl(last);
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      focusEl(first);
    }
  }

  function bindGalleryControls() {
    var modal = getGalleryModal();
    if (!modal || modal.dataset.evGalleryBound === '1') return;
    modal.dataset.evGalleryBound = '1';

    modal.addEventListener('click', function (event) {
      if (event.target.closest('[data-ev-wallet-gallery-close]')) {
        event.preventDefault();
        closeWalletGallery();
        return;
      }
      if (event.target.closest('[data-ev-wallet-gallery-prev]')) {
        event.preventDefault();
        stepGallery(-1);
        return;
      }
      if (event.target.closest('[data-ev-wallet-gallery-next]')) {
        event.preventDefault();
        stepGallery(1);
        return;
      }
      var dot = event.target.closest('[data-ev-gallery-dot]');
      if (dot) {
        event.preventDefault();
        var idx = parseInt(dot.getAttribute('data-ev-gallery-dot'), 10);
        if (!isNaN(idx)) scrollGalleryToIndex(idx, 'smooth');
      }
    });

    var viewport = modal.querySelector('[data-ev-wallet-gallery-viewport]');
    if (viewport) {
      var galleryScrollRaf = null;
      viewport.addEventListener('scroll', function () {
        if (galleryScrollRaf !== null) return;
        galleryScrollRaf = window.requestAnimationFrame(function () {
          galleryScrollRaf = null;
          if (!galleryOpen) return;
          var slides = modal.querySelectorAll('[data-ev-gallery-slide]');
          if (!slides.length) return;
          var center = viewport.scrollLeft + viewport.clientWidth / 2;
          var nearest = 0;
          var best = Infinity;
          for (var i = 0; i < slides.length; i++) {
            var c = slides[i].offsetLeft + slides[i].offsetWidth / 2;
            var dist = Math.abs(c - center);
            if (dist < best) {
              best = dist;
              nearest = i;
            }
          }
          if (nearest !== galleryIndex) {
            galleryIndex = nearest;
            for (var s = 0; s < slides.length; s++) {
              slides[s].classList.toggle('is-active', s === galleryIndex);
            }
            syncGalleryChrome();
          }
        });
      }, { passive: true });
    }
  }

  function bindEvents() {
    if (eventsBound || !drawer) return;
    eventsBound = true;

    drawer.querySelectorAll('[data-ev-cart-drawer-close]').forEach(function (el) {
      if (el.hasAttribute('data-ev-cart-drawer-secondary')) return;
      el.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && galleryOpen) {
        event.preventDefault();
        closeWalletGallery();
        return;
      }
      if (event.key === 'Escape' && isOpen) {
        if (isIndividualSubmitting() || submitting) return;
        closeDrawer();
      }
      if (galleryOpen && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        stepGallery(event.key === 'ArrowLeft' ? -1 : 1);
        return;
      }
      trapFocus(event);
      onDrawerKeyScroll(event);
    });

    drawer.addEventListener('click', function (event) {
      var galleryBtn = event.target.closest('[data-ev-wallet-gallery]');
      if (galleryBtn && drawer.contains(galleryBtn)) {
        event.preventDefault();
        event.stopPropagation();
        var gid = galleryBtn.getAttribute('data-product-id');
        openWalletGallery(gid, galleryBtn);
        return;
      }

      var removeW2 = event.target.closest('[data-ev-remove-wallet2]');
      if (removeW2 && drawer.contains(removeW2)) {
        event.preventDefault();
        if (isIndividualSubmitting() || submitting) return;
        walletSelections[2] = null;
        individualUpsell.wallet2 = null;
        activeSlot = 2;
        showError('');
        refreshUI({ items: [] }, { rebuildCards: true, cards: 'states' });
        return;
      }

      var socialToggle = event.target.closest('[data-ev-social-reward-toggle]');
      if (socialToggle && drawer.contains(socialToggle)) {
        event.preventDefault();
        var rewardRoot = drawer.querySelector('[data-ev-social-reward]');
        if (rewardRoot) {
          rewardRoot.classList.add('is-expanded');
          socialToggle.setAttribute('aria-expanded', 'true');
          setSocialSectionHidden(socialToggle, true);
        }
        return;
      }

      var socialOpen = event.target.closest('[data-ev-social-open]');
      if (socialOpen && drawer.contains(socialOpen)) {
        event.preventDefault();
        openSocialProfile(
          socialOpen.getAttribute('data-ev-social-open') || '',
          socialOpen.getAttribute('data-ev-social-url') || ''
        );
        return;
      }

      var socialApply = event.target.closest('[data-ev-social-apply]');
      if (socialApply && drawer.contains(socialApply)) {
        event.preventDefault();
        if (socialApply.disabled) return;
        applySocialDiscountCode();
        return;
      }

      var socialCopy = event.target.closest('[data-ev-social-copy]');
      if (socialCopy && drawer.contains(socialCopy)) {
        event.preventDefault();
        copySocialDiscountCode();
        return;
      }

      var replaceBtn = event.target.closest('[data-replace-slot]');
      if (replaceBtn && drawer.contains(replaceBtn)) {
        if (isIndividualCompleted() || isIndividualSubmitting() || selectPending) return;
        var slot = parseInt(replaceBtn.getAttribute('data-replace-slot'), 10);
        if (slot === 2 || slot === 3) {
          var prevReplace = walletSelections[slot] || null;
          showError('');
          if (mode === 'gift' || mode === 'executive') {
            /*
             * Keep completed chrome until Shopify confirms removal, then
             * syncDrawerModeAfterCartMutation downgrades (Gift→Individual / Exec→Gift).
             */
            setBusy(true);
            clearBuilderSlotFromCart(slot)
              .then(function (cart) {
                setBusy(false);
                refreshUI(cart, { cards: 'states', quiet: true, preserveScroll: true });
              })
              .catch(function (error) {
                console.error('[EV Cart Drawer]', error);
                setBusy(false);
                walletSelections[slot] = prevReplace;
                if (slot === 2) individualUpsell.wallet2 = prevReplace;
                showError(error.message || 'Could not update cart.');
                refreshUI(lastCart, { cards: 'states', quiet: true, preserveScroll: true });
              });
            return;
          }
          walletSelections[slot] = null;
          if (slot === 2) individualUpsell.wallet2 = null;
          activeSlot = slot;
          refreshUI(isIndividualMode() ? { items: [] } : (mode === 'complete' ? lastCart : { items: [] }), {
            cards: 'states',
            quiet: true,
            preserveScroll: true
          });
          return;
        }
      }

      var pickBtn = event.target.closest('[data-ev-drawer-pick]');
      if (!pickBtn || !drawer.contains(pickBtn)) return;
      if (submitting || isIndividualSubmitting() || isIndividualCompleted() || selectPending) return;
      var variantId = parseInt(pickBtn.getAttribute('data-variant-id'), 10);
      if (!variantId) return;
      assignToActiveSlot(variantId);
    });

    bindGalleryControls();
    bindSocialRewardFocusUnlock();

    document.addEventListener('ev:cart:added', function (event) {
      var detail = (event && event.detail) || {};
      if (detail.skipDrawer) return;
      if (detail.drawerMode === 'gift' || detail.drawerMode === 'executive') {
        openDrawer({
          mode: detail.drawerMode,
          buildContext: detail.buildContext
        });
        return;
      }
      if (detail.mode === 'confirm' || detail.offer === 'individual' || detail.offer === 'gift' || detail.offer === 'executive') {
        openDrawer({
          mode: 'confirm',
          confirm: detail,
          productName: detail.productName
        });
        return;
      }
      openDrawer({
        mode: 'complete',
        productName: detail.productName
      });
    });

    document.addEventListener('ev:set:build', function (event) {
      var detail = (event && event.detail) || {};
      openDrawer({
        mode: detail.mode || 'gift',
        buildContext: detail.buildContext
      });
    });

    /* Keep drawer lastCart aligned with theme cart sync (e.g. external clear). */
    document.addEventListener('ev:cart:updated', function (event) {
      var cart = event && event.detail && event.detail.cart;
      if (cart && typeof cart === 'object' && Array.isArray(cart.items)) {
        lastCart = cart;
      }
    });
  }

  function init() {
    drawer = document.querySelector('[data-ev-cart-drawer]');
    if (!drawer) return;
    if (drawer.dataset.evDrawerInit === '1') return;
    drawer.dataset.evDrawerInit = '1';
    panel = drawer.querySelector('.ev-cart-drawer__panel');
    ensureWalletProducts();
    hideLegacyCheckoutLink();
    bindEvents();
    bindStickyHeader();
    bindCarouselControls();
    bindSlotsCarousel();
    bindDrawerLayoutObservers();

    window.EV = window.EV || {};
    window.EV.openCartDrawer = openDrawer;
    window.EV.closeCartDrawer = closeDrawer;
    window.EV.initializeCartDrawerUI = function () {
      scheduleInitializeCartDrawerUI({});
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
